import { EventEmitter } from "node:events";
import { createServer, type Server, type IncomingMessage, type ServerResponse } from "node:http";
import { randomUUID, createHash } from "node:crypto";
import { readFileSync } from "node:fs";
import { homedir as osHomedir } from "node:os";
import { writeFile, mkdir } from "node:fs/promises";
import { join } from "node:path";
import { readFile } from "node:fs/promises";
import sodium from "libsodium-wrappers-sumo";
import WebSocket from "ws";
import {
  generateIdentityKeypair,
  generateEphemeralKeypair,
  computeFingerprint,
  createProofOfPossession,
  performX3DH,
  DoubleRatchet,
  decryptFile,
  encryptFile,
  computeFileDigest,
  ScanEngine,
  TelemetryReporter,
  MLSGroupManager,
} from "@agentvault/crypto";
import type { SerializedMLSGroupState } from "@agentvault/crypto";
import { saveMlsState, loadMlsState, deleteMlsState, hasPendingWelcome, savePendingKpBundle, loadPendingKpBundle, clearPendingWelcome, acquireA2ASyncLock, releaseA2ASyncLock } from "./mls-state.js";
import { joinWithFirstMatchingKp, type KpBundle } from "./mls-kp-pool.js";

import type {
  SecureChannelConfig,
  ChannelState,
  MessageMetadata,
  AttachmentData,
  PersistedState,
  LegacyPersistedState,
  HistoryEntry,
  SendOptions,
  DecisionRequest,
  DecisionResponse,
  HeartbeatStatus,
  StatusAlert,
  RoomMemberInfo,
  RoomConversationInfo,
  RoomInfo,
  RoomState,
  A2AChannel,
  A2AMessage,
  DeliveryTarget,
  DeliveryContent,
  DeliveryOptions,
  DeliveryReceipt,
  TargetInfo,
  CredentialGrantPayload,
  CredentialRevokePayload,
} from "./types.js";
import { CredentialStore } from "./credential-store.js";
import {
  hexToBytes,
  bytesToHex,
  base64ToBytes,
  bytesToBase64,
  encryptedMessageToTransport,
  transportToEncryptedMessage,
} from "./crypto-helpers.js";
import { saveState, loadState, clearState, backupState, restoreState, uploadBackupToServer } from "./state.js";
import {
  enrollDevice,
  pollDeviceStatus,
  activateDevice,
} from "./transport.js";

/** Message types that should be forwarded to the agent in room contexts.
 * Internal protocol messages (thinking, status, heartbeat, typing, ack, etc.) are dropped. */
const ROOM_AGENT_TYPES = new Set([
  "message", "text", "decision_request", "decision_response", "artifact_share",
]);

/** Credential protocol message types — intercepted by plugin, never forwarded to agent. */
const CREDENTIAL_MESSAGE_TYPES = new Set([
  "credential_grant", "credential_revoke", "credential_request",
]);

const CATCHUP_MESSAGE_TYPES = new Set([
  "history_catchup_request", "history_catchup_response",
]);

const POLL_INTERVAL_MS = 6_000; // 6s — safely below the 1/5sec server-side rate limit
const RECONNECT_BASE_MS = 1_000;
const RECONNECT_MAX_MS = 30_000;
const PENDING_POLL_INTERVAL_MS = 15_000;

// Outbound idempotency guard (#475): collapse exact-duplicate content to the
// same destination+thread within a SLIDING window to a single send. A buggy
// agent runtime that emits the same reply many times in a burst (observed on
// prod: 23× in ~5s at ~4/sec) is collapsed to one delivery; distinct content,
// distinct topic/conversation, distinct destination, or a repeat after the
// window all pass untouched.
//
// Safety invariants (post-review #476):
//  - A key is recorded ONLY after a send SUCCEEDS, so a failed first send can
//    never suppress the retries that follow it (no silent message loss).
//  - Suppression of any one key is capped in absolute time, so a legitimately
//    periodic identical message can never be suppressed forever.
//  - Side-effecting content types (decision_request/action_confirmation) are
//    never content-deduped — their identity is not captured by a content hash.
const OUTBOUND_DEDUPE_WINDOW_MS = 3_000; // sliding: gap between dup emits must exceed this to pass
const OUTBOUND_DEDUPE_MAX_SUPPRESS_MS = 15_000; // absolute cap: never suppress one key longer than this
const OUTBOUND_DEDUPE_MAX = 256; // prune stale keys once the map grows past this
// Only these content types are eligible for content-hash dedupe. Side-effecting
// types carry an identity a content hash cannot stand in for.
const OUTBOUND_DEDUPE_TYPES = new Set(["text", "status_alert", "artifact", "attachment"]);

interface OutboundDedupeEntry {
  firstTs: number; // start of the current suppression run (for the absolute cap)
  lastTs: number; // most recent send/suppress of this key (for the sliding window)
  suppressed: number;
}

/**
 * Build the outbound-dedupe key for a delivery, or null to skip deduping.
 * Returns null for side-effecting content types. Prefers a runtime-supplied
 * idempotency id when present; otherwise keys on destination + conversation +
 * topic + priority + message_type + a hash of the exact content, so ONLY an
 * exact-duplicate to the same place/thread/priority collapses. Pure (no `this`).
 */
function outboundDedupeKey(
  resolved: { kind: string; id?: string },
  content: DeliveryContent,
  options?: DeliveryOptions,
): string | null {
  if (!OUTBOUND_DEDUPE_TYPES.has(content.type)) return null;
  const base = [resolved.kind, resolved.id ?? "owner", options?.conversationId ?? "", options?.topicId ?? ""];
  // If the runtime stamps a stable per-logical-message id (repeated on re-emit),
  // dedupe on THAT — it distinguishes a re-emit from a legit identical message.
  const explicitId = (options?.metadata?.idempotency_key ?? options?.metadata?.message_id) as string | undefined;
  if (explicitId) return [...base, `id:${explicitId}`].join("|");
  const contentHash = createHash("sha256").update(JSON.stringify(content)).digest("hex").slice(0, 32);
  const priority = options?.priority ?? "";
  const messageType = (options?.metadata?.message_type as string | undefined) ?? "";
  return [...base, `p:${priority}`, `mt:${messageType}`, contentHash].join("|");
}

/** Drop dedupe entries older than the window once the map grows past the cap. */
function pruneRecentDeliveries(map: Map<string, OutboundDedupeEntry>, now: number): void {
  if (map.size <= OUTBOUND_DEDUPE_MAX) return;
  for (const [key, entry] of map) {
    if (now - entry.lastTs >= OUTBOUND_DEDUPE_WINDOW_MS) map.delete(key);
  }
}

/**
 * Migrates legacy single-session persisted state to the new
 * multi-session format. If the state is already in the new format,
 * returns it as-is.
 */
function migratePersistedState(raw: any): PersistedState {
  // New format: has `sessions` and `primaryConversationId`
  if (raw.sessions && raw.primaryConversationId) {
    return raw as PersistedState;
  }

  // Legacy format: single conversationId + ratchetState
  const legacy = raw as LegacyPersistedState;
  return {
    deviceId: legacy.deviceId,
    deviceJwt: legacy.deviceJwt,
    primaryConversationId: legacy.conversationId,
    sessions: {
      [legacy.conversationId]: {
        ownerDeviceId: "",
        ratchetState: legacy.ratchetState,
      },
    },
    identityKeypair: legacy.identityKeypair,
    ephemeralKeypair: legacy.ephemeralKeypair,
    fingerprint: legacy.fingerprint,
    lastMessageTimestamp: legacy.lastMessageTimestamp,
    messageHistory: [],
  };
}

/** True when `target` addresses this agent's OWN hub address (#499, A2 parity
 * for OpenClaw). Deterministic self-reference guard for A2A: an agent must
 * never open (or send on) an A2A channel to itself — that is the self-name
 * bug's dead-room outcome. Normalized (trim + lowercase) EXACT match on the
 * full hub address, so a genuinely different peer with a similar name is
 * unaffected. Returns false when own address is unknown (never guard on an
 * empty self). Mirrors Hermes' find_agent self-sentinel. */
export function _isSelfHubAddress(ownHubAddress: string, target: string): boolean {
  const own = (ownHubAddress ?? "").trim().toLowerCase();
  const tgt = (target ?? "").trim().toLowerCase();
  return own.length > 0 && own === tgt;
}

export class SecureChannel extends EventEmitter {
  private _state: ChannelState = "idle";
  private _deviceId: string | null = null;
  private _fingerprint: string | null = null;
  private _primaryConversationId: string = "";
  private _deviceJwt: string | null = null;
  private _sessions: Map<
    string,
    { ownerDeviceId: string; ratchet: DoubleRatchet; activated: boolean; epoch?: number }
  > = new Map();
  private _ws: WebSocket | null = null;
  private _pollTimer: ReturnType<typeof setTimeout> | null = null;
  private _reconnectAttempt = 0;
  private _reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private _rapidDisconnects = 0;
  private _lastWsOpenTime = 0;
  private _pingTimer: ReturnType<typeof setInterval> | null = null;
  private _lastServerMessage = 0;
  private _pendingAcks: string[] = [];
  private _ackTimer: ReturnType<typeof setTimeout> | null = null;
  private _stopped = false;
  private _persisted: PersistedState | null = null;
  // AgentVault display_name, resolved from GET /hub/identities (#499, A1).
  // hub_address alone isn't enough — its random suffix (wren-8789ee6d…) differs
  // from what the owner types (Wren). Empty until resolved; the DM preamble is
  // then omitted rather than asserting a wrong name. Re-resolved on every
  // connect (picks up renames; retries after a failed fetch).
  private _ownDisplayName = "";
  // Dedupes concurrent resolves (connect-trigger + lazy DM-trigger) so a burst
  // of DMs can't stampede /hub/identities.
  private _resolvingOwnDisplayName: Promise<void> | null = null;
  private _httpServer: Server | null = null;
  private _mcpServer: import("./mcp-server.js").AgentVaultMcpServer | null = null;
  private _pollFallbackTimer: ReturnType<typeof setInterval> | null = null;
  private _heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private _heartbeatCallback: (() => HeartbeatStatus) | null = null;
  private _heartbeatIntervalSeconds: number = 0;
  private _wakeDetectorTimer: ReturnType<typeof setInterval> | null = null;
  private _lastWakeTick: number = Date.now();
  private _trustToken: string | null = null;
  private _trustTier: string | null = null;
  private _trustTokenExpiresAt: string | null = null;
  private _trustTokenInterval: ReturnType<typeof setInterval> | null = null;
  private _pendingPollTimer: ReturnType<typeof setInterval> | null = null;
  private _syncMessageIds: Set<string> | null = null;
  private _deliveryHeartbeat: ReturnType<typeof setInterval> | null = null;
  private _a2aReconcileTimer: ReturnType<typeof setInterval> | null = null;
  private _deliveryPulling = false;
  private _drDeliveryPulling = false;

  /** MLS group managers per room/conversation (roomId or conv:conversationId or 1to1-group:groupId -> MLSGroupManager) */
  private _mlsGroups: Map<string, MLSGroupManager> = new Map();

  /** Maps conversationId -> conversation_group_id for shared 1:1 MLS groups */
  private _sessionGroupIds: Map<string, string> = new Map();
  /** Consecutive MLS decrypt failure count per group key for epoch recovery. */
  private _mlsDecryptFailCounts: Map<string, number> = new Map();
  private static readonly MAX_MLS_DECRYPT_FAILS = 3;

  /** Cached MLS KeyPackage bundle for this device (regenerated on each connect). */
  private _mlsKeyPackage: Awaited<ReturnType<MLSGroupManager["generateKeyPackage"]>> | null = null;
  /** Pending KeyPackage bundle from request-Welcome flow (used by _handleMlsWelcome). */
  private _pendingMlsKpBundle: Awaited<ReturnType<MLSGroupManager["generateKeyPackage"]>> | undefined;
  /**
   * Pool of pending KeyPackage bundles this device has published but not yet
   * consumed by a Welcome. The backend consumes published KeyPackages newest-first (#508),
   * so a given room's creator may have been served ANY of these — `_handleMlsWelcome`
   * trial-decrypts the incoming Welcome against every bundle in the pool (plus the
   * connect KP and the persisted per-group bundle). Capped at `_KP_POOL_TARGET`;
   * a consumed bundle is removed on a successful join. (KP-pool design 2026-06-18.)
   */
  private _pendingKpBundles: KpBundle[] = [];
  /** Pool target: keep this many unconsumed KeyPackages published; backend caps to match. */
  private static readonly _KP_POOL_TARGET = 10;
  /** Reentrancy guard so concurrent pool top-ups don't double-publish past target. */
  private _kpPoolFilling = false;

  /** Buffer for MLS commits received before Welcome (keyed by groupId, sorted by epoch). */
  private _pendingMlsCommits: Map<string, Array<{ epoch: number; data: any }>> = new Map();

  /** In-memory credential store for renter-provided credentials (never persisted). */
  private _credentialStore = new CredentialStore();

  /** Rooms whose roster has been refreshed from the backend at least once in
   * this process (GH #366). Cleared on (re)join so the next first message
   * re-fetches the authoritative roster. Prevents per-message refetch storms. */
  private _roomRosterRefreshed = new Set<string>();
  /** In-flight roster-refresh promises (per room) — dedup concurrent refreshes. */
  private _roomRosterRefreshInFlight = new Map<string, Promise<void>>();

  /** Dedup buffer for A2A message IDs (prevents double-delivery via direct + Redis) */
  private _a2aSeenMessageIds = new Set<string>();
  private static readonly A2A_SEEN_MAX = 500;
  /** Dedup buffer for regular message IDs (prevents double-decrypt via direct WS + Redis pub/sub) */
  private _seenMessageIds = new Set<string>();
  private static readonly SEEN_MSG_MAX = 500;
  /**
   * Outbound idempotency guard (#475): last send time per (destination+topic+content)
   * key. Collapses an exact-duplicate burst from a misbehaving agent runtime to a
   * single delivery. In-memory only (short window; no cross-restart persistence needed).
   */
  private _recentDeliveries = new Map<string, OutboundDedupeEntry>();
  private _scanEngine: ScanEngine | null = null;
  private _scanRuleSetVersion = 0;
  private _telemetryReporter: TelemetryReporter | null = null;

  /** Topic ID from the most recent inbound message — used as fallback for replies. */
  private _lastIncomingTopicId: string | undefined;

  /** Room ID from the most recent inbound room message — used as fallback for HTTP /send replies. */
  private _lastInboundRoomId: string | undefined;

  /** Rate-limit: last resync_request timestamp per conversation (5-min cooldown). */
  private _lastResyncRequest: Map<string, number> = new Map();

  /** Dedup: last room/group welcome self-heal request timestamp per group (30s window). */
  private _welcomeSelfHealInflight: Map<string, number> = new Map();

  /** Debounce timer for server backup uploads (60s). */
  private _serverBackupTimer: ReturnType<typeof setTimeout> | null = null;
  private _serverBackupRunning = false;

  // Reactive device-JWT reissue guard. Reset at the start of every _connect
  // so each WS session gets exactly one reissue attempt on a 401, preventing
  // an authedFetch loop when the reissued JWT is itself rejected.
  private _reissueAttempted: boolean = false;

  // Set to true when an `auth_failed` event has been emitted in the current
  // session (either via proactive reissue in `_maybeReissueDeviceJwt` or via
  // reactive reissue in `_authedFetch`). Used by the rapid-disconnect
  // classifier in `_scheduleReconnect` to distinguish auth failure from
  // session-collision so we emit the correct cause and stop reconnecting.
  // Reset at the start of every `_connect`.
  private _authFailedThisSession: boolean = false;

  // Last observed WebSocket close code, captured by the ws.on("close", ...)
  // handler. Used by the rapid-disconnect classifier to surface
  // server-driven "session_replaced" events (close codes 4000 / 4001).
  private _lastCloseCode: number | null = null;

  // Liveness detection: server sends app-level {"event":"ping"} every 30s.
  // We check every 30s; if no data received in 90s (3 missed pings), connection is dead.
  private static readonly PING_INTERVAL_MS = 30_000;
  private static readonly SILENCE_TIMEOUT_MS = 90_000;
  private static readonly POLL_FALLBACK_INTERVAL_MS = 30_000;  // 30s when messages found
  private static readonly POLL_FALLBACK_IDLE_MS = 60_000;      // 60s when idle

  /**
   * When true, start() ignores any persisted credentials/backup in dataDir
   * and goes straight to the fresh-enrollment path. Surface for setup --force
   * to recover from expired/corrupt device JWTs.
   *
   * Spec: docs/superpowers/specs/2026-05-25-device-credential-lifecycle-design.md §8.2
   */
  private _forceReEnroll: boolean;

  constructor(private config: SecureChannelConfig) {
    super();
    this._forceReEnroll = config.forceReEnroll ?? false;
  }

  /**
   * Decode the `exp` claim from a JWT without verifying the signature. The
   * plugin only uses this to decide whether to proactively reissue a device
   * credential; the backend always re-verifies signatures.
   */
  private _decodeJwtExp(jwt: string): number {
    try {
      const payload = jwt.split(".")[1];
      if (!payload) return 0;
      const body = JSON.parse(Buffer.from(payload, "base64url").toString("utf-8"));
      return typeof body.exp === "number" ? body.exp : 0;
    } catch {
      return 0;
    }
  }

  /**
   * Notify the AV backend that device credentials are catastrophically broken
   * (the plugin's own auth is what failed, so this endpoint is intentionally
   * unauthenticated). Writes a single row into ``devices.last_auth_failure``
   * which the AV web UI surfaces as a re-enroll banner (Task 25).
   *
   * Best-effort: a transport failure here MUST NOT propagate — we still want
   * the in-process ``auth_failed`` event to fire so the CLI/UI can prompt.
   *
   * Spec: docs/superpowers/specs/2026-05-25-device-credential-lifecycle-design.md §8.1
   */
  private async _postAuthFailedToBackend(
    reason: "device_jwt_expired" | "device_revoked",
  ): Promise<void> {
    const deviceId = this._persisted?.deviceId;
    const apiUrl = this.config?.apiUrl;
    if (!deviceId || !apiUrl) return;
    try {
      await fetch(`${apiUrl}/api/v1/devices/${deviceId}/health`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          kind: "auth_failed",
          reason,
          action_required: "re_enroll",
          ts: new Date().toISOString(),
        }),
      });
    } catch (err) {
      console.warn("[SecureChannel] failed to post auth_failed:", err);
    }
  }

  /**
   * Proactively reissue the persisted device JWT when its remaining lifetime
   * is under 30 days. Called before each WebSocket connect attempt so the
   * credential is refreshed during routine reconnect cycles rather than
   * forcing the customer to re-enroll once it expires.
   *
   * Non-fatal: a transient network error or 5xx keeps the current JWT and
   * relies on the next reconnect. Terminal failures (401/403) surface via
   * the `auth_failed` event so callers can prompt the user to recover.
   */
  private async _maybeReissueDeviceJwt(): Promise<void> {
    const jwt = this._persisted?.deviceJwt ?? this._deviceJwt;
    if (!jwt) return;

    const exp = this._decodeJwtExp(jwt);
    const nowSec = Math.floor(Date.now() / 1000);
    if (exp - nowSec >= 30 * 86400) return; // > 30 days left, skip

    try {
      const resp = await fetch(`${this.config.apiUrl}/api/v1/auth/reissue`, {
        method: "POST",
        headers: { Authorization: `Bearer ${jwt}` },
      });

      if (resp.status === 200) {
        const body = (await resp.json()) as { device_jwt?: string };
        if (body.device_jwt) {
          // Always update in-memory if we got a fresh token, so this connect
          // uses it even if persisted state is somehow missing.
          this._deviceJwt = body.device_jwt;
          if (this._persisted) {
            this._persisted.deviceJwt = body.device_jwt;
            await this._persistState();
            console.log("[SecureChannel] device_jwt reissued (5y)");
          } else {
            console.log("[SecureChannel] device_jwt reissued (5y) — in memory only (no persisted state)");
          }
        } else {
          console.warn("[SecureChannel] reissue returned 200 but no device_jwt in response");
        }
        return;
      }

      if (resp.status === 401 || resp.status === 403) {
        // Terminal — credentials need user intervention. Surface via the
        // event surface so callers (CLI, UI) can show a recovery prompt.
        console.warn(`[SecureChannel] reissue rejected (${resp.status}); credentials need attention`);
        this._authFailedThisSession = true;
        const reason: "device_jwt_expired" | "device_revoked" =
          resp.status === 401 ? "device_jwt_expired" : "device_revoked";
        // Task 24 — notify backend (best-effort) BEFORE emitting locally so
        // the AV web UI banner can render even if listeners are slow to react.
        await this._postAuthFailedToBackend(reason);
        this.emit("auth_failed", { reason });
        return;
      }

      // 5xx / other — non-fatal. Retry on next connect.
      console.warn(`[SecureChannel] reissue ${resp.status}; keeping current jwt`);
    } catch (err) {
      console.warn("[SecureChannel] reissue network error; keeping current jwt:", err);
    }
  }

  /**
   * Wraps `fetch` for authenticated AV REST endpoints with reactive device-JWT
   * reissue on 401. On the first 401 of a WS session, calls /auth/reissue
   * once; if it returns a fresh JWT, adopts it (in-memory + persisted) and
   * retries the original request with the new bearer. The `_reissueAttempted`
   * guard ensures we never loop — a second 401 (or a 401/403 from the reissue
   * itself) emits `auth_failed` and the caller sees the original 401.
   *
   * Existing fetch sites can opt in by replacing `fetch(url, init)` with
   * `this._authedFetch(url, init)`. The wrapper sets the `Authorization`
   * header itself, so callers can omit it.
   */
  private async _authedFetch(url: string, init?: RequestInit): Promise<Response> {
    const jwt = this._deviceJwt ?? this._persisted?.deviceJwt ?? "";
    const baseHeaders = (init?.headers as Record<string, string> | undefined) ?? {};
    const headers: Record<string, string> = {
      ...baseHeaders,
      Authorization: `Bearer ${jwt}`,
    };
    let resp = await fetch(url, { ...init, headers });

    if (resp.status === 401 && !this._reissueAttempted) {
      this._reissueAttempted = true;
      let reissueResp: Response;
      try {
        reissueResp = await fetch(`${this.config.apiUrl}/api/v1/auth/reissue`, {
          method: "POST",
          headers: { Authorization: `Bearer ${jwt}` },
        });
      } catch (err) {
        // Network error on reissue — leave the original 401 for the caller.
        console.warn("[SecureChannel] authedFetch reissue network error:", err);
        return resp;
      }

      if (reissueResp.status === 200) {
        const body = (await reissueResp.json()) as { device_jwt?: string };
        if (body.device_jwt) {
          this._deviceJwt = body.device_jwt;
          if (this._persisted) {
            this._persisted.deviceJwt = body.device_jwt;
            try {
              await this._persistState();
            } catch (err) {
              // Non-fatal — fresh JWT is in-memory regardless.
              console.warn("[SecureChannel] authedFetch persist after reissue failed:", err);
            }
          }
          const retryHeaders: Record<string, string> = {
            ...baseHeaders,
            Authorization: `Bearer ${body.device_jwt}`,
          };
          resp = await fetch(url, { ...init, headers: retryHeaders });
        }
      } else if (reissueResp.status === 401 || reissueResp.status === 403) {
        // Terminal — surface to callers (CLI / UI) so they can prompt the
        // user to re-enroll. Leave `resp` as the original 401 so the caller
        // sees an auth failure rather than misinterpreting a silent retry.
        this._authFailedThisSession = true;
        const reason: "device_jwt_expired" | "device_revoked" =
          reissueResp.status === 401 ? "device_jwt_expired" : "device_revoked";
        // Task 24 — notify backend (best-effort) BEFORE emitting locally so
        // the AV web UI banner can render even if listeners are slow to react.
        await this._postAuthFailedToBackend(reason);
        this.emit("auth_failed", { reason });
      }
      // Other reissue statuses (5xx etc.) fall through: caller gets the original 401.
    }
    return resp;
  }

  get state(): ChannelState {
    return this._state;
  }

  get deviceId(): string | null {
    return this._deviceId;
  }

  /** This agent's AgentVault display_name (#499, A1). "" until resolved. */
  get ownDisplayName(): string {
    return this._ownDisplayName;
  }

  /** This agent's AgentVault hub_address (#499, A1). "" until known. */
  get ownHubAddress(): string {
    return this._persisted?.hubAddress ?? "";
  }

  /** Public best-effort trigger to (re)resolve this agent's display_name.
   * Called lazily from the 1:1 DM path when the name is still unknown, so a
   * resolve that failed at connect (or a fresh agent that hadn't registered
   * yet) is retried without waiting for the next WS reconnect (#499, A1). */
  ensureOwnIdentityResolved(): Promise<void> {
    return this._resolveOwnDisplayName();
  }

  /** Resolve this agent's own display_name from GET /api/v1/hub/identities
   * (matches its own device_id). Deduped — concurrent callers share one
   * in-flight fetch. Refreshes the cached value on success (picks up a rename);
   * on failure leaves the prior value intact. Best-effort — never throws.
   * Mirrors Hermes RestClient.get_own_identity (#499, Component 0). */
  private _resolveOwnDisplayName(): Promise<void> {
    if (this._resolvingOwnDisplayName) return this._resolvingOwnDisplayName;
    const p = (async () => {
      const jwt = this._persisted?.deviceJwt ?? this._deviceJwt;
      const deviceId = this._persisted?.deviceId ?? this._deviceId;
      if (!jwt || !deviceId) return;
      try {
        const res = await fetch(`${this.config.apiUrl}/api/v1/hub/identities`, {
          headers: { Authorization: `Bearer ${jwt}` },
        });
        if (!res.ok) return;
        const list = await res.json();
        if (!Array.isArray(list)) return;
        const mine = list.find((x: any) => x?.device_id === deviceId);
        if (mine?.display_name) this._ownDisplayName = String(mine.display_name);
      } catch {
        /* best-effort — retried on next connect (hub_identity_sync) or next DM */
      }
    })();
    this._resolvingOwnDisplayName = p;
    void p.finally(() => {
      if (this._resolvingOwnDisplayName === p) this._resolvingOwnDisplayName = null;
    });
    return p;
  }

  get fingerprint(): string | null {
    return this._fingerprint;
  }

  /** Returns the primary conversation ID (backward-compatible). */
  get conversationId(): string | null {
    return this._primaryConversationId || null;
  }

  /** Returns all active conversation IDs. */
  get conversationIds(): string[] {
    return Array.from(this._sessions.keys());
  }

  /** Returns the number of active sessions. */
  get sessionCount(): number {
    return this._sessions.size;
  }

  /** Room ID from the most recent inbound room message (for HTTP /send fallback). */
  get lastInboundRoomId(): string | undefined {
    return this._lastInboundRoomId;
  }

  /** Returns all persisted room IDs and names (for outbound target registration). */
  get roomIds(): Array<{ roomId: string; name: string }> {
    if (!this._persisted?.rooms) return [];
    return Object.values(this._persisted.rooms).map((r) => ({ roomId: r.roomId, name: r.name }));
  }

  /** Returns hub addresses of all persisted A2A peer participants. */
  get a2aPeerAddresses(): string[] {
    if (!this._persisted?.a2aChannels) return [];
    const addrs: string[] = [];
    for (const ch of Object.values(this._persisted.a2aChannels)) {
      for (const p of ch.participants ?? []) {
        if (p.status === "active" || p.status === "invited") {
          addrs.push(p.hubAddress);
        }
      }
    }
    return [...new Set(addrs)];
  }

  /** Resolves an A2A channel ID to the first active peer's hub address, or null if not found. */
  resolveA2AChannelHub(channelId: string): string | null {
    const entry = this._persisted?.a2aChannels?.[channelId];
    if (!entry?.participants?.length) return null;
    const active = entry.participants.find(p => p.status === "active");
    return active?.hubAddress ?? entry.participants[0]?.hubAddress ?? null;
  }

  /** Returns the TelemetryReporter instance (available after WebSocket connect). */
  get telemetry(): TelemetryReporter | null {
    return this._telemetryReporter;
  }

  /**
   * Check if a skill is in shadow mode. Returns the shadow config if active, undefined otherwise.
   */
  getShadowConfig(skillName: string): { sessionId: string; autonomyLevel: string; decisionClass: string } | undefined {
    return this._persisted?.shadowSkills?.[skillName];
  }

  async start(): Promise<void> {
    this._stopped = false;
    await sodium.ready;

    // setup --force path: wipe any cached credentials so the enrollment
    // flow below creates fresh ones from the invite token. Without this
    // clear, a partially-written backup or legacy state file could
    // resurface on the next start() and we'd be back to the silent
    // "Already enrolled" failure mode this flag exists to break.
    //
    // Spec: docs/superpowers/specs/2026-05-25-device-credential-lifecycle-design.md §8.2
    if (this._forceReEnroll) {
      console.log("[SecureChannel] forceReEnroll set — clearing cached credentials, will enroll fresh");
      await clearState(this.config.dataDir);
      this._persisted = null;
      await this._enroll();
      return;
    }

    // Check for persisted state (may be legacy or new format)
    const raw = await loadState(this.config.dataDir);
    if (raw) {
      await this._hydrateFromPersisted(raw);
      this._connect();
      return;
    }

    // Try restoring from backup before enrolling
    const restored = await restoreState(this.config.dataDir);
    if (restored) {
      console.log("[SecureChannel] Restored state from backup");
      const restoredRaw = await loadState(this.config.dataDir);
      if (restoredRaw) {
        await this._hydrateFromPersisted(restoredRaw);
        this._connect();
        return;
      }
    }

    // Full lifecycle: enroll -> poll -> activate -> connect
    await this._enroll();
  }

  /**
   * Hydrate all in-memory state from persisted creds. The single source of
   * truth for both the primary boot and the .bak-restore boot, so the two can
   * never drift (the drift is what caused #416: the backup branch used to skip
   * MLS group loading and dropped owner MLS DMs). Callers invoke _connect()
   * after this resolves.
   */
  private async _hydrateFromPersisted(raw: unknown): Promise<void> {
    await backupState(this.config.dataDir);
    this._persisted = migratePersistedState(raw);
    if (!this._persisted.messageHistory) {
      this._persisted.messageHistory = [];
    }
    this._deviceId = this._persisted.deviceId;
    this._deviceJwt = this._persisted.deviceJwt;
    this._primaryConversationId = this._persisted.primaryConversationId;
    this._fingerprint = this._persisted.fingerprint;

    // Restore sticky room context for proactive send routing
    this._lastInboundRoomId = this._persisted.lastInboundRoomId;

    // Restore dedup sets from persisted state
    if (this._persisted.seenMessageIds) {
      this._seenMessageIds = new Set(this._persisted.seenMessageIds.slice(-SecureChannel.SEEN_MSG_MAX));
    }
    if (this._persisted.seenA2AMessageIds) {
      this._a2aSeenMessageIds = new Set(this._persisted.seenA2AMessageIds.slice(-SecureChannel.A2A_SEEN_MAX));
    }

    // Clean up stale DR room sessions (rooms are MLS-only now)
    if (this._persisted?.rooms) {
      const roomConvIds = new Set<string>();
      for (const room of Object.values(this._persisted.rooms)) {
        for (const cid of room.conversationIds || []) {
          roomConvIds.add(cid);
        }
      }
      let cleanedCount = 0;
      for (const cid of roomConvIds) {
        if (this._persisted.sessions[cid]) {
          delete this._persisted.sessions[cid];
          cleanedCount++;
        }
      }
      if (cleanedCount > 0) {
        console.log(`[SecureChannel] Cleaned ${cleanedCount} stale DR room sessions`);
      }
    }

    // Restore all ratchet sessions
    for (const [convId, sessionData] of Object.entries(
      this._persisted.sessions,
    )) {
      if (sessionData.ratchetState) {
        const ratchet = DoubleRatchet.deserialize(sessionData.ratchetState);
        this._sessions.set(convId, {
          ownerDeviceId: sessionData.ownerDeviceId,
          ratchet,
          activated: sessionData.activated ?? false,
          epoch: sessionData.epoch,
        });
      }
    }

    // Rebuild _sessionGroupIds from persisted conversationGroupIds
    if (this._persisted.conversationGroupIds) {
      this._sessionGroupIds = new Map(Object.entries(this._persisted.conversationGroupIds));
      console.log(`[SecureChannel] Restored ${this._sessionGroupIds.size} conversation→group mappings`);
    }

    // Load MLS group states for rooms
    if (this._persisted.rooms) {
      for (const [roomId, room] of Object.entries(this._persisted.rooms)) {
        const mlsGroupId = room.mlsGroupId;
        if (mlsGroupId) {
          try {
            const mlsState = await loadMlsState(this.config.dataDir, mlsGroupId);
            if (mlsState) {
              const mgr = new MLSGroupManager();
              mgr.importState(JSON.parse(mlsState) as SerializedMLSGroupState);
              this._mlsGroups.set(roomId, mgr);
            }
          } catch (mlsErr) {
            console.warn(`[SecureChannel] Failed to restore MLS state for room ${roomId.slice(0, 8)}:`, mlsErr);
          }
        }
      }
    }

    // Load MLS group states for 1:1 conversations
    for (const [convId, entry] of Object.entries(this._persisted.mlsConversations ?? {})) {
      if (entry.mlsGroupId) {
        try {
          const mlsState = await loadMlsState(this.config.dataDir, entry.mlsGroupId);
          if (mlsState) {
            const mgr = new MLSGroupManager();
            mgr.importState(JSON.parse(mlsState) as SerializedMLSGroupState);
            this._mlsGroups.set(`conv:${convId}`, mgr);
          }
        } catch (mlsErr) {
          console.warn(`[SecureChannel] Failed to restore MLS state for conv ${convId.slice(0, 8)}:`, mlsErr);
        }
      }
    }

    // Load shared 1:1 MLS groups
    for (const [gid, entry] of Object.entries(this._persisted.mlsGroups ?? {})) {
      if (!entry.mlsGroupId) continue;
      try {
        const stateJson = await loadMlsState(this.config.dataDir, entry.mlsGroupId);
        if (stateJson) {
          const mgr = new MLSGroupManager();
          mgr.importState(JSON.parse(stateJson) as SerializedMLSGroupState);
          this._mlsGroups.set(`1to1-group:${gid}`, mgr);
          console.log(`[SecureChannel] Loaded shared MLS group for ${gid.slice(0, 8)} (epoch=${mgr.epoch})`);
        }
      } catch (loadErr) {
        console.warn(`[SecureChannel] Failed to load shared MLS group ${gid.slice(0, 8)}:`, loadErr);
      }
    }

    // Load MLS group states for A2A channels
    for (const [channelId, entry] of Object.entries(this._persisted.a2aChannels ?? {})) {
      if (entry.mlsGroupId) {
        try {
          const mlsState = await loadMlsState(this.config.dataDir, entry.mlsGroupId);
          if (mlsState) {
            const mgr = new MLSGroupManager();
            mgr.importState(JSON.parse(mlsState) as SerializedMLSGroupState);
            this._mlsGroups.set(`a2a:${channelId}`, mgr);
          }
        } catch (mlsErr) {
          console.warn(`[SecureChannel] Failed to restore MLS state for A2A ${channelId.slice(0, 8)}:`, mlsErr);
        }
      }
    }
  }

  /**
   * Fetch scan rules from the server and load them into the ScanEngine.
   */
  private async _fetchScanRules(): Promise<void> {
    if (!this._scanEngine) return;
    try {
      const resp = await fetch(`${this.config.apiUrl}/api/v1/scan-rules`, {
        headers: { Authorization: `Bearer ${this._persisted?.deviceJwt}` },
      });
      if (!resp.ok) return;
      const data = await resp.json();
      if (data.rule_set_version !== this._scanRuleSetVersion) {
        this._scanEngine.loadRules(data.rules);
        this._scanRuleSetVersion = data.rule_set_version;
      }
    } catch (err) {
      console.error("[SecureChannel] Failed to fetch scan rules:", err);
    }
  }

  /**
   * Activate all sibling 1:1 sessions when any session receives an owner message.
   * Ensures agent replies fan out to ALL owner devices, not just the active one.
   */
  private _activateSiblings(triggerConvId: string): void {
    const roomConvIds = new Set<string>();
    if (this._persisted?.rooms) {
      for (const room of Object.values(this._persisted.rooms)) {
        for (const cid of room.conversationIds) {
          roomConvIds.add(cid);
        }
      }
    }
    for (const [sibConvId, sibSession] of this._sessions) {
      if (sibConvId === triggerConvId) continue;
      if (roomConvIds.has(sibConvId)) continue;
      if (!sibSession.activated) {
        sibSession.activated = true;
        console.log(`[SecureChannel] Sibling session ${sibConvId.slice(0, 8)}... activated (multi-device fan-out)`);
        if (this._persisted?.sessions[sibConvId]) {
          this._persisted.sessions[sibConvId].activated = true;
        }
      }
    }
  }

  /**
   * Append a message to persistent history for cross-device replay.
   */
  private _appendHistory(sender: "owner" | "agent", text: string, topicId?: string): void {
    if (!this._persisted) return;
    if (!this._persisted.messageHistory) {
      this._persisted.messageHistory = [];
    }

    const maxSize = this.config.maxHistorySize ?? 500;
    const entry: HistoryEntry = {
      sender,
      text,
      ts: new Date().toISOString(),
    };
    if (topicId) {
      entry.topicId = topicId;
    }
    this._persisted.messageHistory.push(entry);

    // Evict oldest entries if over limit
    if (this._persisted.messageHistory.length > maxSize) {
      this._persisted.messageHistory = this._persisted.messageHistory.slice(-maxSize);
    }
  }

  /**
   * Resolve the room a conversation belongs to (if any) from persisted room
   * membership. A room-backed conversation must never surface to the native
   * worker tool-gate as a no-roomId 1:1 `message` — the gate treats that as an
   * authenticated owner DM (tool-enabled), bypassing arming. Shared by the
   * real-time DR path (which stamps roomId on the emitted message) and the DR
   * poll fallback (which SKIPS legacy room messages, matching the queue/sync
   * fallbacks). Resolution is from LOCAL persisted room state; the sync path
   * additionally honors the server's `room_id`. Defense-in-depth: DR is
   * 1:1-only and rooms use MLS today, so this is inert now, but closes the
   * residual gap if a room-backed conversation ever rides a DR session.
   */
  private _roomIdForConversation(convId: string): string | undefined {
    if (!this._persisted?.rooms) return undefined;
    for (const [rid, room] of Object.entries(this._persisted.rooms)) {
      if (room.conversationIds?.includes(convId)) return rid;
    }
    return undefined;
  }

  /**
   * True when a conversation is NOT a genuine 1:1 owner DM — i.e. it belongs to a
   * room OR an A2A channel — and therefore must NEVER surface to the native worker
   * tool-gate as a no-roomId `message` (the gate treats that as an authenticated
   * owner DM, tool-enabled, bypassing arming). This is the predicate every fallback
   * emit guard uses (MLS 1:1, DR-delivery queue, HTTP poll).
   *
   * Resolves from LOCAL persisted state — BOTH `_persisted.rooms[*].conversationIds`
   * and `_persisted.a2aChannels[*].conversationId` — and accepts EITHER the
   * conversation id or the conversation-group id, so a convGroupId-only room/A2A
   * payload is still caught while a genuine convGroupId-only 1:1 shared-group DM
   * (present in neither map) still returns false and emits.
   *
   * Some fallback guards additionally honor an AUTHORITATIVE server routing field as
   * a backstop for the cold-local-state window — but WHICH field each carries differs
   * per endpoint, so do NOT assume a blanket "backend always populates room_id/
   * a2a_channel_id" at a new call site:
   *   - HTTP poll (`/devices/{id}/messages`): server `room_id` populated for room-backed
   *     convs (conv_room_map). No `a2a_channel_id` on this endpoint → A2A caught locally.
   *   - DR-delivery (`/dr/delivery` -> dr_delivery_service.pull_pending): server `room_id`
   *     populated (from the conversation); NO `a2a_channel_id` (DrDeliveryQueue is
   *     1:1-only, never A2A). room_id is null in practice today but real for any future
   *     room-over-DR row.
   *   - MLS 1:1 (`_handleMessageMLS`): room/A2A are routed away UPSTREAM at the
   *     MLS-delivery dispatcher (authoritative `room_id`/`a2a_channel_id`) before this
   *     handler; the fields are absent on `data` here, so this predicate is the belt.
   * The MLS-delivery *queue* endpoint (mls_delivery_service.pull_pending) is the one that
   * returns all of room_id/a2a_channel_id/conversation_group_id — but that feeds the
   * dispatcher, not these emit guards directly.
   *
   * No fail-closed drop-unknown guard is used: that would risk dropping a legitimate
   * 1:1 owner DM that arrives before room/A2A hydration (a North-Star violation). The
   * reachable residual (truly-cold local state AND the server omitting its routing field
   * on a room/A2A row) is empty today because the only paths that carry room/A2A rows
   * (poll + MLS dispatcher) always populate the field; DR-delivery and sync are
   * structurally 1:1-only.
   */
  private _isNonDmConversation(convId?: string, convGroupId?: string): boolean {
    const ids = [convId, convGroupId].filter((x): x is string => !!x);
    if (ids.length === 0) return false;
    if (this._persisted?.rooms) {
      for (const room of Object.values(this._persisted.rooms)) {
        if (room.conversationIds?.some((c) => ids.includes(c))) return true;
      }
    }
    if (this._persisted?.a2aChannels) {
      for (const ch of Object.values(this._persisted.a2aChannels)) {
        if (ch.conversationId && ids.includes(ch.conversationId)) return true;
      }
    }
    return false;
  }

  /**
   * Get recent message history for a specific room, for LLM context injection.
   * Returns the last N messages tagged with `room:{roomId}`.
   */
  getRoomHistory(roomId: string, maxMessages = 20): HistoryEntry[] {
    const history = this._persisted?.messageHistory ?? [];
    const tag = `room:${roomId}`;
    return history.filter((e) => e.topicId === tag).slice(-maxMessages);
  }

  /**
   * Encrypt and send a message to ALL owner devices (fanout).
   * Each session gets the same plaintext encrypted independently.
   */
  async send(plaintext: string, options?: SendOptions): Promise<void> {
    // Permanent failures — still throw
    if (this._state === "error" || this._state === "idle") {
      throw new Error("Channel is not ready");
    }
    if (this._sessions.size === 0) {
      throw new Error("No active sessions");
    }

    // Wake agent proactively before sending (fire-and-forget, best-effort)
    import("./openclaw-compat.js")
      .then(({ requestHeartbeatNow }) => requestHeartbeatNow({ reason: "channel-send" }))
      .catch(() => {});

    const targetConvId = options?.conversationId;
    const topicId = options?.topicId ?? this._lastIncomingTopicId ?? this._persisted?.defaultTopicId;
    const messageType = options?.messageType ?? "text";
    const priority = options?.priority ?? "normal";
    const parentSpanId = options?.parentSpanId;
    const envelopeMetadata = options?.metadata;

    // Outbound scan — before encryption
    let scanStatus: string | undefined;
    if (this._scanEngine) {
      const scanResult = this._scanEngine.scanOutbound(plaintext);
      if (scanResult.status === "blocked") {
        this.emit("scan_blocked", { direction: "outbound", violations: scanResult.violations });
        throw new Error(`Message blocked by scan rule: ${scanResult.violations[0]?.rule_name}`);
      }
      scanStatus = scanResult.status; // "clean" or "flagged"
    }

    // Store agent message in history for cross-device replay
    this._appendHistory("agent", plaintext, topicId);

    // Build set of room conversation IDs to exclude from 1:1 send
    const roomConvIds = new Set<string>();
    if (this._persisted?.rooms) {
      for (const room of Object.values(this._persisted.rooms)) {
        for (const cid of room.conversationIds) {
          roomConvIds.add(cid);
        }
      }
    }

    const messageGroupId = randomUUID();
    const clientMessageId = randomUUID();
    let sentCount = 0;
    const pendingWsSends: string[] = [];
    const sentSharedGroupIds = new Set<string>();

    // --- Shared 1:1 MLS groups first (encrypt once per group) ---
    if (this._persisted?.mlsGroups && this._state === "ready" && this._ws) {
      for (const [gid, entry] of Object.entries(this._persisted.mlsGroups)) {
        if (!entry.mlsGroupId) continue;
        const mlsGroup = this._mlsGroups.get(`1to1-group:${gid}`);
        if (!mlsGroup?.isInitialized || Number(mlsGroup.epoch) <= 0) continue;

        try {
          const plaintextBytes = new TextEncoder().encode(plaintext);
          const cipherBytes = await mlsGroup.encrypt(plaintextBytes);
          await saveMlsState(this.config.dataDir, entry.mlsGroupId, JSON.stringify(mlsGroup.exportState()));

          const payload: Record<string, unknown> = {
            group_id: entry.mlsGroupId,
            epoch: Number(mlsGroup.epoch),
            payload: Buffer.from(cipherBytes).toString("hex"),
            topic_id: topicId,
            client_message_id: clientMessageId,
            message_type: messageType,
            priority: priority,
            parent_span_id: parentSpanId,
            metadata: scanStatus
              ? { ...(envelopeMetadata ?? {}), scan_status: scanStatus }
              : envelopeMetadata,
          };
          if (this._persisted?.hubAddress) payload.hub_address = this._persisted.hubAddress;
          if (this._persisted?.hubId) payload.sender_hub_id = this._persisted.hubId;
          pendingWsSends.push(JSON.stringify({ event: "message_mls", data: payload }));
          sentSharedGroupIds.add(gid);
          sentCount++;
          console.log(`[SecureChannel] Shared MLS group send for group ${gid.slice(0, 8)} (${entry.mlsGroupId.slice(0, 8)})`);
        } catch (err) {
          console.error(`[SecureChannel] Shared MLS group send failed for ${gid.slice(0, 8)}:`, err);
        }
      }
    }

    for (const [convId, session] of this._sessions) {
      if (!session.activated) continue;
      // Skip room pairwise sessions — those use sendToRoom()
      if (roomConvIds.has(convId)) continue;
      // If a specific conversation is targeted, skip all others
      if (targetConvId && convId !== targetConvId) continue;

      // Skip if this conversation's group was already sent via shared MLS group above
      const convGroupId = this._sessionGroupIds?.get(convId);
      if (convGroupId && sentSharedGroupIds.has(convGroupId)) {
        sentCount++;
        continue;
      }

      try {
        // MLS send path — prefer shared 1:1 group, fall back to per-conversation
        const mlsGroup = (convGroupId ? this._mlsGroups.get(`1to1-group:${convGroupId}`) : null)
          ?? this._mlsGroups.get(`conv:${convId}`);
        const mlsGroupId = (convGroupId ? this._persisted?.mlsGroups?.[convGroupId]?.mlsGroupId : null)
          ?? this._persisted?.mlsConversations?.[convId]?.mlsGroupId;

        // Skip if we already sent via this shared group (another conv in same group)
        if (convGroupId && mlsGroupId && sentSharedGroupIds.has(convGroupId)) {
          sentCount++;
          continue;
        }

        if (mlsGroup?.isInitialized && mlsGroupId && (Number(mlsGroup.epoch) > 0) && this._state === "ready" && this._ws) {
          const plaintextBytes = new TextEncoder().encode(plaintext);
          const cipherBytes = await mlsGroup.encrypt(plaintextBytes);
          await saveMlsState(this.config.dataDir, mlsGroupId, JSON.stringify(mlsGroup.exportState()));

          const payload: Record<string, unknown> = {
            conversation_id: convId,
            group_id: mlsGroupId,
            epoch: Number(mlsGroup.epoch),
            payload: Buffer.from(cipherBytes).toString("hex"),
            topic_id: topicId,
            client_message_id: clientMessageId,
            message_type: messageType,
            priority: priority,
            parent_span_id: parentSpanId,
            metadata: scanStatus
              ? { ...(envelopeMetadata ?? {}), scan_status: scanStatus }
              : envelopeMetadata,
          };
          if (this._persisted?.hubAddress) {
            payload.hub_address = this._persisted.hubAddress;
          }
          if (this._persisted?.hubId) {
            payload.sender_hub_id = this._persisted.hubId;
          }
          pendingWsSends.push(
            JSON.stringify({
              event: "message_mls",
              data: payload,
            }),
          );
          if (convGroupId) sentSharedGroupIds.add(convGroupId);
        } else {
          // Double Ratchet fallback
          const encrypted = session.ratchet.encrypt(plaintext);
          const transport = encryptedMessageToTransport(encrypted);

          const msg = {
            convId,
            headerBlob: transport.header_blob,
            ciphertext: transport.ciphertext,
            messageGroupId,
            topicId,
          };

          if (this._state === "ready" && this._ws) {
            const payload: Record<string, unknown> = {
              conversation_id: msg.convId,
              header_blob: msg.headerBlob,
              ciphertext: msg.ciphertext,
              message_group_id: msg.messageGroupId,
              topic_id: msg.topicId,
              client_message_id: clientMessageId,
              message_type: messageType,
              priority: priority,
              parent_span_id: parentSpanId,
              metadata: scanStatus
                ? { ...(envelopeMetadata ?? {}), scan_status: scanStatus }
                : envelopeMetadata,
            };
            if (this._persisted?.hubAddress) {
              payload.hub_address = this._persisted.hubAddress;
            }
            if (this._persisted?.hubId) {
              payload.sender_hub_id = this._persisted.hubId;
            }
            pendingWsSends.push(
              JSON.stringify({
                event: "message",
                data: payload,
              }),
            );
          } else {
            // Queue for later (DR only — MLS requires live WS)
            if (!this._persisted!.outboundQueue) {
              this._persisted!.outboundQueue = [];
            }
            if (this._persisted!.outboundQueue.length >= 50) {
              this._persisted!.outboundQueue.shift();
              console.warn("[SecureChannel] Outbound queue full, dropping oldest message");
            }
            this._persisted!.outboundQueue.push(msg);
            console.log(`[SecureChannel] Message queued (state=${this._state}, queue=${this._persisted!.outboundQueue.length})`);
          }
        }
        sentCount++;
      } catch (err) {
        // Per-session error — log and continue to next session so one
        // stale/broken session doesn't prevent delivery to healthy ones
        console.error(`[SecureChannel] send() failed for conv ${convId.slice(0, 8)}...:`, err);
      }
    }

    // MLS-only send path: for conversations with MLS groups but no DR session.
    // This handles 1:1 chats where the message arrived via MLS from a device
    // that may not have a corresponding DR session in the agent's session map.
    if (this._persisted?.mlsConversations && this._state === "ready" && this._ws) {
      for (const [mlsConvId, entry] of Object.entries(this._persisted.mlsConversations)) {
        if (targetConvId && mlsConvId !== targetConvId) continue;
        if (this._sessions.has(mlsConvId)) continue; // already handled above
        if (roomConvIds.has(mlsConvId)) continue;
        // Prefer shared 1:1 group, fall back to per-conversation
        const mlsOnlyConvGroupId = this._sessionGroupIds?.get(mlsConvId);
        const mlsGroup = (mlsOnlyConvGroupId ? this._mlsGroups.get(`1to1-group:${mlsOnlyConvGroupId}`) : null)
          ?? this._mlsGroups.get(`conv:${mlsConvId}`);
        const resolvedMlsGroupId = (mlsOnlyConvGroupId ? this._persisted?.mlsGroups?.[mlsOnlyConvGroupId]?.mlsGroupId : null)
          ?? entry.mlsGroupId;
        if (!mlsGroup?.isInitialized || !resolvedMlsGroupId || Number(mlsGroup.epoch) <= 0) continue;
        // Skip if we already sent via this shared group
        if (mlsOnlyConvGroupId && sentSharedGroupIds.has(mlsOnlyConvGroupId)) {
          sentCount++;
          continue;
        }

        try {
          const plaintextBytes = new TextEncoder().encode(plaintext);
          const cipherBytes = await mlsGroup.encrypt(plaintextBytes);
          await saveMlsState(this.config.dataDir, resolvedMlsGroupId, JSON.stringify(mlsGroup.exportState()));

          const payload: Record<string, unknown> = {
            conversation_id: mlsConvId,
            group_id: resolvedMlsGroupId,
            epoch: Number(mlsGroup.epoch),
            payload: Buffer.from(cipherBytes).toString("hex"),
            topic_id: topicId,
            client_message_id: clientMessageId,
            message_type: messageType,
            priority: priority,
            parent_span_id: parentSpanId,
            metadata: envelopeMetadata,
          };
          if (this._persisted?.hubAddress) payload.hub_address = this._persisted.hubAddress;
          if (this._persisted?.hubId) payload.sender_hub_id = this._persisted.hubId;
          pendingWsSends.push(JSON.stringify({ event: "message_mls", data: payload }));
          sentCount++;
          if (mlsOnlyConvGroupId) sentSharedGroupIds.add(mlsOnlyConvGroupId);
          console.log(`[SecureChannel] MLS-only send for conv ${mlsConvId.slice(0, 8)} (no DR session)`);
        } catch (err) {
          console.error(`[SecureChannel] MLS-only send failed for conv ${mlsConvId.slice(0, 8)}:`, err);
        }
      }
    }

    if (sentCount === 0 && this._sessions.size > 0) {
      console.warn("[SecureChannel] send() delivered to 0 sessions (all skipped or failed)");
    }

    // Persist ratchet states BEFORE sending over WS to avoid desync on crash
    await this._persistState();

    for (const frame of pendingWsSends) {
      this._ws!.send(frame);
    }
  }

  /**
   * Send a typing indicator to all owner devices.
   * Ephemeral (unencrypted metadata), no ratchet advancement.
   */
  sendTyping(): void {
    if (!this._ws || this._ws.readyState !== WebSocket.OPEN) return;
    for (const convId of this._sessions.keys()) {
      this._ws.send(
        JSON.stringify({
          event: "typing",
          data: { conversation_id: convId },
        }),
      );
    }
  }

  /**
   * Send an activity span to all owner devices via WS.
   * Ephemeral (unencrypted metadata, like typing), no ratchet advancement.
   *
   * Includes a `resource` block so the backend can persist the span with
   * the same resource attributes that TelemetryReporter spans get
   * (av.plugin.version, service.version, gen_ai.agent.*). Without this,
   * legacy activity_span paths produce resource-less span rows.
   */
  sendActivitySpan(spanData: Record<string, unknown>): void {
    if (!this._ws || this._ws.readyState !== WebSocket.OPEN) return;
    const pluginVersion =
      typeof __AV_VERSION__ !== "undefined" ? __AV_VERSION__ : "0.0.0-dev";
    const agentName = this.config.agentName ?? "Agent";
    const resource: Record<string, string> = {
      "service.name": "agentvault-agent",
      "service.version": pluginVersion,
      "av.plugin.version": pluginVersion,
      "gen_ai.agent.name": agentName,
      "gen_ai.agent.version": this.config.agentVersion ?? "0.0.0",
    };
    if (this._persisted?.hubId) {
      resource["gen_ai.agent.id"] = this._persisted.hubId;
    }
    this._ws.send(
      JSON.stringify({
        event: "activity_span",
        data: {
          ...spanData,
          agent_name: agentName,
          resource,
        },
      }),
    );
  }

  /**
   * Send a worker-capability heartbeat to the backend (Slice 2 Plan C).
   * Reports whether this device can run the native tool-capable worker and
   * which rooms it currently considers armed, so the backend's arming
   * record stays in sync with the actual bridge/worker state.
   */
  sendWorkerHeartbeat(h: { workerCapable: boolean; armedRooms: string[] }): void {
    if (!this._ws || this._ws.readyState !== WebSocket.OPEN) return;
    this._ws.send(
      JSON.stringify({
        event: "worker_heartbeat",
        data: { worker_capable: h.workerCapable, armed_rooms: h.armedRooms },
      }),
    );
  }

  /**
   * Send a decision request to the owner.
   * Builds a structured envelope with decision metadata and sends it
   * as a high-priority message. Returns the generated decision_id.
   */
  async sendDecisionRequest(request: DecisionRequest): Promise<string> {
    const decision_id = `dec_${randomUUID().replace(/-/g, "").slice(0, 16)}`;

    const payload = JSON.stringify({
      type: "message",
      text: `\u{1F4CB} ${request.title}`,
      decision: {
        decision_id,
        ...request,
      },
    });

    await this.send(payload, {
      messageType: "decision_request",
      priority: "high",
      metadata: {
        decision_id,
        title: request.title,
        description: request.description,
        options: request.options,
        context_refs: request.context_refs,
        deadline: request.deadline,
        auto_action: request.auto_action,
      },
    });

    return decision_id;
  }

  /**
   * Wait for a decision response matching the given decisionId.
   * Listens on the "message" event for messages where
   * metadata.messageType === "decision_response" and the parsed plaintext
   * contains a matching decision.decision_id.
   * Optional timeout rejects with an Error.
   */
  waitForDecision(decisionId: string, timeoutMs?: number): Promise<DecisionResponse> {
    return new Promise<DecisionResponse>((resolve, reject) => {
      let timer: ReturnType<typeof setTimeout> | null = null;

      const handler = (plaintext: string, metadata: MessageMetadata) => {
        if (metadata.messageType !== "decision_response") return;

        try {
          const parsed = JSON.parse(plaintext);
          if (parsed.decision?.decision_id === decisionId) {
            if (timer) clearTimeout(timer);
            this.removeListener("message", handler);
            resolve({
              decision_id: parsed.decision.decision_id,
              selected_option_id: parsed.decision.selected_option_id,
              resolved_at: parsed.decision.resolved_at,
              action: parsed.decision.action,
              defer_until: parsed.decision.defer_until,
              defer_reason: parsed.decision.defer_reason,
            });
          }
        } catch {
          // Not valid JSON, ignore
        }
      };

      this.on("message", handler);

      if (timeoutMs !== undefined) {
        timer = setTimeout(() => {
          this.removeListener("message", handler);
          reject(new Error(`Decision ${decisionId} timed out after ${timeoutMs}ms`));
        }, timeoutMs);
      }
    });
  }

  // --- Multi-agent room methods ---

  /**
   * Join a room by collecting pairwise conversation IDs involving this device.
   * Encryption is handled by MLS group operations, not per-member DR sessions.
   */
  async joinRoom(roomData: {
    roomId: string;
    name: string;
    members: RoomMemberInfo[];
    conversations: RoomConversationInfo[];
    forceRekey?: boolean;
  }): Promise<void> {
    if (!this._persisted) {
      throw new Error("Channel not initialized");
    }

    await sodium.ready;

    const myDeviceId = this._deviceId!;
    const conversationIds: string[] = [];

    for (const conv of roomData.conversations) {
      if (conv.participantA !== myDeviceId && conv.participantB !== myDeviceId) {
        continue;
      }
      conversationIds.push(conv.id);
    }

    // Store room state — preserve MLS fields from prior session
    if (!this._persisted.rooms) {
      this._persisted.rooms = {};
    }
    const existingMlsGroupId = this._persisted.rooms[roomData.roomId]?.mlsGroupId;
    const existingMlsTs = (this._persisted.rooms[roomData.roomId] as any)?.lastMlsMessageTs;
    this._persisted.rooms[roomData.roomId] = {
      roomId: roomData.roomId,
      name: roomData.name,
      conversationIds,
      members: roomData.members,
      ...(existingMlsGroupId && { mlsGroupId: existingMlsGroupId }),
      ...(existingMlsTs && { lastMlsMessageTs: existingMlsTs }),
    };

    // --- MLS group initialization ---
    const mlsRoom = this._persisted.rooms![roomData.roomId];

    // Check if we already have MLS state for this room
    if (mlsRoom.mlsGroupId && !roomData.forceRekey) {
      const existingState = await loadMlsState(this.config.dataDir, mlsRoom.mlsGroupId);
      if (existingState) {
        try {
          const mgr = new MLSGroupManager();
          mgr.importState(JSON.parse(existingState) as SerializedMLSGroupState);
          this._mlsGroups.set(roomData.roomId, mgr);
          console.log(`[SecureChannel] Loaded MLS group for room ${roomData.roomId.slice(0, 8)} (epoch=${mgr.epoch})`);
        } catch (mlsLoadErr) {
          console.warn(`[SecureChannel] Failed to load MLS state for room ${roomData.roomId.slice(0, 8)}:`, mlsLoadErr);
        }
      }
    } else if (roomData.forceRekey && mlsRoom.mlsGroupId) {
      // Force rekey: clear MLS state
      await deleteMlsState(this.config.dataDir, mlsRoom.mlsGroupId);
      this._mlsGroups.delete(roomData.roomId);
      delete mlsRoom.mlsGroupId;
    }

    // Register with MLS group on backend (agent never creates — waits for Welcome from owner)
    if (!mlsRoom.mlsGroupId) {
      try {
        const initRes = await fetch(`${this.config.apiUrl}/api/v1/mls/rooms/${roomData.roomId}/init`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${this._deviceJwt}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            member_device_ids: roomData.members.map(m => m.deviceId),
          }),
        });

        if (initRes.ok) {
          const groupData = await initRes.json();
          mlsRoom.mlsGroupId = groupData.group_id;
          // Agent does NOT create the group — owner will send Welcome via mls_welcome event.
          // The _handleMlsWelcome handler will call joinFromWelcome and set up the MLS group.
          console.log(`[SecureChannel] MLS room ${roomData.roomId.slice(0, 8)} registered (groupId=${groupData.group_id.slice(0, 8)}, awaiting Welcome from owner)`);
        }
      } catch (mlsInitErr) {
        console.error(`[SecureChannel] MLS room init failed:`, mlsInitErr);
      }
    }

    // GH #366: a fresh (re)join may carry a stale/partial roster snapshot, so
    // force the next inbound room message to re-fetch the authoritative roster.
    this._roomRosterRefreshed.delete(roomData.roomId);

    await this._persistState();
    this.emit("room_joined", { roomId: roomData.roomId, name: roomData.name });
  }

  /**
   * Send an encrypted message to all members of a room.
   * Uses MLS (preferred) or pairwise fan-out as fallback.
   */
  async sendToRoom(
    roomId: string,
    plaintext: string,
    opts?: {
      messageType?: string;
      priority?: string;
      metadata?: Record<string, unknown>;
    },
  ): Promise<void> {
    if (!this._persisted?.rooms?.[roomId]) {
      throw new Error(`Room ${roomId} not found`);
    }

    // Persist outgoing room message to history for contextual memory
    const msgType = opts?.messageType ?? "text";
    if (msgType === "text" && plaintext) {
      this._appendHistory("agent", plaintext, `room:${roomId}`);
    }

    // Wake agent proactively before sending (fire-and-forget, best-effort)
    import("./openclaw-compat.js")
      .then(({ requestHeartbeatNow }) => requestHeartbeatNow({ reason: "channel-sendToRoom" }))
      .catch(() => {});

    const room = this._persisted.rooms[roomId];
    const messageType = opts?.messageType ?? "text";

    // ── MLS path (preferred) ──
    if (room.mlsGroupId) {
      const mlsGroup = this._mlsGroups.get(roomId);
      if (mlsGroup?.isInitialized) {
        try {
          const plaintextBytes = new TextEncoder().encode(plaintext);
          const ciphertext = await mlsGroup.encrypt(plaintextBytes);

          // Persist MLS state BEFORE sending
          await saveMlsState(this.config.dataDir, room.mlsGroupId, JSON.stringify(mlsGroup.exportState()));

          if (this._state === "ready" && this._ws) {
            this._ws.send(JSON.stringify({
              event: "room_message_mls",
              data: {
                room_id: roomId,
                group_id: room.mlsGroupId,
                epoch: Number(mlsGroup.epoch),
                payload: Buffer.from(ciphertext).toString("hex"),
                message_type: messageType,
                priority: opts?.priority ?? "normal",
                metadata: opts?.metadata,
              },
            }));
          }
          return;
        } catch (mlsErr) {
          throw new Error(
            `MLS encrypt failed for room ${roomId.slice(0, 8)}... — ` +
            `MLS group must be initialized before sending. Error: ${mlsErr}`,
          );
        }
      }
    }

    // No MLS group — error (DR fallback removed)
    throw new Error(
      `Room ${roomId.slice(0, 8)}... has no initialized MLS group. ` +
      `MLS initialization must complete before messages can be sent.`,
    );
  }

  /**
   * Leave a room: remove sessions and persisted room state.
   */
  async leaveRoom(roomId: string): Promise<void> {
    if (!this._persisted?.rooms?.[roomId]) {
      return; // Already left or never joined
    }

    const room = this._persisted.rooms[roomId];

    // Remove sessions for this room's conversations
    for (const convId of room.conversationIds) {
      this._sessions.delete(convId);
      delete this._persisted.sessions[convId];
    }

    // Remove room state
    delete this._persisted.rooms[roomId];

    await this._persistState();
    this.emit("room_left", { roomId });
  }

  /**
   * Return info for all joined rooms.
   */
  getRooms(): RoomInfo[] {
    if (!this._persisted?.rooms) return [];
    return Object.values(this._persisted.rooms).map((rs) => ({
      roomId: rs.roomId,
      name: rs.name,
      members: rs.members,
      conversationIds: rs.conversationIds,
    }));
  }

  /**
   * Refresh a room's member roster from the authoritative backend roster
   * (GET /rooms/{roomId}) and merge it into the persisted state (GH #366).
   *
   * The local roster is snapshotted at join-time (joinRoom) and only refreshed
   * by a later `room_joined` WS re-broadcast. When members join concurrently,
   * the first inbound message can arrive BEFORE that re-broadcast, so the agent
   * processes a message with an incomplete roster (e.g. self + owner but not the
   * other agent) and frames a group room as a 1:1 DM. Fetching on demand here
   * makes the roster complete before the message is surfaced.
   *
   * Best-effort: on any error (offline, 404, auth) the existing roster is kept.
   * Concurrent calls for the same room are deduped via _roomRosterRefreshInFlight.
   */
  private async _refreshRoomRoster(roomId: string): Promise<void> {
    const existing = this._roomRosterRefreshInFlight.get(roomId);
    if (existing) return existing;

    const run = (async () => {
      const jwt = this._deviceJwt ?? this._persisted?.deviceJwt;
      if (!jwt || !this._persisted?.rooms?.[roomId]) return;
      try {
        const res = await fetch(`${this.config.apiUrl}/api/v1/rooms/${roomId}`, {
          headers: { Authorization: `Bearer ${jwt}` },
        });
        if (!res.ok) return;
        const room = await res.json();
        const members: RoomMemberInfo[] = (room.members || [])
          // Active members only — drop members who have left.
          .filter((m: any) => !m.left_at && (m.status === undefined || m.status === "active"))
          .map((m: any) => ({
            deviceId: m.device_id,
            entityType: m.entity_type,
            displayName: m.display_name,
            identityPublicKey: m.identity_public_key,
            ephemeralPublicKey: m.ephemeral_public_key,
          }));
        if (members.length > 0) {
          const roomState = this._persisted.rooms[roomId];
          const before = roomState.members?.length ?? 0;
          roomState.members = members;
          this._roomRosterRefreshed.add(roomId);
          if (members.length !== before) {
            console.log(
              `[SecureChannel] Roster refreshed for room ${roomId.slice(0, 8)}: ${before} → ${members.length} members`,
            );
            await this._persistState();
          }
        }
      } catch (err) {
        console.warn(`[SecureChannel] Roster refresh failed for room ${roomId.slice(0, 8)}:`, err);
      }
    })();

    this._roomRosterRefreshInFlight.set(roomId, run);
    try {
      await run;
    } finally {
      this._roomRosterRefreshInFlight.delete(roomId);
    }
  }

  // --- Heartbeat and status methods ---

  startHeartbeat(
    intervalSeconds: number,
    statusCallback: () => HeartbeatStatus,
  ): void {
    this.stopHeartbeat();
    this._heartbeatCallback = statusCallback;
    this._heartbeatIntervalSeconds = intervalSeconds;
    this._sendHeartbeat();
    this._heartbeatTimer = setInterval(() => {
      this._sendHeartbeat();
    }, intervalSeconds * 1000);
  }

  async stopHeartbeat(): Promise<void> {
    if (this._heartbeatTimer) {
      clearInterval(this._heartbeatTimer);
      this._heartbeatTimer = null;
    }
    if (this._heartbeatCallback && this._state === "ready") {
      try {
        await this.send(
          JSON.stringify({
            agent_status: "shutting_down",
            current_task: "",
            timestamp: new Date().toISOString(),
          }),
          {
            messageType: "heartbeat",
            metadata: { next_heartbeat_seconds: this._heartbeatIntervalSeconds },
          },
        );
      } catch { /* best-effort shutdown heartbeat */ }
    }
    this._heartbeatCallback = null;
  }

  async sendStatusAlert(alert: StatusAlert): Promise<void> {
    const priority = alert.severity === "error" || alert.severity === "critical"
      ? "high" : "normal";
    const envelope: Record<string, unknown> = {
      title: alert.title,
      message: alert.message,
      severity: alert.severity,
      timestamp: new Date().toISOString(),
    };
    if (alert.detail !== undefined) envelope.detail = alert.detail;
    if (alert.detailFormat !== undefined) envelope.detail_format = alert.detailFormat;
    if (alert.category !== undefined) envelope.category = alert.category;
    await this.send(
      JSON.stringify(envelope),
      {
        messageType: "status_alert",
        priority,
        metadata: { severity: alert.severity },
      },
    );
  }

  async sendArtifact(artifact: {
    filePath: string;
    filename: string;
    mimeType: string;
    description?: string;
  }): Promise<void> {
    if (this._state !== "ready" || this._sessions.size === 0 || !this._ws) {
      throw new Error("Channel is not ready");
    }

    // Upload encrypted file via existing attachment infrastructure
    const attachMeta = await this._uploadAttachment(artifact.filePath, this._primaryConversationId);

    // Build artifact_share envelope
    const envelope = JSON.stringify({
      type: "artifact",
      blob_id: attachMeta.blobId,
      blob_url: attachMeta.blobUrl,
      filename: artifact.filename,
      mime_type: artifact.mimeType,
      size_bytes: attachMeta.size,
      description: artifact.description,
      attachment: attachMeta,
    });

    const messageGroupId = randomUUID();
    const pendingWsSends: string[] = [];

    for (const [convId, session] of this._sessions) {
      if (!session.activated) continue;
      const encrypted = session.ratchet.encrypt(envelope);
      const transport = encryptedMessageToTransport(encrypted);
      pendingWsSends.push(
        JSON.stringify({
          event: "message",
          data: {
            conversation_id: convId,
            header_blob: transport.header_blob,
            ciphertext: transport.ciphertext,
            message_group_id: messageGroupId,
            message_type: "artifact_share",
          },
        }),
      );
    }

    // Persist ratchet states BEFORE sending over WS to avoid desync on crash
    await this._persistState();

    for (const frame of pendingWsSends) {
      this._ws.send(frame);
    }
  }

  async sendActionConfirmation(confirmation: {
    action: string;
    status: "completed" | "failed" | "partial";
    decisionId?: string;
    detail?: string;
  }): Promise<void> {
    const envelope: Record<string, unknown> = {
      type: "action_confirmation",
      action: confirmation.action,
      status: confirmation.status,
    };
    if (confirmation.decisionId !== undefined) envelope.decision_id = confirmation.decisionId;
    if (confirmation.detail !== undefined) envelope.detail = confirmation.detail;

    await this.send(
      JSON.stringify(envelope),
      {
        messageType: "action_confirmation",
        metadata: { status: confirmation.status },
      },
    );
  }

  async sendActionConfirmationToRoom(
    roomId: string,
    confirmation: {
      action: string;
      status: "completed" | "failed" | "partial";
      decisionId?: string;
      detail?: string;
      estimated_cost?: number;
    },
  ): Promise<void> {
    const envelope: Record<string, unknown> = {
      type: "action_confirmation",
      action: confirmation.action,
      status: confirmation.status,
    };
    if (confirmation.decisionId !== undefined) envelope.decision_id = confirmation.decisionId;
    if (confirmation.detail !== undefined) envelope.detail = confirmation.detail;

    const metadata: Record<string, unknown> = {
      action: confirmation.action,
      status: confirmation.status,
    };
    if (confirmation.estimated_cost !== undefined) {
      metadata.estimated_cost = confirmation.estimated_cost;
    }

    await this.sendToRoom(roomId, JSON.stringify(envelope), {
      messageType: "action_confirmation",
      priority: "high",
      metadata,
    });
  }

  // --- Unified Delivery Protocol ---

  /**
   * Canonical message dispatcher. ALL outbound messages should flow through this method.
   * Routes based on explicit target — never silently falls back to a room.
   */
  async deliver(
    target: DeliveryTarget,
    content: DeliveryContent,
    options?: DeliveryOptions,
  ): Promise<DeliveryReceipt> {
    const ts = new Date().toISOString();

    // Validate channel state
    if (this._state === "idle" || this._state === "error") {
      return {
        ok: false,
        destination: { kind: "owner" },
        error: `Channel is in ${this._state} state`,
        timestamp: ts,
      };
    }

    // Resolve target
    let resolved: { kind: "owner" | "room" | "a2a"; id?: string };
    if (target.kind === "context") {
      if (this._lastInboundRoomId) {
        resolved = { kind: "room", id: this._lastInboundRoomId };
        console.log(`[deliver] target=context→room:${this._lastInboundRoomId.slice(0, 8)}...`);
      } else {
        resolved = { kind: "owner" };
        console.log(`[deliver] target=context→owner`);
      }
    } else if (target.kind === "owner") {
      resolved = { kind: "owner" };
    } else if (target.kind === "room") {
      if (!this._persisted?.rooms?.[target.roomId]) {
        const err = `Room ${target.roomId} not found`;
        console.log(`[deliver] target=room:${target.roomId.slice(0, 8)}... content=${content.type} result=FAIL: ${err}`);
        return { ok: false, destination: { kind: "room", id: target.roomId }, error: err, timestamp: ts };
      }
      resolved = { kind: "room", id: target.roomId };
    } else if (target.kind === "a2a") {
      // Look up channel by hub address in participants — try persisted state first
      const a2aEntries = this._persisted?.a2aChannels ? Object.values(this._persisted.a2aChannels) : [];
      let channelEntry = a2aEntries.find((ch) =>
        ch.participants?.some(p => p.hubAddress === target.hubAddress && (p.status === "active" || p.status === "invited")),
      );

      // If not found, fetch from API and send directly (bypasses persisted state race)
      if (!channelEntry && content.type === "text" && this._ws && this._state === "ready") {
        try {
          console.log(`[deliver] A2A local miss for ${target.hubAddress}, trying direct API send...`);
          const channels = await this.listA2AChannels();
          const match = channels.find((ch) =>
            (ch.status === "active" || ch.status === "approved") &&
            ch.participants?.some(p => p.hub_address === target.hubAddress),
          );
          if (match?.conversationId) {
            // Re-read after sync updated persisted state
            channelEntry = Object.values(this._persisted?.a2aChannels ?? {}).find(
              (ch) => ch.participants?.some(p => p.hubAddress === target.hubAddress),
            );
          }
        } catch (syncErr) {
          console.warn("[deliver] A2A direct send failed:", syncErr);
        }
      }

      if (!channelEntry) {
        const storedAddresses = a2aEntries.flatMap(ch => ch.participants?.map(p => p.hubAddress) ?? []).join(", ") || "(none)";
        const err = `No A2A channel found for hub address: ${target.hubAddress} (stored: ${storedAddresses})`;
        console.log(`[deliver] target=a2a:${target.hubAddress} content=${content.type} result=FAIL: ${err}`);
        return { ok: false, destination: { kind: "a2a", id: target.hubAddress }, error: err, timestamp: ts };
      }
      resolved = { kind: "a2a", id: target.hubAddress };
    } else if (target.kind === "a2a_channel") {
      // Direct channel ID routing — for multi-agent A2A channels
      const channelEntry = this._persisted?.a2aChannels?.[target.channelId];
      if (!channelEntry) {
        // Try syncing first
        try {
          await this.listA2AChannels();
        } catch { /* ignore */ }
        const entry = this._persisted?.a2aChannels?.[target.channelId];
        if (!entry) {
          const err = `A2A channel ${target.channelId.slice(0, 8)} not found`;
          console.log(`[deliver] target=channel:${target.channelId.slice(0, 8)} content=${content.type} result=FAIL: ${err}`);
          return { ok: false, destination: { kind: "a2a", id: target.channelId }, error: err, timestamp: ts };
        }
      }
      resolved = { kind: "a2a", id: target.channelId };
    } else {
      return { ok: false, destination: { kind: "owner" }, error: "Unknown target kind", timestamp: ts };
    }

    // Outbound idempotency guard (#475): a misbehaving agent runtime can hand us
    // the SAME message for the same destination many times in a burst (prod: 23×
    // in ~5s). Collapse an exact-duplicate of a message we ALREADY sent to the
    // same destination/thread within the sliding window (and under the absolute
    // cap) to a single send, so the owner never receives N copies. The key is
    // recorded only AFTER a successful dispatch (below), so a failed send never
    // suppresses its retries. Fail-open: an error here must never block delivery.
    let dedupeKey: string | null = null;
    try {
      dedupeKey = outboundDedupeKey(resolved, content, options);
      if (dedupeKey) {
        const now = Date.now();
        const recent = (this._recentDeliveries ??= new Map());
        const prior = recent.get(dedupeKey);
        if (
          prior &&
          now - prior.lastTs < OUTBOUND_DEDUPE_WINDOW_MS &&
          now - prior.firstTs < OUTBOUND_DEDUPE_MAX_SUPPRESS_MS
        ) {
          prior.lastTs = now; // slide: keep a steady burst collapsed to the first send
          prior.suppressed += 1;
          console.warn(
            `[deliver] suppressed duplicate ${content.type} → ${resolved.kind}:${(resolved.id ?? "owner").slice(0, 8)} ` +
            `(x${prior.suppressed} within ${OUTBOUND_DEDUPE_WINDOW_MS}ms)`,
          );
          return { ok: true, destination: resolved, deduped: true, timestamp: ts };
        }
      }
    } catch (dedupeErr) {
      dedupeKey = null;
      console.warn("[deliver] dedupe guard error (ignored):", dedupeErr);
    }

    // Dispatch based on content type + resolved target
    try {
      let decisionId: string | undefined;

      if (content.type === "text") {
        if (resolved.kind === "room") {
          await this.sendToRoom(resolved.id!, content.text, {
            messageType: options?.metadata?.message_type as string | undefined,
            priority: options?.priority,
            metadata: options?.metadata,
          });
        } else if (resolved.kind === "a2a") {
          // resolved.id is hub address for kind=a2a, channel ID for kind=a2a_channel
          if (target.kind === "a2a_channel") {
            await this.sendToA2AChannel(resolved.id!, content.text);
          } else {
            await this.sendToAgent(resolved.id!, content.text, {
              parentSpanId: options?.parentSpanId,
            });
          }
        } else {
          await this.send(content.text, {
            conversationId: options?.conversationId,
            topicId: options?.topicId,
            messageType: options?.metadata?.message_type as string | undefined,
            priority: options?.priority,
            metadata: options?.metadata,
          });
        }
      } else if (content.type === "decision_request") {
        if (resolved.kind !== "owner") {
          const err = "Decision requests can only be sent to owner";
          console.log(`[deliver] target=${resolved.kind}:${resolved.id} content=decision_request result=FAIL: ${err}`);
          return { ok: false, destination: resolved, error: err, timestamp: ts };
        }
        decisionId = await this.sendDecisionRequest(content.request);
      } else if (content.type === "status_alert") {
        if (resolved.kind === "room") {
          const envelope: Record<string, unknown> = {
            title: content.alert.title,
            message: content.alert.message,
            severity: content.alert.severity,
            timestamp: ts,
          };
          if (content.alert.detail !== undefined) envelope.detail = content.alert.detail;
          if (content.alert.detailFormat !== undefined) envelope.detail_format = content.alert.detailFormat;
          if (content.alert.category !== undefined) envelope.category = content.alert.category;
          await this.sendToRoom(resolved.id!, JSON.stringify(envelope), {
            messageType: "status_alert",
            priority: content.alert.severity === "error" || content.alert.severity === "critical" ? "high" : "normal",
            metadata: { severity: content.alert.severity },
          });
        } else if (resolved.kind === "owner") {
          await this.sendStatusAlert(content.alert);
        } else {
          return { ok: false, destination: resolved, error: "Status alerts cannot be sent to A2A targets", timestamp: ts };
        }
      } else if (content.type === "action_confirmation") {
        if (resolved.kind === "room") {
          await this.sendActionConfirmationToRoom(resolved.id!, content.confirmation);
        } else if (resolved.kind === "owner") {
          await this.sendActionConfirmation(content.confirmation);
        } else {
          return { ok: false, destination: resolved, error: "Action confirmations cannot be sent to A2A targets", timestamp: ts };
        }
      } else if (content.type === "artifact") {
        if (resolved.kind !== "owner") {
          return { ok: false, destination: resolved, error: "Artifacts can only be sent to owner", timestamp: ts };
        }
        await this.sendArtifact(content.artifact);
      } else if (content.type === "attachment") {
        if (resolved.kind !== "owner") {
          return { ok: false, destination: resolved, error: "Attachments can only be sent to owner", timestamp: ts };
        }
        await this.sendWithAttachment(content.text, content.filePath, {
          topicId: options?.topicId,
        });
      }

      const targetLabel = resolved.kind === "owner" ? "owner" : `${resolved.kind}:${resolved.id?.slice(0, 8)}...`;
      console.log(`[deliver] target=${targetLabel} content=${content.type} result=ok`);

      // Record-after-success (#475/#476): only now that the send has succeeded do
      // we arm the dedupe window for this key, so a failed send never suppresses
      // its own retries. A continuing run keeps its firstTs (for the absolute cap);
      // a fresh run (no prior, window expired, or cap exceeded) starts a new one.
      if (dedupeKey) {
        try {
          const now = Date.now();
          const recent = (this._recentDeliveries ??= new Map());
          const prior = recent.get(dedupeKey);
          const continuing =
            !!prior &&
            now - prior.lastTs < OUTBOUND_DEDUPE_WINDOW_MS &&
            now - prior.firstTs < OUTBOUND_DEDUPE_MAX_SUPPRESS_MS;
          recent.set(dedupeKey, {
            firstTs: continuing ? prior!.firstTs : now,
            lastTs: now,
            suppressed: continuing ? prior!.suppressed : 0,
          });
          pruneRecentDeliveries(recent, now);
        } catch (recordErr) {
          console.warn("[deliver] dedupe record error (ignored):", recordErr);
        }
      }

      return {
        ok: true,
        destination: resolved,
        queued: this._state !== "ready",
        decisionId,
        timestamp: ts,
      };
    } catch (err) {
      const targetLabel = resolved.kind === "owner" ? "owner" : `${resolved.kind}:${resolved.id?.slice(0, 8)}...`;
      const errMsg = err instanceof Error ? err.message : String(err);
      console.log(`[deliver] target=${targetLabel} content=${content.type} result=FAIL: ${errMsg}`);
      return { ok: false, destination: resolved, error: errMsg, timestamp: ts };
    }
  }

  /**
   * Returns all available delivery destinations with availability status.
   */
  listTargets(): TargetInfo[] {
    const targets: TargetInfo[] = [
      {
        kind: "owner",
        id: "owner",
        label: "Owner (direct)",
        available: this._state === "ready" && this._sessions.size > 0,
      },
    ];

    // Add rooms
    if (this._persisted?.rooms) {
      for (const room of Object.values(this._persisted.rooms)) {
        targets.push({
          kind: "room",
          id: room.roomId,
          label: room.name || `Room ${room.roomId.slice(0, 8)}`,
          available: this._state === "ready" && room.conversationIds.length > 0,
        });
      }
    }

    // Add A2A peers (each active participant is a target)
    if (this._persisted?.a2aChannels) {
      for (const ch of Object.values(this._persisted.a2aChannels)) {
        for (const p of ch.participants ?? []) {
          if (p.status !== "active" && p.status !== "invited") continue;
          targets.push({
            kind: "a2a",
            id: p.hubAddress,
            label: `A2A: ${p.hubAddress}`,
            available: this._state === "ready" && (
              !!ch.mlsGroupId && this._mlsGroups.has(`a2a:${ch.channelId}`)
            ),
          });
        }
      }
    }

    return targets;
  }

  private _sendHeartbeat(): void {
    if (this._state !== "ready" || !this._heartbeatCallback) return;
    const status = this._heartbeatCallback();
    this.send(
      JSON.stringify({
        agent_status: status.agent_status,
        current_task: status.current_task,
        timestamp: new Date().toISOString(),
      }),
      {
        messageType: "heartbeat",
        metadata: { next_heartbeat_seconds: this._heartbeatIntervalSeconds },
      },
    ).catch((err) => {
      this.emit("error", new Error(`Heartbeat send failed: ${err}`));
    });
  }

  // --- Trust Gate token management ---

  getTrustHeaders(): Record<string, string> {
    if (!this._trustToken) return {};
    return { "AgentVault-Trust": this._trustToken };
  }

  get trustToken(): string | null {
    return this._trustToken;
  }

  get trustTier(): string | null {
    return this._trustTier;
  }

  get trustTokenExpiresAt(): string | null {
    return this._trustTokenExpiresAt;
  }

  async refreshTrustToken(): Promise<void> {
    try {
      const res = await fetch(`${this.config.apiUrl}/api/v1/gate/token`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${this._deviceJwt}`,
          "Content-Type": "application/json",
        },
      });
      if (res.ok) {
        const data = await res.json();
        this._trustToken = data.token;
        this._trustTier = data.tier;
        this._trustTokenExpiresAt = data.expires_at;
      }
    } catch (err) {
      // Best-effort — keep existing token if available
      console.warn("[AgentVault] Trust token refresh failed:", err);
    }
  }

  startTrustTokenRefresh(): void {
    // Refresh every 12 minutes (80% of 15-min TTL)
    this.refreshTrustToken();
    this._trustTokenInterval = setInterval(
      () => this.refreshTrustToken(),
      12 * 60 * 1000,
    );
  }

  stopTrustTokenRefresh(): void {
    if (this._trustTokenInterval) {
      clearInterval(this._trustTokenInterval);
      this._trustTokenInterval = null;
    }
  }

  async stop(): Promise<void> {
    this._stopped = true;
    await this.stopHeartbeat();
    this.stopTrustTokenRefresh();
    this._flushAcks(); // Send any pending ACKs before stopping
    this._stopPing();
    this._stopWakeDetector();
    this._stopPendingPoll();
    this._stopPollFallback();
    this._stopHttpServer();
    if (this._ackTimer) {
      clearTimeout(this._ackTimer);
      this._ackTimer = null;
    }
    if (this._pollTimer) {
      clearTimeout(this._pollTimer);
      this._pollTimer = null;
    }
    if (this._reconnectTimer) {
      clearTimeout(this._reconnectTimer);
      this._reconnectTimer = null;
    }
    // Flush and clean up telemetry reporter before closing WebSocket
    if (this._telemetryReporter) {
      this._telemetryReporter.stopAutoFlush();
      await this._telemetryReporter.flush();
      this._telemetryReporter = null;
    }
    // removeAllListeners() below means the "close" handler won't fire, so clear
    // interval timers here explicitly (otherwise they leak across stop()).
    this._stopA2aReconcileTimer();
    if (this._ws) {
      this._ws.removeAllListeners();
      this._ws.close();
      this._ws = null;
    }
    this._setState("disconnected");
  }

  // --- Local HTTP server for proactive sends ---

  startHttpServer(port: number): void {
    if (this._httpServer) return;

    // Lazy import to avoid circular dependency at module load time
    let _handlers: typeof import("./http-handlers.js") | null = null;
    const getHandlers = async () => {
      if (!_handlers) _handlers = await import("./http-handlers.js");
      return _handlers;
    };

    this._httpServer = createServer(async (req: IncomingMessage, res: ServerResponse) => {
      // Only accept local connections
      const remote = req.socket.remoteAddress;
      if (remote !== "127.0.0.1" && remote !== "::1" && remote !== "::ffff:127.0.0.1") {
        res.writeHead(403, { "Content-Type": "application/json" });
        res.end(JSON.stringify({ ok: false, error: "Forbidden" }));
        return;
      }

      const handlers = await getHandlers();

      if (req.method === "POST" && req.url === "/send") {
        let body = "";
        req.on("data", (chunk: Buffer) => { body += chunk.toString(); });
        req.on("end", async () => {
          try {
            const parsed = JSON.parse(body);
            const result = await handlers.handleSendRequest(parsed, this);
            res.writeHead(result.status, { "Content-Type": "application/json" });
            res.end(JSON.stringify(result.body));
          } catch (err) {
            res.writeHead(500, { "Content-Type": "application/json" });
            res.end(JSON.stringify({ ok: false, error: String(err) }));
          }
        });
      } else if (req.method === "POST" && req.url === "/decision") {
        let body = "";
        req.on("data", (chunk: Buffer) => { body += chunk.toString(); });
        req.on("end", async () => {
          try {
            const parsed = JSON.parse(body);
            const result = await handlers.handleDecisionRequest(parsed, this);
            res.writeHead(result.status, { "Content-Type": "application/json" });
            res.end(JSON.stringify(result.body));
          } catch (err) {
            res.writeHead(500, { "Content-Type": "application/json" });
            res.end(JSON.stringify({ ok: false, error: String(err) }));
          }
        });
      } else if (req.method === "POST" && req.url === "/action") {
        let body = "";
        req.on("data", (chunk: Buffer) => { body += chunk.toString(); });
        req.on("end", async () => {
          try {
            const parsed = JSON.parse(body);
            const result = await handlers.handleActionRequest(parsed, this);
            res.writeHead(result.status, { "Content-Type": "application/json" });
            res.end(JSON.stringify(result.body));
          } catch (err) {
            res.writeHead(500, { "Content-Type": "application/json" });
            res.end(JSON.stringify({ ok: false, error: String(err) }));
          }
        });
      } else if (req.method === "GET" && req.url === "/status") {
        const result = handlers.handleStatusRequest(this);
        res.writeHead(result.status, { "Content-Type": "application/json" });
        res.end(JSON.stringify(result.body));
      } else if (req.method === "GET" && req.url === "/targets") {
        const result = handlers.handleTargetsRequest(this);
        res.writeHead(result.status, { "Content-Type": "application/json" });
        res.end(JSON.stringify(result.body));
      } else if (req.url === "/mcp" && (req.method === "POST" || req.method === "GET" || req.method === "DELETE")) {
        // MCP Streamable HTTP endpoint — delegate to attached MCP server
        if (!this._mcpServer) {
          res.writeHead(503, { "Content-Type": "application/json" });
          res.end(JSON.stringify({ ok: false, error: "MCP server not initialized" }));
          return;
        }
        this._mcpServer.handleRequest(req, res).catch((err: unknown) => {
          console.error("[MCP] Request handling error:", err);
          if (!res.headersSent) {
            res.writeHead(500, { "Content-Type": "application/json" });
            res.end(JSON.stringify({ ok: false, error: "Internal MCP error" }));
          }
        });
      } else if (req.method === "GET" && req.url === "/trust") {
        const result = handlers.handleTrustRequest(this);
        res.writeHead(result.status, { "Content-Type": "application/json" });
        res.end(JSON.stringify(result.body));
      } else if (req.method === "GET" && req.url === "/mcp-config") {
        const agentName = this.config.agentName ?? "agent";
        const mcpSkillCount = this._mcpServer?.skillCount ?? 0;
        const result = handlers.handleMcpConfigRequest(agentName, port, mcpSkillCount);
        res.writeHead(result.status, { "Content-Type": "application/json" });
        res.end(JSON.stringify(result.body));
      } else {
        res.writeHead(404, { "Content-Type": "application/json" });
        res.end(JSON.stringify({ ok: false, error: "Not found. Use POST /send, POST /decision, POST /action, GET /status, GET /targets, or /mcp" }));
      }
    });

    this._httpServer.listen(port, "127.0.0.1", () => {
      this.emit("http-ready", port);
    });
  }

  private _stopHttpServer(): void {
    if (this._httpServer) {
      this._httpServer.close();
      this._httpServer = null;
    }
  }

  /**
   * Attach an MCP server instance to this channel.
   * The MCP server will be served at /mcp on the local HTTP server.
   */
  setMcpServer(mcpServer: import("./mcp-server.js").AgentVaultMcpServer): void {
    this._mcpServer = mcpServer;
  }

  /** The attached MCP server, if any. */
  get mcpServer(): import("./mcp-server.js").AgentVaultMcpServer | null {
    return this._mcpServer;
  }

  // --- Topic management ---

  /**
   * Create a new topic within the conversation group.
   * Requires the channel to be initialized with a groupId (from activation).
   */
  async createTopic(name: string): Promise<{ id: string; name: string; isDefault: boolean }> {
    if (!this._persisted?.groupId) {
      throw new Error("Channel not initialized or groupId unknown");
    }
    if (!this._deviceJwt) {
      throw new Error("Channel not authenticated");
    }

    const res = await fetch(`${this.config.apiUrl}/api/v1/topics`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${this._deviceJwt}`,
      },
      body: JSON.stringify({
        group_id: this._persisted.groupId,
        name,
        creator_device_id: this._persisted.deviceId,
      }),
    });

    if (!res.ok) {
      const detail = await res.text();
      throw new Error(`Create topic failed (${res.status}): ${detail}`);
    }

    const resp = await res.json();
    const topic = { id: resp.id, name: resp.name, isDefault: resp.is_default };

    if (!this._persisted.topics) {
      this._persisted.topics = [];
    }
    this._persisted.topics.push(topic);
    await this._persistState();

    return topic;
  }

  /**
   * List all topics in the conversation group.
   * Requires the channel to be initialized with a groupId (from activation).
   */
  async listTopics(): Promise<Array<{ id: string; name: string; isDefault: boolean }>> {
    if (!this._persisted?.groupId) {
      throw new Error("Channel not initialized or groupId unknown");
    }
    if (!this._deviceJwt) {
      throw new Error("Channel not authenticated");
    }

    const res = await fetch(
      `${this.config.apiUrl}/api/v1/topics?group_id=${encodeURIComponent(this._persisted.groupId)}`,
      {
        headers: {
          Authorization: `Bearer ${this._deviceJwt}`,
        },
      },
    );

    if (!res.ok) {
      const detail = await res.text();
      throw new Error(`List topics failed (${res.status}): ${detail}`);
    }

    const resp: any[] = await res.json();
    const topics = resp.map((t) => ({ id: t.id, name: t.name, isDefault: t.is_default }));

    this._persisted.topics = topics;
    await this._persistState();

    return topics;
  }

  // --- A2A Channel methods (agent-to-agent communication) ---

  /**
   * Request a new A2A channel with another agent by their hub address.
   * Returns the channel_id from the server response.
   */
  async requestA2AChannel(responderHubAddress: string): Promise<string> {
    if (!this._persisted?.hubAddress) {
      throw new Error("This agent does not have a hub address assigned");
    }
    // #499 A2 (deterministic backstop): never open an A2A channel to our OWN
    // hub address. When the owner addresses the agent by its own name, the
    // model can try to "find and channel" itself → a stuck dead room. Refuse at
    // the creation chokepoint so no self-A2A channel can ever be created; the
    // error surfaces to the model, which then replies to the owner directly.
    if (_isSelfHubAddress(this._persisted.hubAddress, responderHubAddress)) {
      throw new Error(
        "Refusing to open an A2A channel to this agent's own hub address " +
        "(self-reference). The owner is addressing you by your own name — " +
        "reply to them directly instead of opening a channel to yourself.",
      );
    }
    if (!this._deviceJwt) {
      throw new Error("Channel not authenticated");
    }

    const res = await fetch(`${this.config.apiUrl}/api/v1/a2a/channels/request`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${this._deviceJwt}`,
      },
      body: JSON.stringify({
        initiator_hub_address: this._persisted.hubAddress,
        responder_hub_address: responderHubAddress,
      }),
    });

    if (!res.ok) {
      const detail = await res.text();
      throw new Error(`A2A channel request failed (${res.status}): ${detail}`);
    }

    const resp = await res.json();
    const channelId = resp.channel_id || resp.id;

    // Store channel info in persisted state with participants array
    if (!this._persisted.a2aChannels) {
      this._persisted.a2aChannels = {};
    }
    this._persisted.a2aChannels[channelId] = {
      channelId,
      conversationId: resp.conversation_id || "",
      role: "creator",
      participants: [
        {
          hubId: "",
          hubAddress: responderHubAddress,
          status: "invited",
        },
      ],
    };
    await this._persistState();

    return channelId;
  }

  /**
   * Invite another agent to an existing A2A channel by their hub address.
   */
  async inviteToA2AChannel(channelId: string, hubAddress: string): Promise<void> {
    if (!this._deviceJwt) {
      throw new Error("Channel not authenticated");
    }

    const res = await fetch(`${this.config.apiUrl}/api/v1/a2a/channels/${channelId}/invite`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${this._deviceJwt}`,
      },
      body: JSON.stringify({
        hub_address: hubAddress,
      }),
    });

    if (!res.ok) {
      const detail = await res.text();
      throw new Error(`A2A channel invite failed (${res.status}): ${detail}`);
    }

    // Add participant to local state
    const entry = this._persisted?.a2aChannels?.[channelId];
    if (entry) {
      if (!entry.participants) entry.participants = [];
      // Avoid duplicates
      if (!entry.participants.some(p => p.hubAddress === hubAddress)) {
        entry.participants.push({ hubId: "", hubAddress, status: "invited" });
        await this._persistState();
      }
    }
  }

  /**
   * Send a message to another agent via an active A2A channel.
   * Looks up the A2A conversation by hub address and sends via WS.
   *
   * If the channel has an established E2E session, the message is encrypted
   * with the Double Ratchet. If the responder hasn't received the initiator's
   * first message yet (ratchet not activated), the message is queued locally
   * and flushed when the first inbound message arrives.
   *
   * Falls back to plaintext for channels without a session (legacy/pre-encryption).
   */
  async sendToAgent(hubAddress: string, text: string, opts?: { parentSpanId?: string }): Promise<void> {
    // #499 A2 (defense-in-depth): never send an A2A message to our own address.
    if (_isSelfHubAddress(this._persisted?.hubAddress ?? "", hubAddress)) {
      throw new Error(
        "Refusing to send an A2A message to this agent's own hub address " +
        "(self-reference) — reply to the owner directly.",
      );
    }
    if (!this._persisted?.a2aChannels) {
      // Try re-syncing before giving up
      try {
        await this.listA2AChannels();
      } catch { /* ignore */ }
      if (!this._persisted?.a2aChannels) {
        throw new Error("No A2A channels established");
      }
    }

    // Find the channel for this hub address by searching participants.
    const a2a = this._persisted.a2aChannels;
    let allMatches = Object.values(a2a).filter(
      (ch) => ch.participants?.some(p => p.hubAddress === hubAddress && (p.status === "active" || p.status === "invited")),
    );
    let channelEntry =
      allMatches.find((ch) => ch.conversationId) ?? allMatches[0];

    // On-demand re-sync from server if channel not found locally
    if (!channelEntry) {
      try {
        console.log(`[sendToAgent] Channel not found for ${hubAddress}, re-syncing from server...`);
        await this.listA2AChannels();
        allMatches = Object.values(this._persisted.a2aChannels ?? {}).filter(
          (ch) => ch.participants?.some(p => p.hubAddress === hubAddress && (p.status === "active" || p.status === "invited")),
        );
        channelEntry = allMatches.find((ch) => ch.conversationId) ?? allMatches[0];
      } catch (syncErr) {
        console.warn("[sendToAgent] A2A re-sync failed:", syncErr);
      }
    }

    if (!channelEntry) {
      throw new Error(`No A2A channel found for hub address: ${hubAddress}`);
    }
    if (!channelEntry.conversationId) {
      throw new Error(`A2A channel ${channelEntry.channelId} does not have a conversation yet (not activated)`);
    }

    if (this._state !== "ready" || !this._ws) {
      throw new Error("Channel is not connected");
    }

    // --- MLS path (preferred over Double Ratchet) ---
    if (channelEntry.mlsGroupId) {
      const mlsGroup = this._mlsGroups.get(`a2a:${channelEntry.channelId}`);
      if (mlsGroup?.isInitialized) {
        const plaintextBytes = new TextEncoder().encode(text);
        const ciphertext = await mlsGroup.encrypt(plaintextBytes);

        // Persist MLS state BEFORE sending
        await saveMlsState(
          this.config.dataDir,
          channelEntry.mlsGroupId,
          JSON.stringify(mlsGroup.exportState()),
        );

        this._ws!.send(JSON.stringify({
          event: "a2a_message_mls",
          data: {
            channel_id: channelEntry.channelId,
            group_id: channelEntry.mlsGroupId,
            epoch: Number(mlsGroup.epoch),
            payload: Buffer.from(ciphertext).toString("hex"),
            hub_address: this._persisted!.hubAddress,
          },
        }));
        return;
      }
    }

    // No MLS group — channel not yet initialized
    throw new Error(
      `A2A channel ${channelEntry.channelId.slice(0, 8)}... has no MLS group. ` +
      `MLS initialization must complete before messages can be sent.`,
    );
  }

  /**
   * Send a message to a specific A2A channel by channel ID.
   * Used for multi-agent channels where hub address routing is ambiguous.
   */
  async sendToA2AChannel(channelId: string, text: string): Promise<void> {
    if (!this._persisted?.a2aChannels) {
      try { await this.listA2AChannels(); } catch { /* ignore */ }
    }

    const entry = this._persisted?.a2aChannels?.[channelId];
    if (!entry) {
      throw new Error(`A2A channel ${channelId.slice(0, 8)} not found in persisted state`);
    }
    if (!entry.conversationId) {
      throw new Error(`A2A channel ${channelId.slice(0, 8)} has no conversation (not activated)`);
    }
    if (this._state !== "ready" || !this._ws) {
      throw new Error("Channel is not connected");
    }

    if (entry.mlsGroupId) {
      const mlsGroup = this._mlsGroups.get(`a2a:${channelId}`);
      if (mlsGroup?.isInitialized) {
        const plaintextBytes = new TextEncoder().encode(text);
        const ciphertext = await mlsGroup.encrypt(plaintextBytes);
        await saveMlsState(this.config.dataDir, entry.mlsGroupId, JSON.stringify(mlsGroup.exportState()));
        this._ws!.send(JSON.stringify({
          event: "a2a_message_mls",
          data: {
            channel_id: channelId,
            group_id: entry.mlsGroupId,
            epoch: Number(mlsGroup.epoch),
            payload: Buffer.from(ciphertext).toString("hex"),
            hub_address: this._persisted!.hubAddress,
          },
        }));
        return;
      }
    }

    throw new Error(`A2A channel ${channelId.slice(0, 8)} has no initialized MLS group`);
  }

  /**
   * Start the periodic A2A reconcile timer (30s), mirroring `_deliveryHeartbeat`.
   *
   * A2A join-triggers (`a2a_channel_approved` etc.) are delivered best-effort by
   * the backend's local-only `send_to_device`; if this agent is briefly
   * disconnected when a channel is created, the push is silently dropped and the
   * channel hangs until the next connect. Re-running `listA2AChannels()` on a
   * timer re-fires the idempotent member sync-fallback (publish KeyPackage +
   * request Welcome) so the channel self-heals while staying connected.
   */
  private _startA2aReconcileTimer(): void {
    if (this._a2aReconcileTimer) clearInterval(this._a2aReconcileTimer);
    this._a2aReconcileTimer = setInterval(() => {
      this.listA2AChannels().catch(() => {});
    }, 30_000);
  }

  /** Stop the periodic A2A reconcile timer (idempotent). */
  private _stopA2aReconcileTimer(): void {
    if (this._a2aReconcileTimer) {
      clearInterval(this._a2aReconcileTimer);
      this._a2aReconcileTimer = null;
    }
  }

  /**
   * List all A2A channels for this agent.
   * Fetches from the server and updates local persisted state.
   */
  async listA2AChannels(): Promise<A2AChannel[]> {
    if (!this._deviceJwt) {
      throw new Error("Channel not authenticated");
    }

    // Uses authedFetch so a 401 (e.g. JWT expired since last connect) triggers
    // exactly one reissue + retry per WS session. See _authedFetch for the
    // guard semantics.
    const res = await this._authedFetch(`${this.config.apiUrl}/api/v1/a2a/channels`);

    if (!res.ok) {
      const detail = await res.text();
      throw new Error(`List A2A channels failed (${res.status}): ${detail}`);
    }

    const resp: any[] = await res.json();
    const channels: A2AChannel[] = resp.map((ch) => ({
      channelId: ch.channel_id || ch.id,
      creator_hub_id: ch.creator_hub_id || "",
      conversationId: ch.conversation_id,
      status: ch.status,
      createdAt: ch.created_at,
      max_participants: ch.max_participants ?? 10,
      participant_count: ch.participant_count ?? (ch.participants?.length ?? 0),
      participants: (ch.participants ?? []).map((p: any) => ({
        id: p.id,
        hub_id: p.hub_id,
        hub_address: p.hub_address,
        agent_name: p.agent_name,
        tenant_id: p.tenant_id,
        device_id: p.device_id,
        role: p.role,
        status: p.status,
        observer_enabled: p.observer_enabled ?? false,
        joined_at: p.joined_at,
        left_at: p.left_at,
      })),
      topic: ch.topic,
    }));

    // Update persisted a2aChannels map with latest server data
    if (this._persisted) {
      if (!this._persisted.a2aChannels) {
        this._persisted.a2aChannels = {};
      }
      const myHub = this._persisted.hubAddress || "";
      const myHubId = this._persisted.hubId || "";
      console.log(`[listA2AChannels] myHub=${myHub}, channels=${channels.length}, raw=${JSON.stringify(channels.map(c => ({ id: c.channelId?.slice(0,8), status: c.status, participants: c.participant_count })))}`);
      for (const ch of channels) {
        if (ch.status === "active" || ch.status === "approved") {
          // Determine our role from participants list
          const myParticipant = ch.participants?.find(
            p => p.hub_id === myHubId || p.hub_address === myHub,
          );
          const role: "creator" | "member" = myParticipant?.role === "creator" ? "creator" : "member";

          // Build local participants array (peers only — exclude self)
          // Carries device_id through for the same reason as the
          // a2a_channel_approved handler — sync-fallback may end up
          // invoking _foundA2AGroup, which needs deviceId for Welcome
          // dispatch (backend ws.py mls_welcome reads target_device_id).
          const localParticipants = (ch.participants ?? [])
            .filter(p => p.hub_id !== myHubId && p.hub_address !== myHub)
            .map(p => ({
              hubId: p.hub_id || "",
              hubAddress: p.hub_address || "",
              status: p.status as "invited" | "active" | "left" | "removed",
              deviceId: p.device_id || null,
            }));

          const existing = this._persisted.a2aChannels[ch.channelId];
          if (existing) {
            // Merge: update participants/conversation but preserve MLS state
            existing.participants = localParticipants;
            existing.conversationId = ch.conversationId || existing.conversationId;
            if (!existing.role) {
              existing.role = role;
            }
          } else {
            // New channel discovered from server
            this._persisted.a2aChannels[ch.channelId] = {
              channelId: ch.channelId,
              conversationId: ch.conversationId || "",
              role,
              participants: localParticipants,
            };
            console.log(
              `[SecureChannel] Discovered A2A channel ${ch.channelId.slice(0, 8)}... role=${role} peers=${localParticipants.length}`,
            );
          }
        }
      }
      // Remove revoked channels from persisted state
      const activeIds = new Set(
        channels.filter((c) => c.status === "active" || c.status === "approved").map((c) => c.channelId),
      );
      for (const id of Object.keys(this._persisted.a2aChannels)) {
        if (!activeIds.has(id)) {
          delete this._persisted.a2aChannels[id];
        }
      }
      await this._persistState();

      // Sync fallback: if this agent is the creator of an active channel
      // but hasn't founded the MLS group yet, do it now.
      // This handles the case where the creator was offline when a2a_channel_approved was sent.
      for (const [chId, chState] of Object.entries(this._persisted?.a2aChannels ?? {})) {
        if (
          chState.role === "creator" &&
          !chState.mlsGroupId &&
          chState.participants.some(p => p.status === "active")
        ) {
          const activeParticipants = chState.participants.filter(p => p.status === "active");
          if (activeParticipants.length >= 1) {
            console.log(`[AgentVault] Sync fallback: founding MLS group for channel ${chId.slice(0, 8)}`);
            try {
              await this._foundA2AGroup(chId, chState.conversationId, activeParticipants);
            } catch (err) {
              console.warn(`[AgentVault] Sync fallback MLS founding failed for ${chId.slice(0, 8)}:`, err);
            }
          }
        }

        // Sync fallback for members: if we're a member without a usable MLS group,
        // look up the group, publish a KeyPackage, and request a Welcome.
        // This handles: (a) member missed a2a_channel_approved WS event,
        // (b) mlsGroupId was stored but Welcome was never processed (e.g. distribute 422).
        const memberMlsGroup = chState.mlsGroupId ? this._mlsGroups.get(`a2a:${chId}`) : undefined;
        if (
          chState.role === "member" &&
          !memberMlsGroup?.isInitialized
        ) {
          // Acquire channel-level lock — skip if another instance is handling this channel
          if (!await acquireA2ASyncLock(this.config.dataDir, chId)) {
            console.log(`[SecureChannel] A2A ${chId.slice(0, 8)} sync: skipping — another instance holds the lock`);
            continue;
          }
          try {
            // Look up the MLS group for this channel
            let groupId = chState.mlsGroupId;
            let memberIds: string[] = [];
            if (!groupId) {
              const groupRes = await fetch(
                `${this.config.apiUrl}/api/v1/mls/a2a/${chId}/group`,
                { headers: { Authorization: `Bearer ${this._deviceJwt}` } },
              );
              if (!groupRes.ok) {
                // Group not yet created by creator — nothing to join yet
                await releaseA2ASyncLock(this.config.dataDir, chId);
                continue;
              }
              const groupData = await groupRes.json();
              groupId = groupData.group_id;
              if (!groupId) {
                await releaseA2ASyncLock(this.config.dataDir, chId);
                continue;
              }
              memberIds = groupData.member_device_ids || [];

              // Store the groupId so delivery pull can restore MLS state later
              chState.mlsGroupId = groupId;
              await this._persistState();
            } else {
              // Already have groupId — fetch current membership to check
              try {
                const groupRes = await fetch(
                  `${this.config.apiUrl}/api/v1/mls/groups/${groupId}`,
                  { headers: { Authorization: `Bearer ${this._deviceJwt}` } },
                );
                if (groupRes.ok) {
                  const groupData = await groupRes.json();
                  memberIds = groupData.member_device_ids || [];
                }
              } catch { /* proceed with empty memberIds */ }
            }

            // Check if we're already in the MLS group on the server
            if (memberIds.includes(this._persisted!.deviceId)) {
              console.log(`[SecureChannel] A2A ${chId.slice(0, 8)} member in server membership but no local MLS state — requesting Welcome`);
              // Fall through to request Welcome (we lost local state)
            }

            // Defense-in-depth: check KP-level lock (may exist from prior partial run)
            if (await hasPendingWelcome(this.config.dataDir, groupId!)) {
              console.log(`[SecureChannel] A2A ${chId.slice(0, 8)} sync fallback: skipping — Welcome already pending for group=${groupId!.slice(0, 8)}`);
              // Do NOT release channel lock — pending Welcome still needs to resolve
              continue;
            }

            // Publish a fresh KeyPackage and persist to disk (shared across agent instances)
            const mgr = new MLSGroupManager();
            const identity = new TextEncoder().encode(this._persisted!.deviceId);
            const kp = await mgr.generateKeyPackage(identity);
            this._recordPublishedKp(kp);
            await savePendingKpBundle(this.config.dataDir, groupId!, kp, MLSGroupManager.serializeKeyPackage);
            const kpBytes = MLSGroupManager.serializeKeyPackage(kp.publicPackage);
            await fetch(`${this.config.apiUrl}/api/v1/mls/key-packages`, {
              method: "POST",
              headers: {
                Authorization: `Bearer ${this._deviceJwt}`,
                "Content-Type": "application/json",
              },
              body: JSON.stringify({
                key_package: Buffer.from(kpBytes).toString("hex"),
                device_id: this._persisted!.deviceId,
              }),
            });

            // Request Welcome from the creator
            await fetch(`${this.config.apiUrl}/api/v1/mls/groups/${groupId}/request-welcome`, {
              method: "POST",
              headers: {
                Authorization: `Bearer ${this._deviceJwt}`,
                "Content-Type": "application/json",
              },
              body: JSON.stringify({ device_id: this._persisted!.deviceId }),
            });
            console.log(
              `[SecureChannel] A2A ${chId.slice(0, 8)} member sync fallback: published KeyPackage + requested Welcome (group=${groupId!.slice(0, 8)})`,
            );
            // Lock is NOT released here — held until Welcome is processed in _handleMlsWelcome
          } catch (err) {
            // Release lock on error so next sync cycle can retry
            await releaseA2ASyncLock(this.config.dataDir, chId).catch(() => {});
            console.warn(`[AgentVault] Sync fallback member MLS join failed for ${chId.slice(0, 8)}:`, err);
          }
        }
      }
    }

    return channels;
  }

  // --- Internal lifecycle ---

  private async _enroll(): Promise<void> {
    this._setState("enrolling");

    try {
      const identity = await generateIdentityKeypair();
      const ephemeral = await generateEphemeralKeypair();
      const fingerprint = computeFingerprint(identity.publicKey);
      const proof = createProofOfPossession(
        identity.privateKey,
        identity.publicKey,
      );

      const result = await enrollDevice(
        this.config.apiUrl,
        this.config.inviteToken,
        bytesToHex(identity.publicKey),
        bytesToHex(ephemeral.publicKey),
        bytesToHex(proof),
        this.config.platform,
      );

      this._deviceId = result.device_id;
      this._fingerprint = result.fingerprint;

      // Store keypairs temporarily for activation (not persisted yet -- no JWT)
      this._persisted = {
        deviceId: result.device_id,
        deviceJwt: "", // set after activation
        primaryConversationId: "", // set after activation
        sessions: {}, // populated after activation
        identityKeypair: {
          publicKey: bytesToHex(identity.publicKey),
          privateKey: bytesToHex(identity.privateKey),
        },
        ephemeralKeypair: {
          publicKey: bytesToHex(ephemeral.publicKey),
          privateKey: bytesToHex(ephemeral.privateKey),
        },
        fingerprint: result.fingerprint,
        messageHistory: [],
      };

      this._poll();
    } catch (err) {
      this._handleError(err as Error);
    }
  }

  private _poll(): void {
    if (this._stopped) return;
    this._setState("polling");

    const doPoll = async () => {
      if (this._stopped) return;
      try {
        const status = await pollDeviceStatus(
          this.config.apiUrl,
          this._deviceId!,
        );

        if ((status as any).rateLimited) {
          // Server rate-limited this poll — back off 2x before retrying
          this._pollTimer = setTimeout(doPoll, POLL_INTERVAL_MS * 2);
          return;
        }

        if (status.status === "APPROVED") {
          await this._activate();
          return;
        }

        if (status.status === "REVOKED") {
          this._handleError(new Error("Device was revoked"));
          return;
        }

        // Still PENDING -- poll again
        this._pollTimer = setTimeout(doPoll, POLL_INTERVAL_MS);
      } catch (err) {
        this._handleError(err as Error);
      }
    };

    this._pollTimer = setTimeout(doPoll, POLL_INTERVAL_MS);
  }

  private async _activate(): Promise<void> {
    this._setState("activating");

    try {
      const result = await activateDevice(
        this.config.apiUrl,
        this._deviceId!,
      );

      // Support both old (single conversation_id) and new (conversations array) response
      const conversations: Array<{
        conversation_id: string;
        owner_device_id: string;
        is_primary: boolean;
        owner_identity_public_key?: string;
        owner_ephemeral_public_key?: string;
        group_id?: string;
        default_topic_id?: string;
      }> = (result as any).conversations || [
        {
          conversation_id: result.conversation_id,
          owner_device_id: "",
          is_primary: true,
        },
      ];

      const primary =
        conversations.find((c) => c.is_primary) || conversations[0];
      this._primaryConversationId = primary.conversation_id;
      this._deviceJwt = result.device_jwt;

      const identity = this._persisted!.identityKeypair;
      const ephemeral = this._persisted!.ephemeralKeypair;
      const sessions: Record<
        string,
        { ownerDeviceId: string; ratchetState: ReturnType<DoubleRatchet["serialize"]>; epoch?: number }
      > = {};

      // Initialize X3DH + ratchet for EVERY conversation (not just primary)
      for (const conv of conversations) {
        // Use per-conversation owner keys if available, fallback to primary keys
        const ownerIdentityKey =
          conv.owner_identity_public_key || result.owner_identity_public_key;
        const ownerEphemeralKey =
          conv.owner_ephemeral_public_key ||
          result.owner_ephemeral_public_key ||
          ownerIdentityKey;

        const sharedSecret = performX3DH({
          myIdentityPrivate: hexToBytes(identity.privateKey),
          myEphemeralPrivate: hexToBytes(ephemeral.privateKey),
          theirIdentityPublic: hexToBytes(ownerIdentityKey),
          theirEphemeralPublic: hexToBytes(ownerEphemeralKey),
          isInitiator: false,
        });

        const ratchet = DoubleRatchet.initReceiver(sharedSecret, {
          publicKey: hexToBytes(identity.publicKey),
          privateKey: hexToBytes(identity.privateKey),
          keyType: "ed25519",
        }, hexToBytes(ownerIdentityKey));

        this._sessions.set(conv.conversation_id, {
          ownerDeviceId: conv.owner_device_id,
          ratchet,
          activated: false, // Wait for owner's first message before sending to this session
          epoch: 1,
        });

        sessions[conv.conversation_id] = {
          ownerDeviceId: conv.owner_device_id,
          ratchetState: ratchet.serialize(),
          epoch: 1,
        };

        console.log(
          `[SecureChannel] Session initialized for conv ${conv.conversation_id.slice(0, 8)}... (owner ${conv.owner_device_id.slice(0, 8)}..., primary=${conv.is_primary})`,
        );
      }

      // Persist full state (preserve existing messageHistory)
      this._persisted = {
        ...this._persisted!,
        deviceJwt: result.device_jwt,
        primaryConversationId: primary.conversation_id,
        sessions,
        messageHistory: this._persisted!.messageHistory ?? [],
      };

      // Store group_id and default_topic_id for topic API calls
      if (conversations.length > 0) {
        const firstConv = conversations[0];
        if (firstConv.group_id) {
          this._persisted.groupId = firstConv.group_id;
        }
        if (firstConv.default_topic_id) {
          this._persisted.defaultTopicId = firstConv.default_topic_id;
        }
      }

      // Build conversation -> group_id map for shared MLS groups (+ persist for reconnect)
      this._sessionGroupIds = new Map<string, string>();
      for (const conv of conversations) {
        if (conv.group_id) {
          this._sessionGroupIds.set(conv.conversation_id, conv.group_id);
        }
      }
      this._persisted.conversationGroupIds = Object.fromEntries(this._sessionGroupIds);

      await saveState(this.config.dataDir, this._persisted);

      // Register webhook if configured
      if (this.config.webhookUrl) {
        try {
          const webhookResp = await fetch(
            `${this.config.apiUrl}/api/v1/devices/self/webhook`,
            {
              method: "PATCH",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${this._deviceJwt}`,
              },
              body: JSON.stringify({ webhook_url: this.config.webhookUrl }),
            }
          );
          if (webhookResp.ok) {
            const webhookData = await webhookResp.json();
            console.log(
              `[SecureChannel] Webhook registered: ${this.config.webhookUrl} (secret: ${webhookData.webhook_secret?.slice(0, 8)}...)`,
            );
            this.emit("webhook_registered", {
              url: this.config.webhookUrl,
              secret: webhookData.webhook_secret,
            });
          } else {
            console.warn(`[SecureChannel] Webhook registration failed: ${webhookResp.status}`);
          }
        } catch (err) {
          console.warn(`[SecureChannel] Webhook registration error: ${err}`);
        }
      }

      this._connect();
    } catch (err) {
      this._handleError(err as Error);
    }
  }

  private async _connect(): Promise<void> {
    if (this._stopped) return;

    // Each WS session gets a fresh reactive-reissue attempt. We reset here so
    // a reconnect after a terminal reissue failure can still try once on the
    // new session (in case the server-side credential state changed).
    this._reissueAttempted = false;
    // Same reset for the auth-failure flag used by the rapid-disconnect
    // classifier — a fresh connect attempt clears prior auth-failed signal.
    this._authFailedThisSession = false;

    // Clear reconnect timer — we're connecting now, so any pending reconnect is superseded.
    if (this._reconnectTimer) {
      clearTimeout(this._reconnectTimer);
      this._reconnectTimer = null;
    }

    // Clean up existing WebSocket to prevent reconnect cascade:
    // The server disconnects old WS when same device reconnects, which fires
    // the old close handler → _scheduleReconnect() → new _connect() → cascade.
    if (this._ws) {
      this._ws.removeAllListeners();
      try { this._ws.close(); } catch { /* already closed */ }
      this._ws = null;
    }

    this._setState("connecting");

    // Proactively reissue the device JWT if it's within 30 days of expiry.
    // Runs before the WS URL is built so the reissued token (if any) is used
    // for this connect.
    await this._maybeReissueDeviceJwt();

    const wsUrl = this.config.apiUrl.replace(/^http/, "ws");
    const url = `${wsUrl}/api/v1/ws?token=${encodeURIComponent(this._deviceJwt!)}&device_id=${this._deviceId}`;

    const ws = new WebSocket(url);
    this._ws = ws;

    ws.on("open", async () => {
      try {
        this._reconnectAttempt = 0;
        this._lastWsOpenTime = Date.now();
        this._startPing(ws);
        this._startWakeDetector();
        this._startPendingPoll();

        // Sync missed messages before declaring ready
        await this._syncMissedMessages();
        // Pull from DR delivery queue — catches messages missed while offline (Signal-model)
        await this._pullDrDeliveryQueue();
        await this._flushOutboundQueue();

        this._setState("ready");

        // Initialize client-side policy scanning if enabled
        if (this.config.enableScanning) {
          this._scanEngine = new ScanEngine();
          await this._fetchScanRules();
        }

        // Sync A2A channels from server (handles missed WS events after restart)
        try {
          console.log(`[SecureChannel] Syncing A2A channels (hubAddress=${this._persisted?.hubAddress ?? "NOT SET"})`);
          await this.listA2AChannels();
          const a2aCount = Object.keys(this._persisted?.a2aChannels ?? {}).length;
          console.log(`[SecureChannel] A2A sync complete: ${a2aCount} channels`);
          this.emit("a2a_channels_synced", { count: a2aCount });

        } catch (err) {
          console.warn("[SecureChannel] A2A channel sync failed (non-fatal):", err);
        }

        // Initialize telemetry reporter for agent-side metrics
        // (hubId may not be set yet — hub_identity_sync event will late-init if needed)
        if (!this._telemetryReporter && this._persisted?.deviceJwt && this._persisted?.hubId) {
          this._telemetryReporter = new TelemetryReporter({
            apiBase: this.config.apiUrl,
            hubId: this._persisted.hubId,
            authHeader: `Bearer ${this._persisted.deviceJwt}`,
            agentName: this.config.agentName ?? this._persisted.hubAddress,
            agentVersion: this.config.agentVersion ?? "0.0.0",
            // __AV_VERSION__ is injected by esbuild from package.json at build time.
            // Falls back to "0.0.0-dev" in non-bundled contexts (tests).
            pluginVersion: typeof __AV_VERSION__ !== "undefined" ? __AV_VERSION__ : "0.0.0-dev",
          });
          this._telemetryReporter.startAutoFlush(30_000);
        }

        // Start Trust Gate token lifecycle
        this.startTrustTokenRefresh();

        // Sync A2A channels FIRST — members publish their own KP during sync fallback,
        // so the on-connect KP must come after to avoid overwriting it on the server.
        // (BUG-20260408-001: on-connect KP overwrote sync fallback KP, causing Welcome mismatch)
        await this.listA2AChannels().catch((err: unknown) => {
          console.warn("[SecureChannel] A2A channel sync on connect failed:", err);
        });

        // Publish a POOL of MLS KeyPackages so the agent can be added to many
        // rooms without starving — the backend consumes one KP per Welcome
        // fulfillment. Tops up to _KP_POOL_TARGET (the backend caps unconsumed
        // KPs to match). A reconnect that still holds its pool republishes
        // little or nothing.
        await this._ensureKpPoolPublished();

        // MLS delivery queue: pull pending messages on connect + 30s heartbeat
        this._pullDeliveryQueue().catch((err: unknown) => {
          console.warn("[SecureChannel] Initial delivery pull failed:", err);
        });
        if (this._deliveryHeartbeat) clearInterval(this._deliveryHeartbeat);
        this._deliveryHeartbeat = setInterval(() => {
          this._pullDeliveryQueue().catch(() => {});
        }, 30_000);
        // Periodic A2A reconcile: self-heal channels whose real-time join-trigger
        // was missed while briefly disconnected (best-effort backend push).
        this._startA2aReconcileTimer();

        // MLS sync: request catch-up for 1:1 conversations
        for (const [convId, entry] of Object.entries(this._persisted?.mlsConversations ?? {})) {
          if (entry.mlsGroupId) {
            const mlsGroup = this._mlsGroups.get(`conv:${convId}`);
            ws.send(JSON.stringify({
              event: "mls_sync",
              data: {
                group_id: entry.mlsGroupId,
                last_epoch: mlsGroup ? Number(mlsGroup.epoch) : 0,
              },
            }));
          }
        }

        // MLS sync: request catch-up for each room with MLS group
        for (const [roomId, room] of Object.entries(this._persisted?.rooms ?? {})) {
          const mlsGroupId = (room as RoomState).mlsGroupId;
          if (mlsGroupId) {
            const mlsGroup = this._mlsGroups.get(roomId);
            const syncData: Record<string, unknown> = {
              group_id: mlsGroupId,
              last_epoch: mlsGroup ? Number(mlsGroup.epoch) : 0,
            };
            // Send last received timestamp for same-epoch catch-up
            const lastTs = (room as RoomState).lastMlsMessageTs;
            if (lastTs) {
              syncData.since_ts = lastTs;
            }
            ws.send(JSON.stringify({ event: "mls_sync", data: syncData }));
          }
        }

        // MLS sync: A2A channels
        for (const [channelId, entry] of Object.entries(this._persisted?.a2aChannels ?? {})) {
          if (entry.mlsGroupId) {
            const mlsGroup = this._mlsGroups.get(`a2a:${channelId}`);
            ws.send(JSON.stringify({
              event: "mls_sync",
              data: {
                group_id: entry.mlsGroupId,
                last_epoch: mlsGroup ? Number(mlsGroup.epoch) : 0,
              },
            }));
          }
        }

        this.emit("ready");
      } catch (openErr) {
        console.error("[SecureChannel] Error in WS open handler:", openErr);
        this.emit("error", openErr);
      }
    });

    ws.on("message", async (raw: WebSocket.Data) => {
      this._lastServerMessage = Date.now();
      this._lastWakeTick = Date.now();
      try {
        const data = JSON.parse(raw.toString());

        // Diagnostic: log all non-ping events to trace A2A delivery
        if (data.event && data.event !== "ping" && data.event !== "typing") {
          console.log(`[SecureChannel] WS event: ${data.event} conv=${(data.conversation_id || data.data?.conversation_id || "").toString().slice(0, 8)}`);
        }

        if (data.event === "ping") {
          ws.send(JSON.stringify({ event: "pong" }));
          return;
        }

        if (data.event === "device_revoked") {
          await this._handleDeviceRevoked();
          return;
        }

        if (data.event === "device_linked") {
          await this._handleDeviceLinked(data.data);
          return;
        }

        if (data.event === "resync_request") {
          await this._handleResyncRequest(data.data);
          return;
        }

        if (data.event === "resync_ack") {
          // Agent doesn't need to handle its own ack echo
          return;
        }

        if (data.event === "message") {
          // Dedup: skip messages already processed (prevents ratchet corruption
          // from double-delivery via direct WS forward + Redis pub/sub)
          const inMsgId = data.data?.message_id;
          if (inMsgId && this._seenMessageIds.has(inMsgId)) {
            return;
          }
          if (inMsgId) {
            this._seenMessageIds.add(inMsgId);
            if (this._seenMessageIds.size > SecureChannel.SEEN_MSG_MAX) {
              const first = this._seenMessageIds.values().next().value;
              if (first) this._seenMessageIds.delete(first);
            }
          }
          try {
            await this._handleIncomingMessage(data.data);
          } catch (msgErr) {
            console.error(
              `[SecureChannel] Message handler failed for conv ${data.data?.conversation_id?.slice(0, 8) ?? "?"}...:`,
              msgErr,
            );
          }
        }

        // --- MLS 1:1 message ---
        if (data.event === "message_mls") {
          const mlsMsgId = data.data?.message_id;
          if (mlsMsgId && this._seenMessageIds.has(mlsMsgId)) {
            return;
          }
          if (mlsMsgId) {
            this._seenMessageIds.add(mlsMsgId);
            if (this._seenMessageIds.size > SecureChannel.SEEN_MSG_MAX) {
              const first = this._seenMessageIds.values().next().value;
              if (first) this._seenMessageIds.delete(first);
            }
          }
          try {
            await this._handleMessageMLS(data.data);
          } catch (mlsMsgErr) {
            console.error(`[SecureChannel] MLS message handler failed:`, mlsMsgErr);
          }
        }

        if (data.event === "room_joined") {
          const d = data.data;
          this.joinRoom({
            roomId: d.room_id,
            name: d.room_name ?? d.name ?? "Room",
            members: (d.members || []).map((m: any) => ({
              deviceId: m.device_id,
              entityType: m.entity_type,
              displayName: m.display_name,
              identityPublicKey: m.identity_public_key,
              ephemeralPublicKey: m.ephemeral_public_key,
            })),
            conversations: (d.conversations || []).map((c: any) => ({
              id: c.id,
              participantA: c.participant_a,
              participantB: c.participant_b,
            })),
            forceRekey: d.force_rekey === true,
          }).catch((err) => this.emit("error", err));
        }

        if (data.event === "room_message_mls") {
          const mlsMsgId = data.data?.message_id;
          if (mlsMsgId && this._seenMessageIds.has(mlsMsgId)) {
            return;
          }
          if (mlsMsgId) {
            this._seenMessageIds.add(mlsMsgId);
            if (this._seenMessageIds.size > SecureChannel.SEEN_MSG_MAX) {
              const first = this._seenMessageIds.values().next().value;
              if (first) this._seenMessageIds.delete(first);
            }
          }
          try {
            await this._handleRoomMessageMLS(data.data || data);
          } catch (mlsRmErr) {
            console.error("[SecureChannel] MLS room message handler failed:", mlsRmErr);
          }
        }

        if (data.event === "mls_commit") {
          try {
            await this._handleMlsCommit(data.data || data);
          } catch (mlsCommitErr) {
            console.error("[SecureChannel] MLS commit handler failed:", mlsCommitErr);
          }
        }

        if (data.event === "mls_welcome") {
          try {
            await this._handleMlsWelcome(data.data || data);
          } catch (mlsWelcomeErr) {
            console.error("[SecureChannel] MLS welcome handler failed:", mlsWelcomeErr);
          }
        }

        if (data.event === "mls_delivery") {
          // Lightweight ping — pull actual messages from delivery queue
          this._pullDeliveryQueue().catch((err: unknown) => {
            console.warn("[SecureChannel] Delivery pull on ping failed:", err);
          });
        }

        if (data.event === "dr_delivery") {
          // Signal-model DR delivery ping — pull DR messages from queue
          this._pullDrDeliveryQueue().catch((err: unknown) => {
            console.warn("[SecureChannel] DR delivery pull on ping failed:", err);
          });
        }

        if (data.event === "mls_welcome_requested") {
          // Someone (observer or rejoining member) needs a Welcome — trigger delivery pull
          // which includes A2A Welcome fulfillment
          console.log(`[SecureChannel] mls_welcome_requested for group ${(data.data?.group_id || "").slice(0, 8)} — pulling deliveries`);
          this._pullDeliveryQueue().catch((err: unknown) => {
            console.warn("[SecureChannel] Delivery pull on welcome request failed:", err);
          });
        }

        if (data.event === "mls_sync_response") {
          try {
            await this._handleMlsSyncResponse(data.data || data);
          } catch (mlsSyncErr) {
            console.error("[SecureChannel] MLS sync response handler failed:", mlsSyncErr);
          }
        }

        if (data.event === "room_participant_added") {
          const p = data.data;
          this.emit("room_participant_added", {
            roomId: p.room_id,
            participant: {
              deviceId: p.participant?.device_id ?? p.device_id,
              participantType: p.participant?.participant_type ?? p.participant_type ?? "human",
              displayName: p.participant?.display_name ?? p.display_name,
            },
          });
        }

        if (data.event === "room_participant_removed") {
          const p = data.data;
          this.emit("room_participant_removed", {
            roomId: p.room_id,
            participant: {
              deviceId: p.participant?.device_id ?? p.device_id,
              participantType: p.participant?.participant_type ?? p.participant_type ?? "human",
              displayName: p.participant?.display_name ?? p.display_name,
            },
          });
        }

        if (data.event === "room_hushed") {
          // #392 room hush (advisory). Forward to consumers (e.g. the native
          // bridge cooperatively holds room_say until hushed_until passes).
          this.emit("room_hushed", {
            roomId: data.data?.room_id,
            hushedUntil: data.data?.hushed_until ?? null,
          });
        }

        if (data.event === "room_advisory") {
          // #392 one-time no-secrets reminder (advisory).
          this.emit("room_advisory", {
            roomId: data.data?.room_id,
            kind: data.data?.kind,
            message: data.data?.message,
          });
        }

        if (data.event === "arm_requested") {
          // Slice 2 Plan C — owner requested worker arming for a room.
          this.emit("arm_requested", {
            roomId: data.data?.room_id,
            requestId: data.data?.request_id,
            workspaceLabel: data.data?.workspace_label ?? null,
          });
        }

        if (data.event === "disarm") {
          // Slice 2 Plan C — owner (or the backend) disarmed a room.
          this.emit("disarm", { roomId: data.data?.room_id });
        }

        if (data.event === "arming_snapshot") {
          // Slice 2 Plan C — full re-sync of currently-armed rooms (e.g. on reconnect).
          // Task 3: also pass through the device-level `work_allowed` grant
          // (Task 2's unified push) so the bridge can derive its fail-closed
          // `workAllowed()` getter — coerced to a strict boolean so a missing
          // field never accidentally reads truthy.
          this.emit("arming_snapshot", {
            workAllowed: data.data?.work_allowed === true,
            roomIds: data.data?.room_ids ?? [],
          });
        }

        if (data.event === "policy_blocked") {
          this.emit("policy_blocked", data.data);
        }

        if (data.event === "message_held") {
          this.emit("message_held", data.data);
        }

        if (data.event === "policy_rejected") {
          this.emit("policy_rejected", data.data);
        }

        if (data.event === "scan_rules_updated") {
          await this._fetchScanRules();
          return;
        }

        // MCP proxy: backend relays an MCP request from a remote client
        if (data.event === "mcp_request") {
          const requestId = data.data?.request_id;
          const mcpPayload = data.data?.mcp_payload;
          if (requestId && mcpPayload && this._mcpServer) {
            try {
              // Create a synthetic HTTP request/response to forward to the MCP server
              const { createMcpProxyRequest } = await import("./mcp-proxy-helpers.js");
              const mcpResponse = await createMcpProxyRequest(this._mcpServer, mcpPayload);
              // Send response back via WS
              ws.send(JSON.stringify({
                event: "mcp_response",
                data: { request_id: requestId, mcp_payload: mcpResponse },
              }));
            } catch (err) {
              ws.send(JSON.stringify({
                event: "mcp_response",
                data: {
                  request_id: requestId,
                  mcp_payload: {
                    jsonrpc: "2.0",
                    id: mcpPayload?.id ?? null,
                    error: { code: -32603, message: String(err) },
                  },
                },
              }));
            }
          }
          return;
        }

        // hub_identity_sync fires on every WS connect — ensures hubId + agentRole are set
        if (data.event === "hub_identity_sync") {
          if (this._persisted && data.data?.hub_id) {
            const changed = this._persisted.hubId !== data.data.hub_id
              || this._persisted.agentRole !== (data.data.agent_role ?? "peer");
            this._persisted.hubAddress = data.data.hub_address;
            this._persisted.hubId = data.data.hub_id;
            this._persisted.agentHubId = data.data.hub_id;
            this._persisted.agentRole = (data.data.agent_role as "lead" | "peer") ?? "peer";
            if (changed) this._persistState();
            // #499 A1: (re)resolve our own display_name on every connect for the
            // DM identity preamble — picks up a rename and retries a prior
            // failed fetch (best-effort, fire-and-forget — never blocks connect).
            void this._resolveOwnDisplayName();

            // Late-init telemetry reporter if it wasn't created during open handler
            if (!this._telemetryReporter && this._persisted.deviceJwt && this._persisted.hubId) {
              this._telemetryReporter = new TelemetryReporter({
                apiBase: this.config.apiUrl,
                hubId: this._persisted.hubId,
                authHeader: `Bearer ${this._persisted.deviceJwt}`,
                agentName: this.config.agentName ?? this._persisted.hubAddress,
                agentVersion: this.config.agentVersion ?? "0.0.0",
                // __AV_VERSION__ is injected by esbuild from package.json at build time.
                // Falls back to "0.0.0-dev" in non-bundled contexts (tests).
                pluginVersion: typeof __AV_VERSION__ !== "undefined" ? __AV_VERSION__ : "0.0.0-dev",
              });
              this._telemetryReporter.startAutoFlush(30_000);
            }
          }
        }

        if (data.event === "hub_identity_assigned") {
          if (this._persisted) {
            this._persisted.hubAddress = data.data.hub_address;
            this._persisted.hubId = data.data.hub_id;
            this._persistState();
          }
          this.emit("hub_identity_assigned", data.data);
        }
        if (data.event === "hub_identity_role_changed") {
          if (this._persisted && data.data?.agent_role) {
            this._persisted.agentRole = data.data.agent_role as "lead" | "peer";
            this._persistState();
            console.log(`[SecureChannel] Agent role changed to: ${data.data.agent_role}`);
          }
          this.emit("hub_identity_role_changed", data.data);
        }
        // --- Shadow Mode WS events ---

        if (data.event === "shadow_config_sync") {
          if (this._persisted && data.data?.skills) {
            this._persisted.shadowSkills = data.data.skills;
            this._persistState();
            console.log(`[SecureChannel] Shadow config synced: ${Object.keys(data.data.skills).length} skill(s)`);
          }
        }

        if (data.event === "shadow_session_created") {
          if (this._persisted && data.data?.skill_name) {
            if (!this._persisted.shadowSkills) this._persisted.shadowSkills = {};
            this._persisted.shadowSkills[data.data.skill_name] = {
              sessionId: data.data.session_id,
              autonomyLevel: data.data.autonomy_level,
              decisionClass: data.data.decision_class,
            };
            this._persistState();
            console.log(`[SecureChannel] Shadow session created for skill: ${data.data.skill_name}`);
          }
          this.emit("shadow_session_created", data.data);
        }

        if (data.event === "shadow_session_graduated") {
          if (this._persisted?.shadowSkills && data.data?.skill_name) {
            if (data.data.autonomy_level === "autonomous") {
              delete this._persisted.shadowSkills[data.data.skill_name];
            } else {
              const entry = this._persisted.shadowSkills[data.data.skill_name];
              if (entry) entry.autonomyLevel = data.data.autonomy_level;
            }
            this._persistState();
            console.log(`[SecureChannel] Shadow session graduated: ${data.data.skill_name} → ${data.data.autonomy_level}`);
          }
          this.emit("shadow_session_graduated", data.data);
        }

        if (data.event === "shadow_session_deleted") {
          if (this._persisted?.shadowSkills && data.data?.skill_name) {
            delete this._persisted.shadowSkills[data.data.skill_name];
            this._persistState();
          }
          this.emit("shadow_session_deleted", data.data);
        }

        if (data.event === "hub_identity_removed") {
          if (this._persisted) {
            delete this._persisted.hubAddress;
            delete this._persisted.hubId;
            this._persistState();
          }
          this.emit("hub_identity_removed", data.data);
        }

        // --- A2A Channel WS events ---

        if (data.event === "a2a_channel_approved") {
          const channelData = data.data || data;
          const channelId = channelData.channel_id;
          const convId = channelData.conversation_id;

          // Determine role from participants or event data
          const myHub = this._persisted?.hubAddress || "";
          const myHubId = this._persisted?.hubId || "";
          const eventParticipants: any[] = channelData.participants ?? [];
          const myParticipant = eventParticipants.find(
            (p: any) => p.hub_id === myHubId || p.hub_address === myHub,
          );
          const existingEntry = this._persisted?.a2aChannels?.[channelId];
          const role: "creator" | "member" =
            existingEntry?.role ||
            myParticipant?.role ||
            (channelData.creator_hub_id === myHubId ? "creator" : "member");

          // Build participants list (peers only).
          //
          // Carries device_id through so _foundA2AGroup can target Welcomes
          // by device (backend ws.py mls_welcome handler reads target_device_id,
          // not target_hub_id — silent drop otherwise; see action_items
          // "CRITICAL: OC plugin emits mls_welcome with target_hub_id+welcome").
          const localParticipants = eventParticipants
            .filter((p: any) => p.hub_id !== myHubId && p.hub_address !== myHub)
            .map((p: any) => ({
              hubId: p.hub_id || "",
              hubAddress: p.hub_address || "",
              status: (p.status || "active") as "invited" | "active" | "left" | "removed",
              deviceId: p.device_id || null,
            }));

          // Store in persisted state
          if (this._persisted && convId) {
            if (!this._persisted.a2aChannels) this._persisted.a2aChannels = {};

            const existingNow = this._persisted.a2aChannels[channelId];

            // MERGE instead of overwrite — preserve existing MLS state
            this._persisted.a2aChannels[channelId] = {
              ...existingNow,
              channelId,
              conversationId: convId,
              role: existingNow?.role || role,
              topic: channelData.topic || existingNow?.topic || undefined,
              participants: localParticipants.length > 0
                ? localParticipants
                : existingNow?.participants ?? [],
            };

            await this._persistState();
          }
          this.emit("a2a_channel_approved", channelData);

          // --- MLS founding for creator agent ---
          if (this._persisted && convId && channelId && role === "creator") {
            try {
              await this._foundA2AGroup(channelId, convId, this._persisted.a2aChannels?.[channelId]?.participants ?? []);
            } catch (err) {
              console.warn(`[SecureChannel] A2A MLS init failed (non-fatal):`, err);
            }
          } else if (this._persisted && convId && channelId && role === "member") {
            // Member agent: publish KeyPackage and wait for creator's Welcome.
            // Do NOT call /init — that creates a separate group and races with the creator.
            try {
              const mgr = new MLSGroupManager();
              const identity = new TextEncoder().encode(this._persisted.deviceId);
              const kp = await mgr.generateKeyPackage(identity);
              this._recordPublishedKp(kp);
              const kpBytes = MLSGroupManager.serializeKeyPackage(kp.publicPackage);
              await fetch(`${this.config.apiUrl}/api/v1/mls/key-packages`, {
                method: "POST",
                headers: {
                  Authorization: `Bearer ${this._deviceJwt}`,
                  "Content-Type": "application/json",
                },
                body: JSON.stringify({
                  key_package: Buffer.from(kpBytes).toString("hex"),
                  device_id: this._persisted.deviceId,
                }),
              });
              console.log(
                `[SecureChannel] A2A ${channelId.slice(0, 8)} member published KeyPackage, awaiting Welcome from creator`,
              );
            } catch (err) {
              console.warn(`[SecureChannel] A2A member KeyPackage publish failed (non-fatal):`, err);
            }
          }

          // NOTE: onA2AChannelReady is now fired after MLS Welcome join (see _handleMlsWelcome)
          // to ensure the MLS group is ready before the seed message is sent.
        }

        if (data.event === "a2a_channel_rejected") {
          this.emit("a2a_channel_rejected", data.data || data);
        }

        if (data.event === "a2a_channel_revoked") {
          const channelData = data.data || data;
          const channelId = channelData.channel_id;
          // Remove from persisted state
          if (this._persisted?.a2aChannels?.[channelId]) {
            delete this._persisted.a2aChannels[channelId];
            await this._persistState();
          }
          this.emit("a2a_channel_revoked", channelData);
        }

        // --- Multi-agent A2A participant events ---

        if (data.event === "a2a_channel_invited") {
          // Another agent invited us to a channel — publish KeyPackage, await Welcome
          const channelData = data.data || data;
          const channelId = channelData.channel_id;
          const convId = channelData.conversation_id;
          console.log(`[SecureChannel] Received a2a_channel_invited for ${channelId?.slice(0, 8)}`);

          if (this._persisted && channelId) {
            if (!this._persisted.a2aChannels) this._persisted.a2aChannels = {};
            const participants = (channelData.participants ?? [])
              .filter((p: any) => p.hub_id !== (this._persisted?.hubId || "") && p.hub_address !== (this._persisted?.hubAddress || ""))
              .map((p: any) => ({
                hubId: p.hub_id || "",
                hubAddress: p.hub_address || "",
                status: (p.status || "active") as "invited" | "active" | "left" | "removed",
              }));

            this._persisted.a2aChannels[channelId] = {
              ...this._persisted.a2aChannels[channelId],
              channelId,
              conversationId: convId || this._persisted.a2aChannels[channelId]?.conversationId || "",
              role: "member",
              participants,
            };
            await this._persistState();

            // Publish KeyPackage so creator can send Welcome
            try {
              const mgr = new MLSGroupManager();
              const identity = new TextEncoder().encode(this._persisted!.deviceId);
              const kp = await mgr.generateKeyPackage(identity);
              this._recordPublishedKp(kp);
              const kpBytes = MLSGroupManager.serializeKeyPackage(kp.publicPackage);
              await fetch(`${this.config.apiUrl}/api/v1/mls/key-packages`, {
                method: "POST",
                headers: {
                  Authorization: `Bearer ${this._deviceJwt}`,
                  "Content-Type": "application/json",
                },
                body: JSON.stringify({
                  key_package: Buffer.from(kpBytes).toString("hex"),
                  device_id: this._persisted!.deviceId,
                }),
              });
              console.log(`[SecureChannel] Published KeyPackage for A2A invite ${channelId.slice(0, 8)}`);
            } catch (kpErr) {
              console.warn(`[SecureChannel] KeyPackage publish failed for invited channel:`, kpErr);
            }
          }
          this.emit("a2a_channel_invited", channelData);
        }

        if (data.event === "a2a_participant_joined") {
          const eventData = data.data || data;
          const channelId = eventData.channel_id;
          const participant = eventData.participant;
          if (this._persisted?.a2aChannels?.[channelId] && participant) {
            const entry = this._persisted.a2aChannels[channelId];
            if (!entry.participants) entry.participants = [];
            const existing = entry.participants.find(p => p.hubId === participant.hub_id || p.hubAddress === participant.hub_address);
            if (existing) {
              existing.status = "active";
              existing.hubId = participant.hub_id || existing.hubId;
              existing.hubAddress = participant.hub_address || existing.hubAddress;
            } else {
              entry.participants.push({
                hubId: participant.hub_id || "",
                hubAddress: participant.hub_address || "",
                status: "active",
              });
            }
            await this._persistState();
            console.log(`[SecureChannel] Participant joined A2A ${channelId.slice(0, 8)}: ${participant.hub_address}`);

            // If we're creator, send Welcome to new participant
            if (entry.role === "creator" && entry.mlsGroupId) {
              const mlsGroup = this._mlsGroups.get(`a2a:${channelId}`);
              if (mlsGroup?.isInitialized) {
                try {
                  const kpRes = await fetch(
                    `${this.config.apiUrl}/api/v1/mls/key-packages?hub_id=${encodeURIComponent(participant.hub_id)}`,
                    { headers: { Authorization: `Bearer ${this._deviceJwt}` } },
                  );
                  if (kpRes.ok) {
                    const kpData = await kpRes.json();
                    if (kpData.key_package) {
                      const kpBytes = new Uint8Array(Buffer.from(kpData.key_package, "hex"));
                      const memberKp = MLSGroupManager.deserializeKeyPackage(kpBytes);
                      const { commit, welcome } = await mlsGroup.addMembers([memberKp]);
                      if (welcome && this._ws) {
                        this._ws.send(JSON.stringify({
                          event: "mls_commit",
                          data: {
                            group_id: entry.mlsGroupId,
                            epoch: Number(mlsGroup.epoch),
                            payload: Buffer.from(commit).toString("hex"),
                          },
                        }));
                        // Sister fix to the _foundA2AGroup dispatch (see PR #236):
                        // backend mls_welcome reads target_device_id+payload, not
                        // target_hub_id+welcome — without the right field names
                        // the welcome silently drops, the new participant never
                        // joins the MLS group, and the channel quietly bricks.
                        const newDeviceId = (participant as any).device_id || null;
                        if (!newDeviceId) {
                          console.warn(
                            `[SecureChannel] Skipping MLS Welcome to new participant ${participant.hub_address} for A2A ${channelId.slice(0, 8)}: no device_id on participant_joined payload.`,
                          );
                        } else {
                          this._ws.send(JSON.stringify({
                            event: "mls_welcome",
                            data: {
                              group_id: entry.mlsGroupId,
                              payload: Buffer.from(welcome).toString("hex"),
                              target_device_id: newDeviceId,
                              a2a_channel_id: channelId,
                              target_hub_id: participant.hub_id, // observability only
                            },
                          }));
                          await saveMlsState(this.config.dataDir, entry.mlsGroupId, JSON.stringify(mlsGroup.exportState()));
                          console.log(`[SecureChannel] Sent MLS Welcome to new participant ${participant.hub_address} (device ${String(newDeviceId).slice(0, 8)})`);
                        }
                      }
                    }
                  }
                } catch (welcomeErr) {
                  console.warn(`[SecureChannel] Welcome to new participant failed:`, welcomeErr);
                }
              }
            }
          }
          this.emit("a2a_participant_joined", eventData);
        }

        if (data.event === "a2a_participant_left" || data.event === "a2a_participant_removed") {
          const eventData = data.data || data;
          const channelId = eventData.channel_id;
          const participantHubId = eventData.hub_id || eventData.participant?.hub_id;
          if (this._persisted?.a2aChannels?.[channelId] && participantHubId) {
            const entry = this._persisted.a2aChannels[channelId];
            const p = entry.participants?.find(pp => pp.hubId === participantHubId);
            if (p) {
              p.status = data.event === "a2a_participant_left" ? "left" : "removed";
              await this._persistState();
              console.log(`[SecureChannel] Participant ${data.event.replace("a2a_participant_", "")} A2A ${channelId.slice(0, 8)}: ${p.hubAddress}`);
            }
          }
          this.emit(data.event, eventData);
        }

        if (data.event === "a2a_agent_removed") {
          // Our agent was kicked from the channel
          const eventData = data.data || data;
          const channelId = eventData.channel_id;
          if (this._persisted?.a2aChannels?.[channelId]) {
            // Clean up MLS group
            const entry = this._persisted.a2aChannels[channelId];
            if (entry.mlsGroupId) {
              this._mlsGroups.delete(`a2a:${channelId}`);
              await deleteMlsState(this.config.dataDir, entry.mlsGroupId);
            }
            delete this._persisted.a2aChannels[channelId];
            await this._persistState();
            console.log(`[SecureChannel] Removed from A2A channel ${channelId.slice(0, 8)}`);
          }
          this.emit("a2a_agent_removed", eventData);
        }

        if (data.event === "a2a_creator_transferred") {
          const eventData = data.data || data;
          const channelId = eventData.channel_id;
          const newCreatorHubId = eventData.new_creator_hub_id;
          if (this._persisted?.a2aChannels?.[channelId]) {
            const myHubId = this._persisted.hubId || "";
            this._persisted.a2aChannels[channelId].role =
              newCreatorHubId === myHubId ? "creator" : "member";
            await this._persistState();
            console.log(`[SecureChannel] Creator transferred for A2A ${channelId.slice(0, 8)}: ${newCreatorHubId === myHubId ? "we are now creator" : "we are member"}`);
          }
          this.emit("a2a_creator_transferred", eventData);
        }

        // Handle server error responses (prevents silent failures)
        if (data.event === "error") {
          const detail = (data.data?.detail || data.detail || "Unknown server error");
          console.error(`[SecureChannel] Server error: ${detail}`);
          this.emit("error", new Error(`Server: ${detail}`));
        }

        if (data.event === "a2a_message_mls") {
          // Dedup
          const msgId = (data.data || data).message_id;
          if (msgId && this._a2aSeenMessageIds.has(msgId)) return;
          if (msgId) {
            this._a2aSeenMessageIds.add(msgId);
            if (this._a2aSeenMessageIds.size > SecureChannel.A2A_SEEN_MAX) {
              const first = this._a2aSeenMessageIds.values().next().value;
              if (first) this._a2aSeenMessageIds.delete(first);
            }
          }
          await this._handleA2AMessageMLS(data.data || data);
        }
      } catch (err) {
        this.emit("error", err);
      }
    });

    ws.on("close", (code?: number) => {
      // Capture the close code so the rapid-disconnect classifier in
      // _scheduleReconnect can distinguish a server-driven "session_replaced"
      // (custom codes 4000/4001) from other disconnects.
      this._lastCloseCode = typeof code === "number" ? code : null;
      this._stopPing();
      this._stopWakeDetector();
      this._stopPendingPoll();
      if (this._deliveryHeartbeat) { clearInterval(this._deliveryHeartbeat); this._deliveryHeartbeat = null; }
      this._stopA2aReconcileTimer();
      if (this._stopped) return;
      this._setState("disconnected");
      this._scheduleReconnect();
    });

    ws.on("error", (err: Error) => {
      this.emit("error", err);
      // The close event will fire after this and handle reconnection
    });
  }

  /**
   * Handle an incoming encrypted message from a specific conversation.
   * Decrypts using the appropriate session ratchet, emits to the agent,
   * and relays as sync messages to sibling sessions.
   */
  /**
   * Handle an incoming MLS-encrypted 1:1 message.
   * Decrypts via the conversation's MLSGroupManager and emits the plaintext.
   */
  private async _handleMessageMLS(data: any): Promise<void> {
    const convId = data.conversation_id;
    const convGroupId = data.conversation_group_id;
    const groupId = data.group_id;
    const senderDeviceId = data.sender_device_id;

    // Skip own echo
    if (senderDeviceId === this._deviceId) return;

    // Find MLS group: shared 1:1 group first, then per-conversation, then by group_id search
    let mgr: MLSGroupManager | undefined;
    let mgrKey: string | undefined;

    if (convGroupId) {
      mgrKey = `1to1-group:${convGroupId}`;
      mgr = this._mlsGroups.get(mgrKey);
    }
    if (!mgr && convId) {
      mgrKey = `conv:${convId}`;
      mgr = this._mlsGroups.get(mgrKey);
    }
    if (!mgr && groupId) {
      // Search shared groups by mlsGroupId
      for (const [gid, entry] of Object.entries(this._persisted?.mlsGroups ?? {})) {
        if (entry.mlsGroupId === groupId) {
          mgrKey = `1to1-group:${gid}`;
          mgr = this._mlsGroups.get(mgrKey);
          break;
        }
      }
    }
    if (!mgr && groupId) {
      // Search legacy per-conversation groups by mlsGroupId
      for (const [cid, entry] of Object.entries(this._persisted?.mlsConversations ?? {})) {
        if (entry.mlsGroupId === groupId) {
          mgrKey = `conv:${cid}`;
          mgr = this._mlsGroups.get(mgrKey);
          break;
        }
      }
    }

    if (!mgr) {
      console.warn(`[SecureChannel] No MLS group for message_mls (conv=${convId?.slice(0, 8)}, group=${groupId?.slice(0, 8)})`);
      // Self-heal (BUG-20260604-001): a late-added member receives group
      // ciphertext it can't decrypt because it never got a Welcome. Ask the
      // committer to re-Welcome us (deduped). Without this the row is dropped
      // forever and the member stays silent.
      if (groupId) void this._requestWelcomeSelfHeal(groupId);
      return;
    }

    try {
      const cipherBytes = new Uint8Array(Buffer.from(data.payload, "hex"));
      const result = await mgr.decrypt(cipherBytes);

      // Save updated MLS state
      const mlsGroupId = (convGroupId ? this._persisted?.mlsGroups?.[convGroupId]?.mlsGroupId : null)
        ?? this._persisted?.mlsConversations?.[convId]?.mlsGroupId
        ?? groupId;
      if (mlsGroupId) {
        await saveMlsState(this.config.dataDir, mlsGroupId, JSON.stringify(mgr.exportState()));
      }

      if (result.isHandshake) return; // commit processing — no plaintext

      const plaintext = new TextDecoder().decode(result.plaintext);

      // Update last message timestamp for sync
      if (data.created_at) {
        this._persisted!.lastMessageTimestamp = data.created_at;
      }

      // Parse envelope
      let text = plaintext;
      let messageType = "text";
      let topicId: string | undefined = data.topic_id;
      try {
        const parsed = JSON.parse(plaintext);
        text = parsed.text ?? plaintext;
        messageType = parsed.type ?? "text";
        topicId = parsed.topicId ?? topicId;
      } catch {
        // plain string
      }

      // Handle history catchup request (from new devices joining 1:1 shared group)
      if (messageType === "history_catchup_request") {
        try {
          const request = JSON.parse(plaintext);
          const history = this._persisted?.messageHistory ?? [];
          let filtered = history;
          if (request.since_ts) {
            const sinceTime = new Date(request.since_ts).getTime();
            filtered = history.filter((e: any) => new Date(e.ts).getTime() > sinceTime);
          }
          // Limit to last 100 messages
          if (filtered.length > 100) filtered = filtered.slice(-100);

          const responsePayload = JSON.stringify({
            type: "history_catchup_response",
            target_device_id: request.requesting_device_id,
            messages: filtered.map((e: any) => ({
              sender: e.sender,
              sender_name: e.sender === "agent" ? (this.config.agentName ?? "Agent") : "Owner",
              text: e.text,
              ts: e.ts,
            })),
            no_history: filtered.length === 0,
          });

          // Send via shared MLS group
          if (mgr && mlsGroupId) {
            const responseBytes = new TextEncoder().encode(responsePayload);
            const cipher = await mgr.encrypt(responseBytes);
            await saveMlsState(this.config.dataDir, mlsGroupId, JSON.stringify(mgr.exportState()));
            if (this._ws) {
              this._ws.send(JSON.stringify({
                event: "message_mls",
                data: {
                  group_id: mlsGroupId,
                  epoch: Number(mgr.epoch),
                  payload: Buffer.from(cipher).toString("hex"),
                  message_type: "history_catchup_response",
                },
              }));
            }
            console.log(`[SecureChannel] Sent 1:1 history catchup: ${filtered.length} messages to ${request.requesting_device_id?.slice(0, 8)}`);
          }
        } catch (catchupErr) {
          console.warn("[SecureChannel] 1:1 history catchup failed:", catchupErr);
        }
        return; // Don't emit to agent
      }

      // Skip history catchup responses (for web clients only)
      if (messageType === "history_catchup_response") return;

      // Belt-and-suspenders (worker-mode removal P2): the 1:1 MLS path structurally
      // carries only 1:1 owner conversations. Room/A2A routing happens UPSTREAM at
      // the MLS-delivery dispatcher (~_pullDeliveryQueue), which routes rows with a
      // server `room_id`/`a2a_channel_id` to _handleRoomMessageMLS/_handleA2AMessageMLS
      // BEFORE this handler and threads only conversation_id/conversation_group_id
      // through — so `data.room_id`/`data.a2a_channel_id` are normally absent here
      // (the two disjuncts below are defensive and virtually never fire). The REAL
      // belt on this path is the LOCAL `_isNonDmConversation` (rooms + a2aChannels,
      // either id): if a room/A2A conv ever reached here it would emit as a no-roomId
      // `message`, which the native worker tool-gate treats as an owner DM (tool-
      // enabled, bypassing arming) — a prompt-injection -> tool-execution path once
      // capability is intrinsic. Skip it BEFORE any session side effect so a to-be-
      // dropped message cannot flip session.activated.
      if (data.room_id || data.a2a_channel_id || this._isNonDmConversation(convId, convGroupId)) {
        console.warn(`[SecureChannel] Dropped 1:1 MLS emit for non-DM conv ${(convId ?? convGroupId ?? "?").slice(0, 8)} (room/A2A guard)`);
        return;
      }

      // Activate the DR session for this conversation so send() includes it.
      // The send() loop iterates this._sessions and checks activated flag —
      // without this, MLS-received messages wouldn't trigger replies.
      if (convId) {
        const session = this._sessions.get(convId);
        if (session && !session.activated) {
          session.activated = true;
          if (this._persisted?.sessions?.[convId]) {
            this._persisted.sessions[convId].activated = true;
          }
          console.log(`[SecureChannel] Session ${convId.slice(0, 8)} activated via MLS message`);
        }
      }

      // Emit to agent
      const metadata = {
        messageId: data.message_id ?? `mls_${Date.now()}`,
        conversationId: convId ?? this._primaryConversationId,
        timestamp: data.created_at ?? new Date().toISOString(),
        topicId,
        messageType,
      };
      this.emit("message", text, metadata);
      if (this.config.onMessage) {
        this.config.onMessage(text, metadata);
      }
    } catch (decryptErr) {
      const failKey = mgrKey || `unknown:${groupId}`;
      const count = (this._mlsDecryptFailCounts.get(failKey) ?? 0) + 1;
      this._mlsDecryptFailCounts.set(failKey, count);
      console.error(`[SecureChannel] MLS 1:1 decrypt failed (${count}/${SecureChannel.MAX_MLS_DECRYPT_FAILS}) for ${failKey}:`, decryptErr);
      if (count >= SecureChannel.MAX_MLS_DECRYPT_FAILS) {
        const recoveryGroupId = (convGroupId ? this._persisted?.mlsGroups?.[convGroupId]?.mlsGroupId : null)
          ?? this._persisted?.mlsConversations?.[convId]?.mlsGroupId
          ?? groupId;
        if (recoveryGroupId && failKey) {
          // #440: catch up a behind member via /sync replay before re-joining.
          if (!(await this._resyncOnDivergence(failKey, recoveryGroupId))) {
            await this._triggerMlsEpochRecovery(failKey, recoveryGroupId);
          }
        }
      }
    }
  }

  private async _handleIncomingMessage(msgData: {
    message_id: string;
    conversation_id: string;
    sender_device_id: string;
    header_blob: string;
    ciphertext: string;
    created_at: string;
    message_group_id?: string;
    topic_id?: string;
    span_id?: string;
    parent_span_id?: string;
    message_type?: string;
    priority?: string;
    envelope_version?: string;
  }): Promise<void> {
    // Don't decrypt our own messages
    if (msgData.sender_device_id === this._deviceId) return;

    // Dedup: skip if this message was already processed during sync
    if (this._syncMessageIds?.has(msgData.message_id)) {
      return;
    }

    const convId = msgData.conversation_id;
    const session = this._sessions.get(convId);
    if (!session) {
      // No session for this conversation — trigger resync to establish one
      console.warn(
        `[SecureChannel] No session for conversation ${convId}, requesting resync`,
      );
      const RESYNC_COOLDOWN_MS = 5 * 60 * 1000;
      const lastResync = this._lastResyncRequest.get(convId) ?? 0;
      if (
        Date.now() - lastResync > RESYNC_COOLDOWN_MS &&
        this._ws &&
        this._persisted
      ) {
        this._lastResyncRequest.set(convId, Date.now());
        this._ws.send(
          JSON.stringify({
            event: "resync_request",
            data: {
              conversation_id: convId,
              reason: "no_session",
              identity_public_key: this._persisted.identityKeypair.publicKey,
              ephemeral_public_key:
                this._persisted.ephemeralKeypair.publicKey,
              epoch: this._persisted.sessions[convId]?.epoch,
            },
          }),
        );
        this.emit("resync_requested", {
          conversationId: convId,
          reason: "no_session",
        });
      }
      return;
    }

    const encrypted = transportToEncryptedMessage({
      header_blob: msgData.header_blob,
      ciphertext: msgData.ciphertext,
    });

    const ratchetSnapshot = session.ratchet.serialize();
    let plaintext: string;
    try {
      plaintext = session.ratchet.decrypt(encrypted);
    } catch (decryptErr) {
      console.error(`[SecureChannel] Decrypt failed for conv ${convId.slice(0, 8)}... — restoring ratchet:`, decryptErr);
      try {
        session.ratchet = DoubleRatchet.deserialize(ratchetSnapshot);
      } catch (restoreErr) {
        console.error("[SecureChannel] Ratchet restore failed:", restoreErr);
      }

      // Initiate resync if within cooldown window
      const RESYNC_COOLDOWN_MS = 5 * 60 * 1000; // 5 minutes
      const lastResync = this._lastResyncRequest.get(convId) ?? 0;
      if (Date.now() - lastResync > RESYNC_COOLDOWN_MS && this._ws && this._persisted) {
        this._lastResyncRequest.set(convId, Date.now());
        const idPubHex = this._persisted.identityKeypair.publicKey;
        const ephPubHex = this._persisted.ephemeralKeypair.publicKey;
        this._ws.send(
          JSON.stringify({
            event: "resync_request",
            data: {
              conversation_id: convId,
              reason: "decrypt_failure",
              identity_public_key: idPubHex,
              ephemeral_public_key: ephPubHex,
              epoch: this._persisted?.sessions[convId]?.epoch,
            },
          }),
        );
        console.log(`[SecureChannel] Sent resync_request for conv ${convId.slice(0, 8)}...`);
        this.emit("resync_requested", { conversationId: convId, reason: "decrypt_failure" });
      }
      return;
    }

    // ACK delivery to server
    this._sendAck(msgData.message_id);

    // Mark session as activated on first owner message — persist immediately
    // Also activate ALL sibling sessions so agent fans out replies to all owner devices
    if (!session.activated) {
      session.activated = true;
      console.log(`[SecureChannel] Session ${convId.slice(0, 8)}... activated by first owner message`);
      if (this._persisted?.sessions[convId]) {
        this._persisted.sessions[convId].activated = true;
      }
      this._activateSiblings(convId);
      await this._persistState();
    }

    // Parse structured payload (new format) or treat as raw text (legacy)
    let messageText: string;
    let messageType: string;
    let attachmentInfo: any = null;
    try {
      const parsed = JSON.parse(plaintext);
      messageType = parsed.type || "message";
      messageText = parsed.text || plaintext;
      if (parsed.attachment) {
        attachmentInfo = parsed.attachment;
      }
    } catch {
      // Legacy plain string format
      messageType = "message";
      messageText = plaintext;
    }

    if (messageType === "session_init") {
      // New device session activated — replay message history
      console.log(`[SecureChannel] session_init received for ${convId.slice(0, 8)}..., replaying history`);
      await this._replayHistoryToSession(convId);
      await this._persistState();
      return;
    }

    // --- Workspace file operations (no scan — these are structured commands) ---
    if (messageType === "workspace_file_upload") {
      try {
        const parsed = JSON.parse(plaintext);
        const workspaceDir = this._resolveWorkspaceDir();
        const { handleWorkspaceUpload } = await import("./workspace-handlers.js");
        const result = await handleWorkspaceUpload(parsed, workspaceDir);
        await this._sendStructuredReply(convId, { type: "workspace_file_ack", filename: parsed.filename, ...result });
      } catch (err: any) {
        await this._sendStructuredReply(convId, { type: "workspace_file_ack", status: "error", error: err.message });
      }
      return;
    }
    if (messageType === "workspace_file_list") {
      try {
        const workspaceDir = this._resolveWorkspaceDir();
        const { handleWorkspaceList } = await import("./workspace-handlers.js");
        const result = await handleWorkspaceList(workspaceDir);
        await this._sendStructuredReply(convId, { type: "workspace_file_list_response", ...result });
      } catch (err: any) {
        await this._sendStructuredReply(convId, { type: "workspace_file_list_response", files: [], error: err.message });
      }
      return;
    }
    if (messageType === "workspace_file_read") {
      try {
        const parsed = JSON.parse(plaintext);
        const workspaceDir = this._resolveWorkspaceDir();
        const { handleWorkspaceRead } = await import("./workspace-handlers.js");
        const result = await handleWorkspaceRead(parsed, workspaceDir);
        await this._sendStructuredReply(convId, { type: "workspace_file_read_response", ...result });
      } catch (err: any) {
        await this._sendStructuredReply(convId, { type: "workspace_file_read_response", error: err.message });
      }
      return;
    }

    // Inbound scan — after decryption
    if (this._scanEngine) {
      const scanResult = this._scanEngine.scanInbound(messageText);
      if (scanResult.status === "blocked") {
        this.emit("scan_blocked", { direction: "inbound", violations: scanResult.violations });
        return; // drop the message
      }
    }

    if (messageType === "message") {
      const topicId = msgData.topic_id;

      // Track last inbound topic so replies default to the correct channel
      this._lastIncomingTopicId = topicId;

      // Handle attachment: download, decrypt, save to disk
      let attachData: AttachmentData | undefined;
      if (attachmentInfo) {
        try {
          const { filePath, decrypted } = await this._downloadAndDecryptAttachment(attachmentInfo);
          attachData = {
            filename: attachmentInfo.filename,
            mime: attachmentInfo.mime,
            size: decrypted.length,
            filePath,
          };

          // For images: include base64 so LLM vision can see the content
          if (attachmentInfo.mime.startsWith("image/")) {
            attachData.base64 = `data:${attachmentInfo.mime};base64,${bytesToBase64(decrypted)}`;
          }

          // For text-based files: extract content inline
          const textMimes = ["text/", "application/json", "application/xml", "application/csv"];
          if (textMimes.some((m) => attachmentInfo.mime.startsWith(m))) {
            attachData.textContent = new TextDecoder().decode(decrypted);
          }
        } catch (err) {
          console.error(`[SecureChannel] Failed to download attachment:`, err);
        }
      }

      // Store owner message in history for cross-device replay
      this._appendHistory("owner", messageText, topicId);

      // Build display text with attachment content
      let emitText = messageText;
      if (attachData) {
        if (attachData.textContent) {
          emitText = `[Attachment: ${attachData.filename} (${attachData.mime}) saved to ${attachData.filePath}]\n---\n${attachData.textContent}\n---\n\n${messageText}`;
        } else if (attachData.base64) {
          emitText = `[Image attachment: ${attachData.filename} saved to ${attachData.filePath}]\nUse your Read tool to view this image file.\n\n${messageText}`;
        } else {
          emitText = `[Attachment: ${attachData.filename} saved to ${attachData.filePath}]\n\n${messageText}`;
        }
      }

      // Resolve room membership — check if this conversation belongs to a room
      const roomId = this._roomIdForConversation(convId);

      // Update room context when a room message arrives (sticky — never cleared by 1:1 traffic)
      if (roomId) this._lastInboundRoomId = roomId;

      // Emit to agent developer
      const metadata: MessageMetadata = {
        messageId: msgData.message_id,
        conversationId: convId,
        timestamp: msgData.created_at,
        topicId,
        attachment: attachData,
        spanId: msgData.span_id,
        parentSpanId: msgData.parent_span_id,
        messageType: msgData.message_type ?? "text",
        priority: msgData.priority ?? "normal",
        envelopeVersion: msgData.envelope_version ?? "1.0.0",
        roomId,
      };

      this.emit("message", emitText, metadata);
      Promise.resolve(this.config.onMessage?.(emitText, metadata)).catch((err: unknown) => {
        console.error("[SecureChannel] onMessage callback error:", err);
      });

      // Relay to sibling sessions as sync messages
      await this._relaySyncToSiblings(convId, session.ownerDeviceId, messageText, topicId);
    }
    // If type === "sync", agent ignores it (sync is for owner devices only)

    // Track last message timestamp for offline sync
    if (this._persisted) {
      this._persisted.lastMessageTimestamp = msgData.created_at;
    }

    // Persist all ratchet states after decrypt
    await this._persistState();
  }

  /**
   * Download an encrypted attachment blob, decrypt it, verify integrity,
   * and save the plaintext file to disk.
   */
  private async _downloadAndDecryptAttachment(info: {
    blobId: string;
    blobUrl: string;
    fileKey: string;
    fileNonce: string;
    digest: string;
    filename: string;
    mime: string;
    size: number;
  }): Promise<{ filePath: string; decrypted: Uint8Array }> {
    const attachDir = join(this.config.dataDir, "attachments");
    await mkdir(attachDir, { recursive: true });

    // Download encrypted blob from API
    const url = `${this.config.apiUrl}${info.blobUrl}`;
    const res = await fetch(url, {
      headers: { Authorization: `Bearer ${this._deviceJwt}` },
    });

    if (!res.ok) {
      throw new Error(`Attachment download failed: ${res.status}`);
    }

    const buffer = await res.arrayBuffer();
    const encryptedData = new Uint8Array(buffer);

    // Verify integrity
    const digest = computeFileDigest(encryptedData);
    if (digest !== info.digest) {
      throw new Error("Attachment digest mismatch — possible tampering");
    }

    // Decrypt
    const fileKey = base64ToBytes(info.fileKey);
    const fileNonce = base64ToBytes(info.fileNonce);
    const decrypted = decryptFile(encryptedData, fileKey, fileNonce);

    // Save to disk
    const filePath = join(attachDir, info.filename);
    await writeFile(filePath, decrypted);
    console.log(`[SecureChannel] Attachment saved: ${filePath} (${decrypted.length} bytes)`);

    return { filePath, decrypted };
  }

  /**
   * Upload an attachment file: encrypt, upload to server, return metadata
   * for inclusion in the message envelope.
   */
  private async _uploadAttachment(
    filePath: string,
    conversationId: string,
  ): Promise<{
    blobId: string;
    blobUrl: string;
    fileKey: string;
    fileNonce: string;
    digest: string;
    filename: string;
    mime: string;
    size: number;
  }> {
    const data = await readFile(filePath);
    const plainData = new Uint8Array(data);

    const result = encryptFile(plainData);

    // Upload encrypted blob via multipart form
    const { Blob: NodeBlob, FormData: NodeFormData } = await import("node:buffer").then(
      () => globalThis,
    );
    const formData = new FormData();
    formData.append("conversation_id", conversationId);
    formData.append(
      "file",
      new Blob([result.encryptedData.buffer as ArrayBuffer], { type: "application/octet-stream" }),
      "attachment.bin",
    );

    const res = await fetch(`${this.config.apiUrl}/api/v1/attachments/upload`, {
      method: "POST",
      headers: { Authorization: `Bearer ${this._deviceJwt}` },
      body: formData,
    });

    if (!res.ok) {
      const detail = await res.text();
      throw new Error(`Attachment upload failed (${res.status}): ${detail}`);
    }

    const resp: any = await res.json();
    const filename = filePath.split("/").pop() || "file";

    return {
      blobId: resp.blob_id,
      blobUrl: resp.blob_url,
      fileKey: bytesToBase64(result.fileKey),
      fileNonce: bytesToBase64(result.fileNonce),
      digest: result.digest,
      filename,
      mime: "application/octet-stream",
      size: plainData.length,
    };
  }

  /**
   * Send a message with an attached file. Encrypts the file, uploads it,
   * then sends the envelope with attachment metadata via Double Ratchet.
   */
  async sendWithAttachment(
    plaintext: string,
    filePath: string,
    options?: { topicId?: string },
  ): Promise<void> {
    if (this._state !== "ready" || this._sessions.size === 0 || !this._ws) {
      throw new Error("Channel is not ready");
    }

    const topicId = options?.topicId ?? this._persisted?.defaultTopicId;

    // Upload attachment using primary conversation for the blob
    const attachMeta = await this._uploadAttachment(filePath, this._primaryConversationId);

    // Build envelope
    const envelope = JSON.stringify({
      type: "message",
      text: plaintext,
      topicId,
      attachment: attachMeta,
    });

    // Store agent message in history
    this._appendHistory("agent", plaintext, topicId);

    const messageGroupId = randomUUID();
    const pendingWsSends: string[] = [];

    for (const [convId, session] of this._sessions) {
      if (!session.activated) continue;

      const encrypted = session.ratchet.encrypt(envelope);
      const transport = encryptedMessageToTransport(encrypted);

      pendingWsSends.push(
        JSON.stringify({
          event: "message",
          data: {
            conversation_id: convId,
            header_blob: transport.header_blob,
            ciphertext: transport.ciphertext,
            message_group_id: messageGroupId,
            topic_id: topicId,
          },
        }),
      );
    }

    // Persist ratchet states BEFORE sending over WS to avoid desync on crash
    await this._persistState();

    for (const frame of pendingWsSends) {
      this._ws.send(frame);
    }
  }

  /**
   * Relay an owner's message to all sibling sessions as encrypted sync messages.
   * This allows all owner devices to see messages from any single device.
   */
  private async _relaySyncToSiblings(
    sourceConvId: string,
    senderOwnerDeviceId: string,
    messageText: string,
    topicId?: string,
  ): Promise<void> {
    if (!this._ws || this._sessions.size <= 1) return;

    // Build set of room conversation IDs to exclude from sync relay
    // (room pairwise sessions should NOT receive 1:1 sync messages)
    const roomConvIds = new Set<string>();
    if (this._persisted?.rooms) {
      for (const room of Object.values(this._persisted.rooms)) {
        for (const cid of room.conversationIds) {
          roomConvIds.add(cid);
        }
      }
    }

    const syncPayload = JSON.stringify({
      type: "sync",
      sender: senderOwnerDeviceId,
      text: messageText,
      ts: new Date().toISOString(),
      topicId,
    });

    const pendingWsSends: string[] = [];

    for (const [siblingConvId, siblingSession] of this._sessions) {
      if (siblingConvId === sourceConvId) continue;
      // Don't relay to sessions where owner hasn't sent their first message yet
      if (!siblingSession.activated) continue;
      // Skip room pairwise sessions — those use sendToRoom()
      if (roomConvIds.has(siblingConvId)) continue;

      const syncEncrypted = siblingSession.ratchet.encrypt(syncPayload);
      const syncTransport = encryptedMessageToTransport(syncEncrypted);

      pendingWsSends.push(
        JSON.stringify({
          event: "message",
          data: {
            conversation_id: siblingConvId,
            header_blob: syncTransport.header_blob,
            ciphertext: syncTransport.ciphertext,
          },
        }),
      );
    }

    // Persist ratchet states BEFORE sending over WS to avoid desync on crash
    await this._persistState();

    for (const frame of pendingWsSends) {
      this._ws.send(frame);
    }
  }

  /**
   * Resolve the agent's workspace directory.
   * Looks for OpenClaw workspace config, falls back to default path.
   */
  private _resolveWorkspaceDir(): string {
    const homedir = osHomedir();
    const agentName = this.config.agentName;

    // Lead agent uses root OpenClaw workspace
    if (this._persisted?.agentRole === "lead") {
      return join(homedir, ".openclaw", "workspace");
    }

    // Try to read OpenClaw config for workspace path
    try {
      const configPath = join(homedir, ".openclaw", "openclaw.json");
      const raw = readFileSync(configPath, "utf-8");
      const config = JSON.parse(raw);
      const agents = config?.agents?.list;
      if (Array.isArray(agents) && agentName) {
        // Find the agent matching this account by id or name
        for (const agent of agents) {
          if ((agent.id === agentName || agent.name === agentName) &&
              agent.workspace && typeof agent.workspace === "string") {
            return agent.workspace;
          }
        }
      }
    } catch {
      // Config not found or unreadable — fall through to default
    }

    // Fallback: OpenClaw convention is ~/.openclaw/workspace-<name>/
    if (agentName && agentName !== "CLI Agent" && agentName !== "OpenClaw Agent") {
      return join(homedir, ".openclaw", `workspace-${agentName}`);
    }

    // Last resort: default workspace
    return join(homedir, ".openclaw", "workspace");
  }

  /**
   * Send a structured JSON reply to a specific conversation.
   * Encrypts the payload via the conversation's ratchet and sends via WebSocket.
   */
  private async _sendStructuredReply(convId: string, payload: Record<string, unknown>): Promise<void> {
    const session = this._sessions.get(convId);
    if (!session || !this._ws) {
      console.warn(`[SecureChannel] Cannot send structured reply — no session for ${convId.slice(0, 8)}...`);
      return;
    }

    const plaintext = JSON.stringify(payload);
    const encrypted = session.ratchet.encrypt(plaintext);
    const transport = encryptedMessageToTransport(encrypted);

    // Persist ratchet state BEFORE sending over WS to avoid desync on crash
    await this._persistState();

    this._ws.send(
      JSON.stringify({
        event: "message",
        data: {
          conversation_id: convId,
          header_blob: transport.header_blob,
          ciphertext: transport.ciphertext,
        },
      }),
    );
  }

  /**
   * Send stored message history to a newly-activated session.
   * Batches all history into a single encrypted message.
   */
  private async _replayHistoryToSession(convId: string): Promise<void> {
    const session = this._sessions.get(convId);
    if (!session || !session.activated || !this._ws) return;

    const history = this._persisted?.messageHistory ?? [];
    if (history.length === 0) {
      console.log(`[SecureChannel] No history to replay for ${convId.slice(0, 8)}...`);
      return;
    }

    console.log(
      `[SecureChannel] Replaying ${history.length} messages to session ${convId.slice(0, 8)}...`,
    );

    const replayPayload = JSON.stringify({
      type: "history_replay",
      messages: history,
    });

    const encrypted = session.ratchet.encrypt(replayPayload);
    const transport = encryptedMessageToTransport(encrypted);

    // Persist ratchet state BEFORE sending over WS to avoid desync on crash
    await this._persistState();

    this._ws.send(
      JSON.stringify({
        event: "message",
        data: {
          conversation_id: convId,
          header_blob: transport.header_blob,
          ciphertext: transport.ciphertext,
        },
      }),
    );
  }

  /**
   * Handle a device_linked event: a new owner device has joined.
   * Fetches the new device's public keys, performs X3DH, and initializes
   * a new ratchet session.
   */
  private async _handleDeviceLinked(event: {
    conversation_id: string;
    owner_device_id: string;
    group_id?: string;
    owner_identity_public_key?: string;
    owner_ephemeral_public_key?: string;
    owner_hub_id?: string;
  }): Promise<void> {
    console.log(
      `[SecureChannel] New owner device linked: ${event.owner_device_id.slice(0, 8)}...`,
    );

    try {
      if (!event.owner_identity_public_key) {
        console.error(
          `[SecureChannel] device_linked event missing owner keys for conv ${event.conversation_id.slice(0, 8)}...`,
        );
        return;
      }

      const identity = this._persisted!.identityKeypair;
      const ephemeral = this._persisted!.ephemeralKeypair;

      // Perform X3DH as responder with the new owner device
      const sharedSecret = performX3DH({
        myIdentityPrivate: hexToBytes(identity.privateKey),
        myEphemeralPrivate: hexToBytes(ephemeral.privateKey),
        theirIdentityPublic: hexToBytes(event.owner_identity_public_key),
        theirEphemeralPublic: hexToBytes(
          event.owner_ephemeral_public_key ?? event.owner_identity_public_key,
        ),
        isInitiator: false,
      });

      // Initialize ratchet as receiver
      const ratchet = DoubleRatchet.initReceiver(sharedSecret, {
        publicKey: hexToBytes(identity.publicKey),
        privateKey: hexToBytes(identity.privateKey),
        keyType: "ed25519",
      }, hexToBytes(event.owner_identity_public_key));

      this._sessions.set(event.conversation_id, {
        ownerDeviceId: event.owner_device_id,
        ratchet,
        activated: false, // Wait for owner's first message
        epoch: 1,
      });

      // Persist
      this._persisted!.sessions[event.conversation_id] = {
        ownerDeviceId: event.owner_device_id,
        ratchetState: ratchet.serialize(),
        activated: false,
        epoch: 1,
      };
      // Persist owner hub_id if provided
      if (event.owner_hub_id) {
        this._persisted!.ownerHubId = event.owner_hub_id;
      }
      await this._persistState();

      console.log(
        `[SecureChannel] Session initialized for device ${event.owner_device_id.slice(0, 8)}... (conv ${event.conversation_id.slice(0, 8)}...)`,
      );
    } catch (err) {
      console.error(
        `[SecureChannel] Failed to handle device_linked:`,
        err,
      );
      this.emit("error", err);
    }
  }

  /**
   * Handle a resync_request from the owner (owner-initiated ratchet re-establishment).
   * Re-derives shared secret via X3DH as responder, initializes fresh receiver ratchet,
   * and sends resync_ack back with agent's public keys.
   */
  private async _handleResyncRequest(data: {
    conversation_id: string;
    reason?: string;
    identity_public_key: string;
    ephemeral_public_key: string;
    sender_device_id?: string;
  }): Promise<void> {
    const convId = data.conversation_id;
    console.log(
      `[SecureChannel] Received resync_request for conv ${convId.slice(0, 8)}... (reason: ${data.reason ?? "unknown"})`,
    );

    try {
      if (!this._persisted) {
        console.error("[SecureChannel] Cannot handle resync — no persisted state");
        return;
      }

      const identity = this._persisted.identityKeypair;
      const ephemeral = this._persisted.ephemeralKeypair;

      // Perform X3DH as responder (agent is always responder in 1:1)
      const sharedSecret = performX3DH({
        myIdentityPrivate: hexToBytes(identity.privateKey),
        myEphemeralPrivate: hexToBytes(ephemeral.privateKey),
        theirIdentityPublic: hexToBytes(data.identity_public_key),
        theirEphemeralPublic: hexToBytes(data.ephemeral_public_key),
        isInitiator: false,
      });

      // Initialize fresh receiver ratchet
      const ratchet = DoubleRatchet.initReceiver(sharedSecret, {
        publicKey: hexToBytes(identity.publicKey),
        privateKey: hexToBytes(identity.privateKey),
        keyType: "ed25519",
      }, hexToBytes(data.identity_public_key));

      // Update session
      const existingSession = this._sessions.get(convId);
      const existingPersisted = this._persisted.sessions[convId];
      const ownerDeviceId = data.sender_device_id ?? existingSession?.ownerDeviceId ?? "";
      const newEpoch = ((existingPersisted?.epoch ?? existingSession?.epoch ?? 0) + 1);
      this._sessions.set(convId, {
        ownerDeviceId,
        ratchet,
        activated: false, // Wait for owner's encrypted session_init
        epoch: newEpoch,
      });

      // Persist
      this._persisted.sessions[convId] = {
        ownerDeviceId,
        ratchetState: ratchet.serialize(),
        activated: false,
        epoch: newEpoch,
      };
      await this._persistState();

      // Send resync_ack with agent's public keys
      if (this._ws) {
        this._ws.send(
          JSON.stringify({
            event: "resync_ack",
            data: {
              conversation_id: convId,
              identity_public_key: identity.publicKey,
              ephemeral_public_key: ephemeral.publicKey,
              epoch: newEpoch,
            },
          }),
        );
      }

      console.log(
        `[SecureChannel] Resync complete for conv ${convId.slice(0, 8)}... — waiting for owner session_init`,
      );
      this.emit("resync_completed", { conversationId: convId });
    } catch (err) {
      console.error(`[SecureChannel] Resync failed for conv ${convId.slice(0, 8)}...:`, err);
      this.emit("error", err);
    }
  }

  // _sendRoomResyncIfCooled removed — rooms are MLS-only now
  // _handleRoomMessage removed — rooms are MLS-only now

  /**
   * Handle credential protocol messages (grant, revoke, request).
   * These are intercepted before reaching the agent's onMessage callback.
   */
  private _handleCredentialMessage(
    roomId: string,
    messageType: string,
    plaintext: string,
    messageId?: string,
  ): void {
    try {
      const payload = JSON.parse(plaintext);

      if (messageType === "credential_grant") {
        const grant = payload as CredentialGrantPayload;

        // Replay prevention
        if (grant.nonce && !this._credentialStore.checkNonce(roomId, grant.nonce)) {
          console.warn(`[SecureChannel] Credential grant replay detected for room ${roomId.slice(0, 8)}..., ignoring`);
          return;
        }

        const keys: string[] = [];
        for (const cred of grant.credentials || []) {
          this._credentialStore.grant(roomId, {
            key: cred.key,
            value: cred.value,
            type: cred.type,
            scope: cred.scope,
            grantedAt: grant.timestamp || new Date().toISOString(),
            agreementId: grant.agreement_id,
            roomId,
          });
          keys.push(cred.key);
        }

        // Log without values
        console.log(
          `[SecureChannel] Credentials granted for room ${roomId.slice(0, 8)}...: ${keys.join(", ")} (${keys.length} keys)`,
        );

        // Send ACK back to room
        this._sendCredentialAck(roomId, grant.agreement_id, keys, "stored");

        this.emit("credentials_granted", { roomId, keys, agreementId: grant.agreement_id });
      } else if (messageType === "credential_revoke") {
        const revoke = payload as CredentialRevokePayload;
        const revoked: string[] = [];

        for (const key of revoke.credential_keys || []) {
          if (this._credentialStore.revoke(roomId, key)) {
            revoked.push(key);
          }
        }

        console.log(
          `[SecureChannel] Credentials revoked for room ${roomId.slice(0, 8)}...: ${revoked.join(", ")} (reason: ${revoke.reason || "none"})`,
        );

        this._sendCredentialAck(roomId, revoke.agreement_id, revoked, "revoked");

        this.emit("credentials_revoked", { roomId, keys: revoked, agreementId: revoke.agreement_id });
      }
      // credential_request is from agent → owner, not handled here (it originates from us)
    } catch (err) {
      console.error("[SecureChannel] Error handling credential message:", err);
    }

    // ACK the message itself
    if (messageId) {
      this._sendAck(messageId);
    }
  }

  /**
   * Send a credential_ack back to a room.
   */
  private _sendCredentialAck(roomId: string, agreementId: string, keys: string[], ackStatus: "stored" | "revoked"): void {
    const ack = JSON.stringify({
      type: "credential_ack",
      agreement_id: agreementId,
      acknowledged_keys: keys,
      status: ackStatus,
      timestamp: new Date().toISOString(),
    });

    // Send as room message using existing send path
    this.sendToRoom(roomId, ack, { messageType: "credential_ack" }).catch((err) => {
      console.warn("[SecureChannel] Failed to send credential ACK:", err);
    });
  }

  // --- Public credential accessors ---

  /** Get a specific renter credential for a room. */
  getCredential(roomId: string, key: string): import("./credential-store.js").RenterCredential | undefined {
    return this._credentialStore.get(roomId, key);
  }

  /** Get all renter credentials for a room (includes values — for agent context). */
  getCredentials(roomId: string): import("./credential-store.js").RenterCredential[] {
    return this._credentialStore.getAll(roomId);
  }

  /** Get credential key→value map for a room (for context injection). */
  getCredentialMap(roomId: string): Record<string, string> {
    return this._credentialStore.getCredentialMap(roomId);
  }

  /** Check if a specific credential exists for a room. */
  hasCredential(roomId: string, key: string): boolean {
    return this._credentialStore.has(roomId, key);
  }

  /** Purge all credentials for a room (call on rental end). */
  purgeRoomCredentials(roomId: string): void {
    this._credentialStore.purgeForRoom(roomId);
  }

  // _findConversationForSender removed — rooms are MLS-only now

  // ---------------------------------------------------------------------------
  // MLS room message handlers
  // ---------------------------------------------------------------------------

  private async _handleRoomMessageMLS(data: any): Promise<void> {
    const groupId = data.group_id;
    const senderDeviceId = data.sender_device_id;

    // Don't decrypt our own messages
    if (senderDeviceId === this._deviceId) return;

    // Find room by MLS group ID
    let roomId: string | undefined;
    for (const [rid, room] of Object.entries(this._persisted?.rooms ?? {})) {
      if ((room as RoomState).mlsGroupId === groupId) {
        roomId = rid;
        break;
      }
    }

    if (!roomId) {
      // Try matching by room_id directly (delivery queue includes room_id)
      if (data.room_id && this._persisted?.rooms?.[data.room_id]) {
        roomId = data.room_id as string;
        console.log(`[SecureChannel] Room ${roomId.slice(0, 8)} matched by room_id (group_id ${groupId?.slice(0, 8)} mismatch)`);
      } else {
        console.warn(`[SecureChannel] No room found for MLS group ${groupId?.slice(0, 8)}`);
        return;
      }
    }

    // At this point roomId is guaranteed defined (guarded above)
    const resolvedRoomId: string = roomId as string;

    // Track room context so HTTP /send and OpenClaw tools auto-route to this room
    this._lastInboundRoomId = resolvedRoomId;

    const mlsGroup = this._mlsGroups.get(resolvedRoomId);
    console.log(`[SecureChannel] MLS decrypt attempt room=${resolvedRoomId.slice(0, 8)} group=${groupId?.slice(0, 8)} loaded=${!!mlsGroup} init=${mlsGroup?.isInitialized} mlsGroupsKeys=[${Array.from(this._mlsGroups.keys()).join(',')}]`);
    if (!mlsGroup?.isInitialized) {
      console.warn(`[SecureChannel] MLS group not loaded for room ${resolvedRoomId.slice(0, 8)}, requesting Welcome`);
      // MLS-only rooms: no DR fallback. Request a Welcome from the group creator.
      // Publish a fresh KeyPackage first so the creator can encrypt the Welcome to us.
      // Use sync lock (per groupId) to prevent concurrent KP publish races across agents.
      const roomLockId = `room-kp-${groupId}`;
      if (!await acquireA2ASyncLock(this.config.dataDir, roomLockId)) {
        console.log(`[SecureChannel] Room KP publish locked for group ${groupId?.slice(0, 8)}, skipping`);
        return;
      }
      try {
        const { MLSGroupManager: MLS } = await import("@agentvault/crypto");
        const mgr = new MLS();
        const kp = await mgr.generateKeyPackage(new TextEncoder().encode(this._deviceId!));
        const kpBytes = MLS.serializeKeyPackage(kp.publicPackage);
        const kpHex = Buffer.from(kpBytes).toString("hex");
        await fetch(`${this.config.apiUrl}/api/v1/mls/key-packages`, {
          method: "POST",
          headers: { Authorization: `Bearer ${this._deviceJwt}`, "Content-Type": "application/json" },
          body: JSON.stringify({ key_package: kpHex, device_id: this._deviceId }),
        });
        // Store the key package bundle for joinFromWelcome later
        this._recordPublishedKp(kp);
        // Persist KP bundle so the Welcome handler can find it even after restart
        await savePendingKpBundle(this.config.dataDir, groupId!, kp, MLSGroupManager.serializeKeyPackage);
        // Request Welcome from creator
        await fetch(`${this.config.apiUrl}/api/v1/mls/groups/${groupId}/request-welcome`, {
          method: "POST",
          headers: { Authorization: `Bearer ${this._deviceJwt}`, "Content-Type": "application/json" },
          body: JSON.stringify({ device_id: this._deviceId }),
        });
        console.log(`[SecureChannel] Welcome requested for room ${resolvedRoomId.slice(0, 8)} group ${groupId?.slice(0, 8)}`);
      } catch (reqErr) {
        console.warn(`[SecureChannel] Welcome request failed for room ${resolvedRoomId.slice(0, 8)}:`, reqErr);
        await releaseA2ASyncLock(this.config.dataDir, roomLockId).catch(() => {});
      }
      return;
    }

    try {
      const ciphertext = Buffer.from(data.payload, "hex");
      const result = await mlsGroup.decrypt(new Uint8Array(ciphertext));

      // Persist updated MLS state
      await saveMlsState(this.config.dataDir, groupId, JSON.stringify(mlsGroup.exportState()));

      if (result.isHandshake || !result.plaintext) {
        // Handshake message (commit/proposal) — no application data
        return;
      }

      const rawPlaintext = new TextDecoder().decode(result.plaintext);

      // Parse structured message envelope (same as pairwise/SK handlers)
      let messageText: string;
      let messageType: string;
      try {
        const parsed = JSON.parse(rawPlaintext);
        messageType = parsed.type || "message";
        messageText = parsed.text || rawPlaintext;
      } catch {
        messageType = "message";
        messageText = rawPlaintext;
      }

      // Intercept credential protocol messages — handled internally
      if (CREDENTIAL_MESSAGE_TYPES.has(messageType)) {
        this._handleCredentialMessage(resolvedRoomId, messageType, messageText, data.message_id);
        if (data.created_at && this._persisted) {
          this._persisted.lastMessageTimestamp = data.created_at;
        }
        await this._persistState();
        return;
      }

      // Intercept history catch-up protocol messages
      if (CATCHUP_MESSAGE_TYPES.has(messageType)) {
        if (messageType === "history_catchup_request") {
          await this._handleHistoryCatchupRequest(resolvedRoomId, messageText, data.message_id);
        }
        // history_catchup_response is for web clients — agent ignores
        return;
      }

      // Only pass user-authored messages to the agent
      if (!ROOM_AGENT_TYPES.has(messageType)) {
        return;
      }

      if (data.message_id) {
        this._sendAck(data.message_id);
      }

      // Advance sync cursors
      if (data.created_at && this._persisted) {
        this._persisted.lastMessageTimestamp = data.created_at;
        // Track per-room MLS timestamp for same-epoch sync catch-up
        const roomState = this._persisted.rooms?.[resolvedRoomId];
        if (roomState) {
          (roomState as any).lastMlsMessageTs = data.created_at;
        }
      }

      await this._persistState();

      // GH #366: ensure the room roster is COMPLETE before surfacing the message.
      // The local roster is a join-time snapshot that can be missing members who
      // joined concurrently — so the first message gets an incomplete roster and
      // the agent frames a group room as a 1:1 DM. Refresh from the backend when
      // either (a) we haven't refreshed this room yet this process, or (b) the
      // sender isn't in the local roster (a member we don't know about). The
      // _roomRosterRefreshed flag keeps this to ~once per room (no refetch storm).
      {
        const known = this._persisted?.rooms?.[resolvedRoomId]?.members ?? [];
        const senderKnown = known.some((m: any) => m.deviceId === senderDeviceId);
        if (!this._roomRosterRefreshed.has(resolvedRoomId) || !senderKnown) {
          await this._refreshRoomRoster(resolvedRoomId);
        }
      }

      // Determine if sender is an agent (for loop prevention)
      const roomMembers = this._persisted?.rooms?.[resolvedRoomId]?.members ?? [];
      const senderMember = roomMembers.find((m: any) => m.deviceId === senderDeviceId);
      const senderIsAgent = senderMember?.entityType === "agent";

      const metadata: MessageMetadata = {
        messageId: data.message_id ?? "",
        conversationId: "",
        timestamp: data.created_at ?? new Date().toISOString(),
        messageType: messageType,
        roomId: resolvedRoomId,
        senderDeviceId,
        senderIsAgent,
        senderName: senderMember?.displayName || "unknown",
        roomName: this._persisted?.rooms?.[resolvedRoomId]?.name,
      };

      // Persist room message to history for contextual memory (MLS path)
      const senderLabel = senderMember?.displayName || "someone";
      this._appendHistory("owner", messageText, `room:${resolvedRoomId}`);

      this.emit("room_message", {
        roomId: resolvedRoomId,
        senderDeviceId,
        senderName: senderLabel,
        plaintext: messageText,
        messageType,
        timestamp: data.created_at ?? new Date().toISOString(),
      });

      // Prefix message with sender name so the LLM knows who said it
      const contextualMessage = senderIsAgent
        ? messageText  // Don't prefix agent-to-agent messages
        : `[${senderLabel}]: ${messageText}`;

      Promise.resolve(this.config.onMessage?.(contextualMessage, metadata)).catch((err: unknown) => {
        console.error("[SecureChannel] onMessage callback error (MLS):", err);
      });
    } catch (err) {
      const failKey = resolvedRoomId;
      const count = (this._mlsDecryptFailCounts.get(failKey) ?? 0) + 1;
      this._mlsDecryptFailCounts.set(failKey, count);
      console.error(`[SecureChannel] MLS room decrypt failed (${count}/${SecureChannel.MAX_MLS_DECRYPT_FAILS}) for ${resolvedRoomId.slice(0, 8)}:`, err);
      if (count >= SecureChannel.MAX_MLS_DECRYPT_FAILS && groupId) {
        // #440: an existing member that fell behind catches up by replaying the
        // missed commits via /sync FIRST; only fall back to re-join if it can't.
        if (!(await this._resyncOnDivergence(resolvedRoomId, groupId))) {
          await this._triggerMlsEpochRecovery(resolvedRoomId, groupId);
        }
      }
    }
  }

  /**
   * Handle a history catch-up request from a device that just rejoined a room.
   * Only the elected agent (smallest device_id among agents) responds.
   */
  private async _handleHistoryCatchupRequest(
    roomId: string,
    requestText: string,
    messageId?: string,
  ): Promise<void> {
    let request: { since_ts?: string | null; requesting_device_id?: string; retry?: boolean };
    try {
      request = JSON.parse(requestText);
    } catch {
      return;
    }

    // Agent election: only the lexicographically smallest agent device_id responds
    const roomMembers = this._persisted?.rooms?.[roomId]?.members ?? [];
    const agentDeviceIds = roomMembers
      .filter((m: RoomMemberInfo) => m.entityType !== "owner" && m.entityType !== "human")
      .map((m: RoomMemberInfo) => m.deviceId)
      .sort();

    const isElected = agentDeviceIds.length > 0 && agentDeviceIds[0] === this._deviceId;

    if (!isElected && !request.retry) {
      console.log(
        `[SecureChannel] History catchup: not elected (elected=${agentDeviceIds[0]?.slice(0, 8)}), ignoring`,
      );
      return;
    }

    // On retry, any agent with history responds
    const history = this.getRoomHistory(roomId, 100);
    let filtered = history;
    if (request.since_ts) {
      const sinceTime = new Date(request.since_ts).getTime();
      filtered = history.filter((e) => new Date(e.ts).getTime() > sinceTime);
    }

    console.log(
      `[SecureChannel] History catchup for room ${roomId.slice(0, 8)}: ` +
      `${filtered.length} messages since ${request.since_ts || "beginning"} ` +
      `(total history: ${history.length})`,
    );

    // Build response
    const responsePayload = JSON.stringify({
      type: "history_catchup_response",
      target_device_id: request.requesting_device_id,
      messages: filtered.map((e) => ({
        sender: e.sender,
        sender_name: e.sender === "agent" ? (this.config.agentName ?? "Agent") : "Owner",
        text: e.text,
        ts: e.ts,
      })),
      no_history: filtered.length === 0,
    });

    // Send via MLS
    try {
      await this.sendToRoom(roomId, responsePayload, { messageType: "history_catchup_response" });
      console.log(
        `[SecureChannel] Sent history catchup response: ${filtered.length} messages to ${request.requesting_device_id?.slice(0, 8)}`,
      );
    } catch (sendErr) {
      console.warn(`[SecureChannel] Failed to send history catchup response:`, sendErr);
    }
  }

  private async _handleMlsCommit(data: any): Promise<void> {
    const groupId = data.group_id;
    const epoch = typeof data.epoch === "number" ? data.epoch : 0;

    // Check rooms
    for (const [roomId, room] of Object.entries(this._persisted?.rooms ?? {})) {
      if ((room as RoomState).mlsGroupId === groupId) {
        const mlsGroup = this._mlsGroups.get(roomId);
        if (mlsGroup?.isInitialized) {
          try {
            const commitBytes = new Uint8Array(Buffer.from(data.payload, "hex"));
            await mlsGroup.processCommit(commitBytes);
            await saveMlsState(this.config.dataDir, groupId, JSON.stringify(mlsGroup.exportState()));
            console.log(`[SecureChannel] MLS commit processed for room ${roomId.slice(0, 8)} (epoch=${mlsGroup.epoch})`);
          } catch (err: any) {
            // Old-epoch commits are irrecoverable — log and move on (don't nack)
            const isOldEpoch = err?.message?.includes("epoch") || err?.message?.includes("old") || err?.message?.includes("Validation");
            if (isOldEpoch) {
              console.log(`[SecureChannel] Discarding old-epoch commit for room ${roomId.slice(0, 8)}`);
            } else {
              console.error(`[SecureChannel] MLS commit failed for room ${roomId.slice(0, 8)}:`, err);
              throw err;
            }
          }
        } else {
          // Buffer commit until Welcome arrives
          this._bufferMlsCommit(groupId, epoch, data);
          console.log(`[SecureChannel] Buffered MLS commit for room ${roomId.slice(0, 8)} (epoch=${epoch}, group not initialized)`);
        }
        return;
      }
    }

    // Check A2A channels
    for (const [chId, chState] of Object.entries(this._persisted?.a2aChannels ?? {})) {
      if ((chState as any).mlsGroupId === groupId) {
        const mlsGroup = this._mlsGroups.get(`a2a:${chId}`);
        if (mlsGroup?.isInitialized) {
          try {
            const commitBytes = new Uint8Array(Buffer.from(data.payload, "hex"));
            await mlsGroup.processCommit(commitBytes);
            await saveMlsState(this.config.dataDir, groupId, JSON.stringify(mlsGroup.exportState()));
            console.log(`[SecureChannel] MLS commit processed for A2A ${chId.slice(0, 8)} (epoch=${mlsGroup.epoch})`);
          } catch (err: any) {
            const isOldEpoch = err?.message?.includes("epoch") || err?.message?.includes("old") || err?.message?.includes("Validation");
            if (isOldEpoch) {
              console.log(`[SecureChannel] Discarding old-epoch commit for A2A ${chId.slice(0, 8)}`);
            } else {
              console.error(`[SecureChannel] MLS commit failed for A2A ${chId.slice(0, 8)}:`, err);
              throw err;
            }
          }
        } else {
          // Buffer commit until Welcome arrives
          this._bufferMlsCommit(groupId, epoch, data);
          console.log(`[SecureChannel] Buffered MLS commit for A2A ${chId.slice(0, 8)} (epoch=${epoch}, group not initialized)`);
        }
        return;
      }
    }
  }

  /** Buffer an MLS commit for replay after Welcome join. Max 50 per group. */
  private _bufferMlsCommit(groupId: string, epoch: number, data: any): void {
    let buf = this._pendingMlsCommits.get(groupId);
    if (!buf) {
      buf = [];
      this._pendingMlsCommits.set(groupId, buf);
    }
    // Dedup by epoch
    if (!buf.some(c => c.epoch === epoch)) {
      buf.push({ epoch, data });
      buf.sort((a, b) => a.epoch - b.epoch);
      // Cap buffer size to prevent memory leak
      if (buf.length > 50) buf.splice(0, buf.length - 50);
    }
  }

  /** Apply buffered MLS commits after Welcome join. */
  private async _applyBufferedMlsCommits(groupId: string, mgr: MLSGroupManager): Promise<void> {
    const buf = this._pendingMlsCommits.get(groupId);
    if (!buf || buf.length === 0) return;
    console.log(`[SecureChannel] Applying ${buf.length} buffered MLS commits for group ${groupId.slice(0, 8)}`);
    let applied = 0;
    for (const entry of buf) {
      if (entry.epoch <= mgr.epoch) continue; // already past this epoch
      try {
        const commitBytes = new Uint8Array(Buffer.from(entry.data.payload, "hex"));
        await mgr.processCommit(commitBytes);
        applied++;
      } catch (err: any) {
        console.warn(`[SecureChannel] Buffered commit epoch=${entry.epoch} failed: ${err?.message}`);
        break; // Stop on first failure — remaining commits depend on this one
      }
    }
    this._pendingMlsCommits.delete(groupId);
    if (applied > 0) {
      await saveMlsState(this.config.dataDir, groupId, JSON.stringify(mgr.exportState()));
      console.log(`[SecureChannel] Applied ${applied} buffered commits, now epoch=${mgr.epoch}`);
    }
  }

  /**
   * MLS epoch recovery for shared 1:1 groups.
   * Clears local state, publishes fresh KeyPackage, and requests Welcome.
   */
  /**
   * Self-heal for a group message we received but have NO MLS group state for
   * (BUG-20260604-001): a member added to a live room after founding gets group
   * ciphertext it can't decrypt because it never received a Welcome. Publish a
   * fresh KeyPackage + request a Welcome so the committer re-runs addMembers and
   * sends us one. Deduped to a 30s window per group so a burst of undecryptable
   * rows doesn't spam request-welcome. Fire-and-forget.
   */
  private async _requestWelcomeSelfHeal(groupId: string): Promise<void> {
    const now = Date.now();
    const last = this._welcomeSelfHealInflight.get(groupId) ?? 0;
    if (now - last < 30_000) return; // within dedup window
    this._welcomeSelfHealInflight.set(groupId, now);
    try {
      // Publish a fresh KeyPackage so the committer's addMembers uses a current one.
      const mgr = new MLSGroupManager();
      const identity = new TextEncoder().encode(this._deviceId!);
      const kp = await mgr.generateKeyPackage(identity);
      const kpBytes = MLSGroupManager.serializeKeyPackage(kp.publicPackage);
      const kpHex = Buffer.from(kpBytes).toString("hex");
      await fetch(`${this.config.apiUrl}/api/v1/mls/key-packages`, {
        method: "POST",
        headers: { Authorization: `Bearer ${this._deviceJwt}`, "Content-Type": "application/json" },
        body: JSON.stringify({ key_package: kpHex, device_id: this._deviceId }),
      });
      this._recordPublishedKp(kp);
      await savePendingKpBundle(this.config.dataDir, groupId, kp, MLSGroupManager.serializeKeyPackage);

      await fetch(`${this.config.apiUrl}/api/v1/mls/groups/${groupId}/request-welcome`, {
        method: "POST",
        headers: { Authorization: `Bearer ${this._deviceJwt}`, "Content-Type": "application/json" },
        body: JSON.stringify({ device_id: this._deviceId }),
      });
      console.log(`[SecureChannel] Welcome self-heal requested for group ${groupId.slice(0, 8)} (no local MLS state)`);
    } catch (err) {
      // Fire-and-forget: clear inflight so a later row can retry.
      this._welcomeSelfHealInflight.delete(groupId);
      console.warn(`[SecureChannel] Welcome self-heal failed for ${groupId.slice(0, 8)}:`, err);
    }
  }

  private async _triggerMlsEpochRecovery(groupKey: string, mlsGroupId: string): Promise<void> {
    console.warn(`[SecureChannel] MLS epoch recovery for ${groupKey} (${mlsGroupId.slice(0, 8)})`);
    // Clear in-memory and persisted state
    this._mlsGroups.delete(groupKey);
    this._mlsDecryptFailCounts.delete(groupKey);

    // Remove persisted MLS state file
    try {
      const { unlink } = await import("fs/promises");
      const path = `${this.config.dataDir}/.mls-state/${mlsGroupId}.json`;
      await unlink(path).catch(() => {});
    } catch { /* ignore */ }

    // Publish fresh KeyPackage
    try {
      const mgr = new MLSGroupManager();
      const identity = new TextEncoder().encode(this._deviceId!);
      const kp = await mgr.generateKeyPackage(identity);
      const kpBytes = MLSGroupManager.serializeKeyPackage(kp.publicPackage);
      const kpHex = Buffer.from(kpBytes).toString("hex");
      await fetch(`${this.config.apiUrl}/api/v1/mls/key-packages`, {
        method: "POST",
        headers: { Authorization: `Bearer ${this._deviceJwt}`, "Content-Type": "application/json" },
        body: JSON.stringify({ key_package: kpHex, device_id: this._deviceId }),
      });
      this._recordPublishedKp(kp);
      await savePendingKpBundle(this.config.dataDir, mlsGroupId, kp, MLSGroupManager.serializeKeyPackage);

      // Request Welcome
      await fetch(`${this.config.apiUrl}/api/v1/mls/groups/${mlsGroupId}/request-welcome`, {
        method: "POST",
        headers: { Authorization: `Bearer ${this._deviceJwt}`, "Content-Type": "application/json" },
        body: JSON.stringify({ device_id: this._deviceId }),
      });
      console.log(`[SecureChannel] Epoch recovery: Welcome requested for group ${mlsGroupId.slice(0, 8)}`);
    } catch (err) {
      console.error(`[SecureChannel] Epoch recovery failed for ${mlsGroupId.slice(0, 8)}:`, err);
    }
  }

  /**
   * #440 resync-on-divergence — catch up an EXISTING member that has fallen
   * BEHIND the group's epoch by FETCHING the missed commits and APPLYING them in
   * epoch order, INSTEAD of tearing down local state and re-joining (the
   * welcome / re-admit path in `_triggerMlsEpochRecovery`, which is for a member
   * that was never in the group). When an existing member lags, re-admit churns
   * the epoch and the room "goes dark"; replaying the missed commits re-converges
   * in place with no re-key.
   *
   * Mirrors the proven `Creator.resyncToCurrentEpoch` L2-gate blueprint:
   *   1. DETECT: our local manager exists + is initialized → local_epoch known.
   *   2. FETCH:  GET /mls/groups/{groupId}/sync?last_epoch=<localEpoch>
   *              → { action: "replay", messages: [...commits...] }
   *              (or { action: "rejoin" } when too far behind to replay).
   *   3. APPLY:  processCommit each returned commit, in epoch order.
   *
   * Returns true ONLY when the member caught up via replay (caller then SKIPS
   * epoch recovery). Returns false for action="rejoin", no local manager, an
   * empty replay, or any failure — in which case the caller falls back to
   * `_triggerMlsEpochRecovery` exactly as before (no behavior change on that path).
   *
   * @param groupKey the `_mlsGroups` map key for the member's manager (roomId,
   *   `1to1-group:<gid>`, `a2a:<channelId>`, …) — also the decrypt-fail-count key.
   * @param groupId  the server-side MLS group id used in the /sync URL.
   */
  private async _resyncOnDivergence(groupKey: string, groupId: string): Promise<boolean> {
    const mgr = this._mlsGroups.get(groupKey);
    if (!mgr?.isInitialized) return false; // no local state to catch up — fall back to rejoin
    const fromEpoch = Number(mgr.epoch);
    try {
      const res = await fetch(
        `${this.config.apiUrl}/api/v1/mls/groups/${groupId}/sync?last_epoch=${fromEpoch}`,
        { headers: { Authorization: `Bearer ${this._deviceJwt}` } },
      );
      if (!res.ok) {
        console.warn(`[SecureChannel] Resync /sync failed for ${groupId.slice(0, 8)}: ${res.status}`);
        return false;
      }
      const body = (await res.json()) as { action?: string; messages?: any[] };
      if (body.action !== "replay") {
        // "rejoin" (gap > REJOIN_THRESHOLD) — too far behind to replay; let the
        // caller fall back to the welcome / re-admit path.
        console.log(`[SecureChannel] Resync action=${body.action} for ${groupId.slice(0, 8)} — falling back to rejoin`);
        return false;
      }
      const commits = (body.messages ?? [])
        .filter((m: any) => m.message_type === "commit")
        .sort((a: any, b: any) => Number(a.epoch) - Number(b.epoch));
      let applied = 0;
      for (const c of commits) {
        if (Number(c.epoch) <= Number(mgr.epoch)) continue; // already past this epoch
        await mgr.processCommit(new Uint8Array(Buffer.from(String(c.payload), "hex")));
        applied++;
      }
      if (applied === 0) {
        // Nothing newer to replay — the failure isn't a simple epoch lag (likely
        // genuine corruption); let the caller fall back to recovery as before.
        console.log(`[SecureChannel] Resync for ${groupId.slice(0, 8)}: no new commits to apply (epoch=${mgr.epoch})`);
        return false;
      }
      await saveMlsState(this.config.dataDir, groupId, JSON.stringify(mgr.exportState()));
      this._mlsDecryptFailCounts.delete(groupKey);
      console.log(
        `[SecureChannel] Resync-on-divergence: applied ${applied} missed commit(s) for ${groupId.slice(0, 8)}, now epoch=${mgr.epoch}`,
      );
      return true;
    } catch (err) {
      console.warn(`[SecureChannel] Resync-on-divergence failed for ${groupId.slice(0, 8)}:`, err);
      return false;
    }
  }

  /**
   * Record a freshly-published KeyPackage bundle. Keeps it as the latest single
   * pending bundle (back-compat) AND appends it to the pending pool so a later
   * Welcome encrypted to it can still be matched even after a subsequent publish
   * overwrote `_pendingMlsKpBundle`. De-duped by public-key bytes; the pool is
   * bounded to `_KP_POOL_TARGET` (oldest dropped) so it can't grow unbounded.
   */
  private _recordPublishedKp(kp: KpBundle): void {
    this._pendingMlsKpBundle = kp;
    const idOf = (b: KpBundle) =>
      Buffer.from(MLSGroupManager.serializeKeyPackage(b.publicPackage)).toString("base64");
    const id = idOf(kp);
    if (!this._pendingKpBundles.some((b) => idOf(b) === id)) {
      this._pendingKpBundles.push(kp);
    }
    while (this._pendingKpBundles.length > SecureChannel._KP_POOL_TARGET) {
      this._pendingKpBundles.shift();
    }
  }

  /** Drop a consumed bundle from the pending pool after a successful join. */
  private _removeKpFromPool(kp: KpBundle): void {
    const idx = this._pendingKpBundles.indexOf(kp);
    if (idx >= 0) this._pendingKpBundles.splice(idx, 1);
  }

  /**
   * Top the published KeyPackage pool back up to `_KP_POOL_TARGET`: generate +
   * publish one fresh KP per missing slot and record each into the pool only
   * after the backend accepts it. Called on connect (awaited) AND fire-and-forget
   * after every consumed Welcome join, so a long-lived connection that is added
   * to many rooms never drains the pool to empty (the starvation this fixes).
   *
   * Reentrancy-guarded so a connect-time fill and a post-join replenish (or two
   * concurrent welcomes) don't double-publish past target. The while-loop
   * re-checks the pool length each iteration, so a consume that lands mid-fill
   * is self-correcting. A rejected publish (non-2xx) is NOT recorded locally —
   * that keeps the local pool view consistent with the backend — and stops the
   * loop; the next trigger retries.
   */
  private async _ensureKpPoolPublished(): Promise<void> {
    if (this._kpPoolFilling) return;
    if (this._pendingKpBundles.length >= SecureChannel._KP_POOL_TARGET) return;
    this._kpPoolFilling = true;
    try {
      const identity = new TextEncoder().encode(this._deviceId!);
      let publishedCount = 0;
      while (this._pendingKpBundles.length < SecureChannel._KP_POOL_TARGET) {
        const mlsMgr = new MLSGroupManager();
        const kp = await mlsMgr.generateKeyPackage(identity);
        const kpHex = Buffer.from(
          MLSGroupManager.serializeKeyPackage(kp.publicPackage),
        ).toString("hex");
        const res = await fetch(`${this.config.apiUrl}/api/v1/mls/key-packages`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${this._deviceJwt}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ key_package: kpHex }),
        });
        if (!res.ok) {
          // Don't record a KP the backend rejected — the local pool must mirror
          // the backend's unconsumed set for try-each to be sound.
          console.warn(
            `[SecureChannel] KeyPackage publish rejected (status ${res.status}); pool=${this._pendingKpBundles.length}/${SecureChannel._KP_POOL_TARGET}, will retry on next trigger`,
          );
          break;
        }
        this._recordPublishedKp(kp);
        if (!this._mlsKeyPackage) this._mlsKeyPackage = kp;
        publishedCount++;
      }
      if (publishedCount > 0) {
        console.log(
          `[SecureChannel] MLS KeyPackage pool published (${publishedCount} new, pool=${this._pendingKpBundles.length}/${SecureChannel._KP_POOL_TARGET})`,
        );
      }
    } catch (kpErr) {
      console.warn("[SecureChannel] Failed to publish MLS KeyPackage pool:", kpErr);
    } finally {
      this._kpPoolFilling = false;
    }
  }

  private async _handleMlsWelcome(data: any): Promise<void> {
    const groupId = data.group_id;
    const conversationId = data.conversation_id;
    const conversationGroupId = data.conversation_group_id;
    const kpSource = this._pendingMlsKpBundle ? "pending" : this._mlsKeyPackage ? "connect" : (await hasPendingWelcome(this.config.dataDir, groupId)) ? "persisted" : "generated";
    console.log(`[SecureChannel] Received MLS welcome for group ${groupId?.slice(0, 8)}${conversationId ? ` conv=${conversationId.slice(0, 8)}` : ""} kpSource=${kpSource}`);

    try {
      const welcomeBytes = new Uint8Array(Buffer.from(data.payload, "hex"));

      // Try-each: the Welcome is HPKE-encrypted to exactly one of the KPs we
      // published, and the backend consumes published KPs newest-first (#508) so we can't
      // predict which one the room's creator was served. Build the candidate
      // set from the pending pool, the connect KP, and the persisted per-group
      // bundle (covers multi-instance gateways / restart), then trial-decrypt
      // against each until one joins. A per-candidate failure means "wrong KP",
      // not a broken Welcome — only ALL candidates failing is a real failure.
      const candidates: Array<KpBundle | null | undefined> = [
        ...this._pendingKpBundles,
        this._pendingMlsKpBundle,
        this._mlsKeyPackage,
      ];
      const persisted = await loadPendingKpBundle(this.config.dataDir, groupId);
      if (persisted) {
        candidates.push({
          publicPackage: MLSGroupManager.deserializeKeyPackage(persisted.publicPackageBytes),
          privatePackage: persisted.privatePackage,
        });
      }

      const candidateCount = candidates.filter(Boolean).length;
      const joined = await joinWithFirstMatchingKp(welcomeBytes, candidates);
      if (!joined) {
        console.error(`[SecureChannel] No matching KeyPackage found for Welcome — cannot join group ${groupId?.slice(0, 8)}. The Welcome is encrypted to a published KP's public key; none of our ${candidateCount} candidate bundle(s) decrypted it. The existing Welcome recovery mechanism will re-request.`);
        return;
      }
      const mgr = joined.mgr;
      // The matched KeyPackage is single-use — drop it from the pool so a later
      // Welcome doesn't reuse it, then fire-and-forget a replenish so the pool
      // tops back up to target. Without this the pool drains monotonically on a
      // long-lived connection and the agent starves after _KP_POOL_TARGET joins.
      this._removeKpFromPool(joined.kp);
      void this._ensureKpPoolPublished().catch((err: unknown) => {
        console.warn("[SecureChannel] KeyPackage pool replenish after join failed:", err);
      });
      console.log(`[SecureChannel] Welcome joined: group=${groupId?.slice(0, 8)} kpSource=${kpSource} welcomeLen=${welcomeBytes.length} candidates=${candidateCount} poolRemaining=${this._pendingKpBundles.length}`);

      // Shared 1:1 group Welcome (has conversation_group_id)
      if (conversationGroupId) {
        const key = `1to1-group:${conversationGroupId}`;
        this._mlsGroups.set(key, mgr);
        await saveMlsState(this.config.dataDir, groupId, JSON.stringify(mgr.exportState()));
        await this._applyBufferedMlsCommits(groupId, mgr);
        if (this._persisted) {
          if (!this._persisted.mlsGroups) this._persisted.mlsGroups = {};
          this._persisted.mlsGroups[conversationGroupId] = {
            mlsGroupId: groupId,
            memberDeviceIds: data.member_device_ids,
          };
          await this._persistState();
        }
        console.log(`[SecureChannel] Joined shared 1:1 MLS group for group ${conversationGroupId.slice(0, 8)} via Welcome (epoch=${mgr.epoch})`);
        return;
      }

      // Legacy per-conversation Welcome (backward compat)
      if (conversationId) {
        const key = `conv:${conversationId}`;
        this._mlsGroups.set(key, mgr);
        await saveMlsState(this.config.dataDir, groupId, JSON.stringify(mgr.exportState()));
        // Apply any commits that arrived before this Welcome
        await this._applyBufferedMlsCommits(groupId, mgr);
        // Persist the group ID in state for reconnect recovery
        if (this._persisted) {
          if (!this._persisted.mlsConversations) this._persisted.mlsConversations = {};
          this._persisted.mlsConversations[conversationId] = { mlsGroupId: groupId };
          await this._persistState();
        }
        console.log(`[SecureChannel] Joined 1:1 MLS group for conv ${conversationId.slice(0, 8)} via Welcome (epoch=${mgr.epoch})`);
        return;
      }

      // Room/A2A Welcome — match by group_id OR room_id from Welcome data
      const welcomeRoomId = data.room_id;
      for (const [roomId, room] of Object.entries(this._persisted?.rooms ?? {})) {
        if ((room as RoomState).mlsGroupId === groupId || roomId === welcomeRoomId) {
          // Update the persisted mlsGroupId in case it changed (e.g., nuclear reset)
          (room as RoomState).mlsGroupId = groupId;
          this._mlsGroups.set(roomId, mgr);
          await saveMlsState(this.config.dataDir, groupId, JSON.stringify(mgr.exportState()));
          await this._persistState();
          // Apply any commits that arrived before this Welcome
          await this._applyBufferedMlsCommits(groupId, mgr);
          console.log(`[SecureChannel] Joined MLS group for room ${roomId.slice(0, 8)} via Welcome (epoch=${mgr.epoch})`);
          // Clear pending KP bundle and release room KP lock after successful join
          this._pendingMlsKpBundle = undefined;
          await releaseA2ASyncLock(this.config.dataDir, `room-kp-${groupId}`).catch(() => {});
          return;
        }
      }

      // Fallback: a Welcome for a room we have NOT persisted yet. Happens when we
      // were added to an existing room and the room_joined that registers the room
      // raced behind (or was lost relative to) the Welcome. Without a room entry the
      // room-match loop above can't match, and every subsequent room message would be
      // dropped with "No room found for MLS group". Self-bootstrap a minimal room
      // entry from the Welcome so group→room mapping exists — mirrors the A2A
      // fallback below. A later room_joined enriches name/members. The backend now
      // sends the new device its own room_joined (PR #412); this is defense-in-depth
      // for the race where that delivery is missed.
      if (welcomeRoomId && this._persisted) {
        await this._registerRoomFromWelcome(
          welcomeRoomId,
          groupId,
          mgr,
          data.room_name as string | undefined,
        );
        this._pendingMlsKpBundle = undefined;
        await releaseA2ASyncLock(this.config.dataDir, `room-kp-${groupId}`).catch(() => {});
        console.log(
          `[SecureChannel] Registered room ${welcomeRoomId.slice(0, 8)} from unmatched Welcome (epoch=${mgr.epoch})`,
        );
        return;
      }

      // A2A channel Welcome — match by group_id or a2a_channel_id from the Welcome data
      const a2aChannelId = data.a2a_channel_id;
      for (const [channelId, entry] of Object.entries(this._persisted?.a2aChannels ?? {})) {
        if (entry.mlsGroupId === groupId || channelId === a2aChannelId) {
          entry.mlsGroupId = groupId; // ensure mapping is stored
          this._mlsGroups.set(`a2a:${channelId}`, mgr);
          await saveMlsState(this.config.dataDir, groupId, JSON.stringify(mgr.exportState()));
          await this._persistState();
          // Apply any commits that arrived before this Welcome
          await this._applyBufferedMlsCommits(groupId, mgr);
          // Clear pending lock and KP bundle on successful join
          await clearPendingWelcome(this.config.dataDir, groupId).catch(() => {});
          await releaseA2ASyncLock(this.config.dataDir, channelId).catch(() => {});
          this._pendingMlsKpBundle = undefined;
          console.log(`[SecureChannel] Joined MLS group for A2A ${channelId.slice(0, 8)} via Welcome (epoch=${mgr.epoch})`);

          // Fire onA2AChannelReady now that MLS group is joined and ready for messaging
          if (this.config.onA2AChannelReady && entry.conversationId) {
            const firstPeer = entry.participants?.find(p => p.status === "active" || p.status === "invited");
            this.config.onA2AChannelReady({
              channelId,
              peerHubAddress: firstPeer?.hubAddress || "",
              role: entry.role || "member",
              conversationId: entry.conversationId,
              topic: entry.topic || undefined,
            });
          }
          return;
        }
      }

      // Fallback: if a2a_channel_id provided but not in persisted state yet, store it
      if (a2aChannelId && this._persisted) {
        if (!this._persisted.a2aChannels) this._persisted.a2aChannels = {};
        if (!this._persisted.a2aChannels[a2aChannelId]) {
          this._persisted.a2aChannels[a2aChannelId] = {
            channelId: a2aChannelId,
            conversationId: "",
            mlsGroupId: groupId,
            participants: [],
          };
        } else {
          this._persisted.a2aChannels[a2aChannelId].mlsGroupId = groupId;
        }
        this._mlsGroups.set(`a2a:${a2aChannelId}`, mgr);
        await saveMlsState(this.config.dataDir, groupId, JSON.stringify(mgr.exportState()));
        await this._persistState();
        await releaseA2ASyncLock(this.config.dataDir, a2aChannelId).catch(() => {});
        console.log(`[SecureChannel] Joined MLS group for A2A ${a2aChannelId.slice(0, 8)} via Welcome (new channel, epoch=${mgr.epoch})`);
        return;
      }

      console.warn(`[SecureChannel] MLS welcome for unmatched group ${groupId?.slice(0, 8)} (no room/A2A/conv match)`);
    } catch (err) {
      // Release any A2A sync lock on failure so next cycle can retry
      const failedA2aId = data.a2a_channel_id;
      if (failedA2aId) await releaseA2ASyncLock(this.config.dataDir, failedA2aId).catch(() => {});
      console.error(`[SecureChannel] MLS welcome processing failed (kpSource=${kpSource}):`, err);
      // Re-throw so delivery queue nacks this message instead of silently acking
      throw err;
    }
  }

  /**
   * Self-bootstrap a room entry + MLS group mapping from a Welcome whose room is
   * not yet persisted. Happens when we were added to an EXISTING room and the
   * `room_joined` that registers the room raced behind (or was lost relative to)
   * the Welcome. Without a room entry the Welcome room-match loop can't match, and
   * every subsequent room message is dropped with "No room found for MLS group".
   * Mirrors the A2A self-bootstrap fallback. The backend now also sends the new
   * device its own `room_joined` (PR #412); this is defense-in-depth for the race
   * where that delivery is missed. A later `room_joined` enriches name/members.
   */
  private async _registerRoomFromWelcome(
    roomId: string,
    groupId: string,
    mgr: MLSGroupManager,
    roomName?: string,
  ): Promise<void> {
    if (!this._persisted) return;
    if (!this._persisted.rooms) this._persisted.rooms = {};
    const existing = this._persisted.rooms[roomId] as RoomState | undefined;
    this._persisted.rooms[roomId] = {
      roomId,
      name: existing?.name ?? (roomName ?? ""),
      conversationIds: existing?.conversationIds ?? [],
      members: existing?.members ?? [],
      mlsGroupId: groupId,
      lastMlsMessageTs: existing?.lastMlsMessageTs,
    };
    this._mlsGroups.set(roomId, mgr);
    await saveMlsState(this.config.dataDir, groupId, JSON.stringify(mgr.exportState()));
    await this._persistState();
    await this._applyBufferedMlsCommits(groupId, mgr);
  }

  /**
   * Pull pending MLS messages from the delivery queue and process them.
   * Called on WS connect, on mls_delivery ping, and every 30s heartbeat.
   */
  private async _pullDeliveryQueue(): Promise<void> {
    if (!this._deviceId || !this._deviceJwt || this._deliveryPulling) return;
    this._deliveryPulling = true;

    try {
      const res = await fetch(
        `${this.config.apiUrl}/api/v1/mls/delivery?device_id=${this._deviceId}`,
        { headers: { Authorization: `Bearer ${this._deviceJwt}` } },
      );
      if (!res.ok) {
        console.warn(`[SecureChannel] Delivery pull failed: ${res.status}`);
        return;
      }
      const { messages } = (await res.json()) as { messages: any[] };
      const hasMessages = messages && messages.length > 0;

      // Process delivery queue messages (if any)
      if (hasMessages) {
        // Sort: welcome first, then commit, then application
        const order: Record<string, number> = { welcome: 0, commit: 1, application: 2 };
        messages.sort((a: any, b: any) => {
          const ao = order[a.message_type] ?? 2;
          const bo = order[b.message_type] ?? 2;
          if (ao !== bo) return ao - bo;
          return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
        });

        const ackedIds: string[] = [];
        const nackedIds: string[] = [];

        for (const msg of messages) {
          try {
            if (msg.sender_device_id === this._deviceId) {
              ackedIds.push(msg.queue_id);
              continue;
            }

            if (msg.message_type === "welcome") {
              await this._handleMlsWelcome({
                group_id: msg.group_id,
                sender_device_id: msg.sender_device_id,
                payload: msg.payload,
                room_id: msg.room_id,
                conversation_id: msg.conversation_id,
                conversation_group_id: msg.conversation_group_id,
                a2a_channel_id: msg.a2a_channel_id,
              });
            } else if (msg.message_type === "commit") {
              await this._handleMlsCommit({
                group_id: msg.group_id,
                sender_device_id: msg.sender_device_id,
                epoch: msg.epoch,
                payload: msg.payload,
              });
            } else if (msg.message_type === "application") {
              // Route to the appropriate handler based on context
              if (msg.room_id) {
                await this._handleRoomMessageMLS({
                  message_id: msg.message_id,
                  group_id: msg.group_id,
                  sender_device_id: msg.sender_device_id,
                  epoch: msg.epoch,
                  payload: msg.payload,
                  room_id: msg.room_id,
                  created_at: msg.created_at,
                });
              } else if (msg.a2a_channel_id) {
                await this._handleA2AMessageMLS({
                  message_id: msg.message_id,
                  group_id: msg.group_id,
                  sender_device_id: msg.sender_device_id,
                  epoch: msg.epoch,
                  payload: msg.payload,
                  a2a_channel_id: msg.a2a_channel_id,
                  created_at: msg.created_at,
                });
              } else if (msg.conversation_id || msg.conversation_group_id) {
                await this._handleMessageMLS({
                  message_id: msg.message_id,
                  group_id: msg.group_id,
                  sender_device_id: msg.sender_device_id,
                  epoch: msg.epoch,
                  payload: msg.payload,
                  conversation_id: msg.conversation_id,
                  conversation_group_id: msg.conversation_group_id,
                  created_at: msg.created_at,
                });
              }
            }
            ackedIds.push(msg.queue_id);
          } catch (err) {
            console.warn(`[SecureChannel] Delivery ${msg.message_type} processing failed:`, err);
            nackedIds.push(msg.queue_id);
            // Epoch recovery for MLS decrypt failures
            if (msg.group_id && (msg.message_type === "commit" || msg.message_type === "application")) {
              const failKey = msg.conversation_group_id ? `1to1-group:${msg.conversation_group_id}` : `group:${msg.group_id}`;
              const count = (this._mlsDecryptFailCounts.get(failKey) ?? 0) + 1;
              this._mlsDecryptFailCounts.set(failKey, count);
              if (count >= SecureChannel.MAX_MLS_DECRYPT_FAILS) {
                await this._triggerMlsEpochRecovery(failKey, msg.group_id);
              }
            }
          }
        }

        // Batch nack failed messages (increments retry_count, dead-letter after 5)
        if (nackedIds.length > 0) {
          try {
            await fetch(`${this.config.apiUrl}/api/v1/mls/delivery/nack`, {
              method: "POST",
              headers: {
                Authorization: `Bearer ${this._deviceJwt}`,
                "Content-Type": "application/json",
              },
              body: JSON.stringify({ queue_ids: nackedIds }),
            });
          } catch { /* best effort */ }
        }

        // Batch ack
        if (ackedIds.length > 0) {
          try {
            await fetch(`${this.config.apiUrl}/api/v1/mls/delivery/ack`, {
              method: "POST",
              headers: {
                Authorization: `Bearer ${this._deviceJwt}`,
                "Content-Type": "application/json",
              },
              body: JSON.stringify({ queue_ids: ackedIds, device_id: this._deviceId }),
            });
            console.log(`[SecureChannel] Delivery acked ${ackedIds.length}/${messages.length}`);
          } catch (ackErr) {
            console.warn("[SecureChannel] Delivery ack failed:", ackErr);
          }
        }
      } // end hasMessages

      // Check for pending Welcome requests for rooms we have MLS state
      for (const [roomId, mlsGroup] of this._mlsGroups) {
        if (!mlsGroup?.isInitialized) continue;
        if (roomId.includes(":")) continue; // Skip non-room entries (conv:xxx, a2a:xxx)

        const roomState = this._persisted?.rooms?.[roomId];
        if (!roomState?.mlsGroupId) continue;

        try {
          const pendingRes = await fetch(
            `${this.config.apiUrl}/api/v1/mls/groups/${roomState.mlsGroupId}/pending-welcomes`,
            { headers: { Authorization: `Bearer ${this._deviceJwt}` } },
          );
          if (!pendingRes.ok) continue;
          const pending: Array<{ id: string; requesting_device_id: string }> = await pendingRes.json();
          if (pending.length === 0) continue;

          console.log(`[SecureChannel] ${pending.length} pending Welcome requests for room ${roomId.slice(0, 8)}`);

          for (const req of pending) {
            try {
              const kpRes = await fetch(
                `${this.config.apiUrl}/api/v1/mls/key-packages/batch?device_ids=${req.requesting_device_id}`,
                { headers: { Authorization: `Bearer ${this._deviceJwt}` } },
              );
              if (!kpRes.ok) continue;
              const kpData: Record<string, string> = await kpRes.json();
              const kpHex = kpData[req.requesting_device_id];
              if (!kpHex) continue;

              const kpBytes = new Uint8Array(Buffer.from(kpHex, "hex"));
              const memberKp = MLSGroupManager.deserializeKeyPackage(kpBytes);
              const { commit, welcome } = await mlsGroup.addMembers([memberKp]);

              if (this._ws) {
                this._ws.send(JSON.stringify({
                  event: "mls_commit",
                  data: {
                    group_id: roomState.mlsGroupId,
                    epoch: Number(mlsGroup.epoch),
                    payload: Buffer.from(commit).toString("hex"),
                  },
                }));
              }

              if (welcome && this._ws) {
                this._ws.send(JSON.stringify({
                  event: "mls_welcome",
                  data: {
                    target_device_id: req.requesting_device_id,
                    group_id: roomState.mlsGroupId,
                    room_id: roomId,
                    payload: Buffer.from(welcome).toString("hex"),
                  },
                }));
              }

              await fetch(
                `${this.config.apiUrl}/api/v1/mls/groups/${roomState.mlsGroupId}/fulfill-welcome`,
                {
                  method: "POST",
                  headers: { Authorization: `Bearer ${this._deviceJwt}`, "Content-Type": "application/json" },
                  body: JSON.stringify({ request_id: req.id }),
                },
              );

              await saveMlsState(this.config.dataDir, roomState.mlsGroupId, JSON.stringify(mlsGroup.exportState()));
              console.log(`[SecureChannel] Fulfilled Welcome for ${req.requesting_device_id.slice(0, 8)} in room ${roomId.slice(0, 8)}`);
            } catch (fulfillErr) {
              console.warn(`[SecureChannel] Welcome fulfill failed for ${req.requesting_device_id.slice(0, 8)}:`, fulfillErr);
            }
          }
        } catch {
          // Non-critical — will retry on next pull
        }
      }

      // --- Shared 1:1 group Welcome fulfillment (batched) ---
      for (const [gid, entry] of Object.entries(this._persisted?.mlsGroups ?? {})) {
        if (!entry.mlsGroupId) continue;
        const mlsGroup = this._mlsGroups.get(`1to1-group:${gid}`);
        if (!mlsGroup?.isInitialized) continue;

        try {
          const pendingRes = await fetch(
            `${this.config.apiUrl}/api/v1/mls/groups/${entry.mlsGroupId}/pending-welcomes`,
            { headers: { Authorization: `Bearer ${this._deviceJwt}` } },
          );
          if (!pendingRes.ok) continue;
          const pending: Array<{ id: string; requesting_device_id: string }> = await pendingRes.json();
          if (pending.length === 0) continue;

          console.log(`[SecureChannel] ${pending.length} pending Welcome requests for shared group ${gid.slice(0, 8)}`);

          // Batch: fetch all KeyPackages at once
          const deviceIds = pending.map((r) => r.requesting_device_id);
          const kpRes = await fetch(
            `${this.config.apiUrl}/api/v1/mls/key-packages/batch?device_ids=${deviceIds.join(",")}`,
            { headers: { Authorization: `Bearer ${this._deviceJwt}` } },
          );
          if (!kpRes.ok) continue;
          const kpData: Record<string, string> = await kpRes.json();

          // Collect valid KeyPackages for batched addMembers
          const validReqs: Array<{ req: typeof pending[0]; kp: any }> = [];
          for (const req of pending) {
            const kpHex = kpData[req.requesting_device_id];
            if (!kpHex) {
              console.warn(`[SecureChannel] No KeyPackage for ${req.requesting_device_id.slice(0, 8)}, skipping`);
              continue;
            }
            const kpBytes = new Uint8Array(Buffer.from(kpHex, "hex"));
            validReqs.push({ req, kp: MLSGroupManager.deserializeKeyPackage(kpBytes) });
          }

          if (validReqs.length === 0) continue;

          // Single addMembers call with all KeyPackages — one commit, individual Welcomes
          try {
            const { commit, welcome } = await mlsGroup.addMembers(validReqs.map((v) => v.kp));

            if (this._ws) {
              this._ws.send(JSON.stringify({
                event: "mls_commit",
                data: {
                  group_id: entry.mlsGroupId,
                  epoch: Number(mlsGroup.epoch),
                  payload: Buffer.from(commit).toString("hex"),
                },
              }));
            }

            // Welcome is a single blob for all new members — send to each target
            if (welcome && this._ws) {
              for (const { req } of validReqs) {
                this._ws.send(JSON.stringify({
                  event: "mls_welcome",
                  data: {
                    target_device_id: req.requesting_device_id,
                    group_id: entry.mlsGroupId,
                    conversation_group_id: gid,
                    payload: Buffer.from(welcome).toString("hex"),
                  },
                }));
              }
            }

            // Fulfill all requests
            for (const { req } of validReqs) {
              await fetch(
                `${this.config.apiUrl}/api/v1/mls/groups/${entry.mlsGroupId}/fulfill-welcome`,
                {
                  method: "POST",
                  headers: { Authorization: `Bearer ${this._deviceJwt}`, "Content-Type": "application/json" },
                  body: JSON.stringify({ request_id: req.id }),
                },
              ).catch(() => {});
            }

            await saveMlsState(this.config.dataDir, entry.mlsGroupId, JSON.stringify(mlsGroup.exportState()));
            console.log(`[SecureChannel] Batched Welcome for ${validReqs.length} devices in shared group ${gid.slice(0, 8)} (epoch=${mlsGroup.epoch})`);
          } catch (batchErr) {
            console.warn(`[SecureChannel] Batched Welcome failed for shared group ${gid.slice(0, 8)}, falling back to individual:`, batchErr);
            // Fallback: add one at a time
            for (const { req, kp } of validReqs) {
              try {
                const { commit, welcome } = await mlsGroup.addMembers([kp]);
                if (this._ws) {
                  this._ws.send(JSON.stringify({ event: "mls_commit", data: { group_id: entry.mlsGroupId, epoch: Number(mlsGroup.epoch), payload: Buffer.from(commit).toString("hex") } }));
                }
                if (welcome && this._ws) {
                  this._ws.send(JSON.stringify({ event: "mls_welcome", data: { target_device_id: req.requesting_device_id, group_id: entry.mlsGroupId, conversation_group_id: gid, payload: Buffer.from(welcome).toString("hex") } }));
                }
                await fetch(`${this.config.apiUrl}/api/v1/mls/groups/${entry.mlsGroupId}/fulfill-welcome`, {
                  method: "POST", headers: { Authorization: `Bearer ${this._deviceJwt}`, "Content-Type": "application/json" },
                  body: JSON.stringify({ request_id: req.id }),
                }).catch(() => {});
                await saveMlsState(this.config.dataDir, entry.mlsGroupId, JSON.stringify(mlsGroup.exportState()));
                console.log(`[SecureChannel] Individual Welcome for ${req.requesting_device_id.slice(0, 8)} in shared group ${gid.slice(0, 8)}`);
              } catch (indivErr) {
                console.warn(`[SecureChannel] Individual Welcome failed for ${req.requesting_device_id.slice(0, 8)}:`, indivErr);
              }
            }
          }
        } catch {
          // Non-critical — will retry on next pull
        }
      }

      // --- 1:1 conversation Welcome fulfillment ---
      for (const [convId, convEntry] of Object.entries(this._persisted?.mlsConversations ?? {})) {
        if (!convEntry.mlsGroupId) continue;
        const mlsGroup = this._mlsGroups.get(`conv:${convId}`);
        if (!mlsGroup?.isInitialized) continue;

        try {
          const pendingRes = await fetch(
            `${this.config.apiUrl}/api/v1/mls/groups/${convEntry.mlsGroupId}/pending-welcomes`,
            { headers: { Authorization: `Bearer ${this._deviceJwt}` } },
          );
          if (!pendingRes.ok) continue;
          const pending: Array<{ id: string; requesting_device_id: string }> = await pendingRes.json();
          if (pending.length === 0) continue;

          console.log(`[SecureChannel] ${pending.length} pending Welcome requests for conv ${convId.slice(0, 8)}`);

          for (const req of pending) {
            try {
              const kpRes = await fetch(
                `${this.config.apiUrl}/api/v1/mls/key-packages/batch?device_ids=${req.requesting_device_id}`,
                { headers: { Authorization: `Bearer ${this._deviceJwt}` } },
              );
              if (!kpRes.ok) continue;
              const kpData: Record<string, string> = await kpRes.json();
              const kpHex = kpData[req.requesting_device_id];
              if (!kpHex) continue;

              const kpBytes = new Uint8Array(Buffer.from(kpHex, "hex"));
              const memberKp = MLSGroupManager.deserializeKeyPackage(kpBytes);
              const { commit, welcome } = await mlsGroup.addMembers([memberKp]);

              if (this._ws) {
                this._ws.send(JSON.stringify({
                  event: "mls_commit",
                  data: {
                    group_id: convEntry.mlsGroupId,
                    epoch: Number(mlsGroup.epoch),
                    payload: Buffer.from(commit).toString("hex"),
                  },
                }));
              }

              if (welcome && this._ws) {
                this._ws.send(JSON.stringify({
                  event: "mls_welcome",
                  data: {
                    target_device_id: req.requesting_device_id,
                    group_id: convEntry.mlsGroupId,
                    conversation_id: convId,
                    payload: Buffer.from(welcome).toString("hex"),
                  },
                }));
              }

              await fetch(
                `${this.config.apiUrl}/api/v1/mls/groups/${convEntry.mlsGroupId}/fulfill-welcome`,
                {
                  method: "POST",
                  headers: { Authorization: `Bearer ${this._deviceJwt}`, "Content-Type": "application/json" },
                  body: JSON.stringify({ request_id: req.id }),
                },
              );

              await saveMlsState(this.config.dataDir, convEntry.mlsGroupId, JSON.stringify(mlsGroup.exportState()));
              console.log(`[SecureChannel] Fulfilled Welcome for ${req.requesting_device_id.slice(0, 8)} in conv ${convId.slice(0, 8)}`);
            } catch (fulfillErr) {
              console.warn(`[SecureChannel] Conv Welcome fulfill failed for ${req.requesting_device_id.slice(0, 8)}:`, fulfillErr);
            }
          }
        } catch {
          // Non-critical — will retry on next pull
        }
      }

      // --- A2A channel Welcome fulfillment ---
      for (const [chId, chState] of Object.entries(this._persisted?.a2aChannels ?? {})) {
        // If creator has no mlsGroupId yet, found the group first
        if (!chState.mlsGroupId && chState.role === "creator") {
          try {
            console.log(`[SecureChannel] Creator founding A2A MLS group for ${chId.slice(0, 8)} (no mlsGroupId)`);
            await this._foundA2AGroup(chId, chState.conversationId, chState.participants ?? []);
          } catch (foundErr) {
            console.warn(`[SecureChannel] A2A MLS founding failed for ${chId.slice(0, 8)}:`, foundErr);
          }
        }
        if (!chState.mlsGroupId) continue;
        let mlsGroup = this._mlsGroups.get(`a2a:${chId}`);

        // Try to restore MLS state from disk if not in memory
        if (!mlsGroup?.isInitialized) {
          try {
            const savedState = await loadMlsState(this.config.dataDir, chState.mlsGroupId);
            if (savedState) {
              const mgr = new MLSGroupManager();
              mgr.importState(JSON.parse(savedState) as SerializedMLSGroupState);
              this._mlsGroups.set(`a2a:${chId}`, mgr);
              mlsGroup = mgr;
              console.log(`[SecureChannel] Restored A2A MLS state for ${chId.slice(0, 8)} during delivery pull`);
            }
          } catch { /* ignore restore failure */ }
        }

        // If still not initialized and we're the creator, found the group now
        if (!mlsGroup?.isInitialized && chState.role === "creator") {
          try {
            console.log(`[SecureChannel] Founding A2A MLS group for ${chId.slice(0, 8)} during delivery pull`);
            await this._foundA2AGroup(chId, chState.conversationId, chState.participants ?? []);
            mlsGroup = this._mlsGroups.get(`a2a:${chId}`);
          } catch (foundErr) {
            console.warn(`[SecureChannel] A2A MLS founding failed for ${chId.slice(0, 8)}:`, foundErr);
          }
        }

        if (!mlsGroup?.isInitialized) continue;

        // Only the creator instance should fulfill pending Welcome requests.
        // Without this guard, multiple instances restore the same MLS group from
        // disk and race on addMembers, producing duplicate commits that desync
        // other members' epoch state (BUG-20260408-002).
        if (chState.role !== "creator") continue;

        try {
          const pendingRes = await fetch(
            `${this.config.apiUrl}/api/v1/mls/groups/${chState.mlsGroupId}/pending-welcomes`,
            { headers: { Authorization: `Bearer ${this._deviceJwt}` } },
          );
          if (!pendingRes.ok) continue;
          const pending: Array<{ id: string; requesting_device_id: string }> = await pendingRes.json();
          if (pending.length === 0) continue;

          console.log(`[SecureChannel] ${pending.length} pending A2A Welcome requests for channel ${chId.slice(0, 8)}`);

          for (const req of pending) {
            try {
              const kpRes = await fetch(
                `${this.config.apiUrl}/api/v1/mls/key-packages/batch?device_ids=${req.requesting_device_id}`,
                { headers: { Authorization: `Bearer ${this._deviceJwt}` } },
              );
              if (!kpRes.ok) continue;
              // Batch endpoint returns {device_id: hex_string} dict, not array
              const kpData: Record<string, string> = await kpRes.json();
              const kpHex = kpData[req.requesting_device_id];
              if (!kpHex) continue;

              const kpBytes = new Uint8Array(Buffer.from(kpHex, "hex"));
              const memberKp = MLSGroupManager.deserializeKeyPackage(kpBytes);
              console.log(`[SecureChannel] A2A addMembers for ${req.requesting_device_id.slice(0, 8)} group=${chState.mlsGroupId.slice(0, 8)} epoch=${mlsGroup.epoch}`);
              let commit: Uint8Array;
              let welcome: Uint8Array | undefined;
              try {
                const result = await mlsGroup.addMembers([memberKp]);
                commit = result.commit;
                welcome = result.welcome;
              } catch (addErr) {
                const errMsg = addErr instanceof Error ? addErr.message : String(addErr);
                if (errMsg.includes("already in the group")) {
                  // Member was added before but Welcome was never delivered.
                  // Remove and re-add to generate a fresh Welcome.
                  console.log(`[SecureChannel] A2A member already in group — removing and re-adding ${req.requesting_device_id.slice(0, 8)}`);
                  try {
                    // TODO(WS118): removeMember needs leaf index, not identity bytes.
                    // This path is the "already in group" recovery — needs proper member index lookup.
                    const identityBytes = new TextEncoder().encode(req.requesting_device_id);
                    await mlsGroup.removeMember(identityBytes as unknown as number);
                    // Re-fetch a fresh KeyPackage (the old one was consumed by the failed add)
                    const kpRes2 = await fetch(
                      `${this.config.apiUrl}/api/v1/mls/key-packages/batch?device_ids=${req.requesting_device_id}`,
                      { headers: { Authorization: `Bearer ${this._deviceJwt}` } },
                    );
                    if (kpRes2.ok) {
                      const kpData2: Record<string, string> = await kpRes2.json();
                      const kpHex2 = kpData2[req.requesting_device_id];
                      if (kpHex2) {
                        const kpBytes2 = new Uint8Array(Buffer.from(kpHex2, "hex"));
                        const memberKp2 = MLSGroupManager.deserializeKeyPackage(kpBytes2);
                        const result2 = await mlsGroup.addMembers([memberKp2]);
                        commit = result2.commit;
                        welcome = result2.welcome;
                      } else {
                        console.log(`[SecureChannel] A2A re-add: no fresh KeyPackage for ${req.requesting_device_id.slice(0, 8)}`);
                        continue;
                      }
                    } else {
                      console.log(`[SecureChannel] A2A re-add: KeyPackage fetch failed (${kpRes2.status})`);
                      continue;
                    }
                  } catch (readdErr) {
                    console.log(`[SecureChannel] A2A remove+re-add FAILED: ${readdErr instanceof Error ? readdErr.message : String(readdErr)}`);
                    continue;
                  }
                } else {
                  console.log(`[SecureChannel] A2A addMembers FAILED: ${errMsg}`);
                  continue;
                }
              }

              if (!welcome) {
                console.warn(`[SecureChannel] A2A addMembers produced no Welcome for ${req.requesting_device_id.slice(0, 8)}`);
                continue;
              }

              const commitHex = Buffer.from(commit).toString("hex");
              const welcomeHex = Buffer.from(welcome).toString("hex");
              let distributed = false;

              // Prefer HTTP distribute (works regardless of WS state)
              const updatedMembers = [this._deviceId!, req.requesting_device_id];
              try {
                const distResp = await fetch(
                  `${this.config.apiUrl}/api/v1/mls/groups/${chState.mlsGroupId}/distribute`,
                  {
                    method: "POST",
                    headers: { Authorization: `Bearer ${this._deviceJwt}`, "Content-Type": "application/json" },
                    body: JSON.stringify({
                      group_id: chState.mlsGroupId,
                      device_id: this._deviceId,
                      commit: commitHex,
                      commit_epoch: Number(mlsGroup.epoch),
                      welcomes: [{ target_device_id: req.requesting_device_id, payload: welcomeHex }],
                      member_device_ids: updatedMembers,
                    }),
                  },
                );
                if (distResp.ok) {
                  distributed = true;
                } else {
                  const detail = await distResp.text().catch(() => "");
                  console.warn(`[SecureChannel] A2A distribute failed (${distResp.status}): ${detail}`);
                }
              } catch (distErr) {
                console.warn(`[SecureChannel] A2A distribute network error:`, distErr);
              }

              // Fallback to WS if HTTP distribute failed
              if (!distributed && this._ws) {
                this._ws.send(JSON.stringify({
                  event: "mls_commit",
                  data: { group_id: chState.mlsGroupId, epoch: Number(mlsGroup.epoch), payload: commitHex },
                }));
                this._ws.send(JSON.stringify({
                  event: "mls_welcome",
                  data: {
                    target_device_id: req.requesting_device_id,
                    group_id: chState.mlsGroupId,
                    a2a_channel_id: chId,
                    payload: welcomeHex,
                  },
                }));
                // WS fallback: also update member_device_ids on the server
                try {
                  await fetch(
                    `${this.config.apiUrl}/api/v1/mls/groups/${chState.mlsGroupId}/distribute`,
                    {
                      method: "POST",
                      headers: { Authorization: `Bearer ${this._deviceJwt}`, "Content-Type": "application/json" },
                      body: JSON.stringify({
                        group_id: chState.mlsGroupId,
                        device_id: this._deviceId,
                        commit_epoch: Number(mlsGroup.epoch),
                        member_device_ids: updatedMembers,
                      }),
                    },
                  );
                } catch { /* non-critical — membership update is best-effort */ }
                distributed = true;
              }

              if (!distributed) {
                console.warn(`[SecureChannel] Cannot deliver Welcome — HTTP distribute and WS both unavailable`);
                continue; // Don't mark as fulfilled if not delivered
              }

              await fetch(
                `${this.config.apiUrl}/api/v1/mls/groups/${chState.mlsGroupId}/fulfill-welcome`,
                {
                  method: "POST",
                  headers: { Authorization: `Bearer ${this._deviceJwt}`, "Content-Type": "application/json" },
                  body: JSON.stringify({ request_id: req.id }),
                },
              );

              await saveMlsState(this.config.dataDir, chState.mlsGroupId, JSON.stringify(mlsGroup.exportState()));
              console.log(`[SecureChannel] Fulfilled A2A Welcome for ${req.requesting_device_id.slice(0, 8)} in channel ${chId.slice(0, 8)}`);
            } catch (fulfillErr) {
              console.log(`[SecureChannel] A2A Welcome fulfill failed: ${fulfillErr instanceof Error ? fulfillErr.message : String(fulfillErr)}`);
            }
          }
        } catch {
          // Non-critical
        }
      }
    } catch (err) {
      console.warn("[SecureChannel] Delivery pull error:", err);
    } finally {
      this._deliveryPulling = false;
    }
  }

  /**
   * Pull pending DR (Double Ratchet) messages from the delivery queue.
   * Signal-model queue-first: called on WS connect and on dr_delivery ping.
   */
  private async _pullDrDeliveryQueue(): Promise<void> {
    if (!this._deviceId || !this._deviceJwt || this._drDeliveryPulling) return;
    this._drDeliveryPulling = true;

    try {
      // Uses authedFetch — DR delivery pull was one of the two endpoints
      // observed 401'ing post-expiry. One reissue+retry per WS session.
      const res = await this._authedFetch(
        `${this.config.apiUrl}/api/v1/dr/delivery?device_id=${this._deviceId}`,
      );
      if (!res.ok) {
        console.warn(`[SecureChannel] DR delivery pull failed: ${res.status}`);
        return;
      }
      const { messages } = (await res.json()) as { messages: any[] };
      if (!messages || messages.length === 0) return;

      const ackedIds: string[] = [];
      const nackedIds: string[] = [];

      for (const msg of messages) {
        try {
          // Skip own messages
          if (msg.sender_device_id === this._deviceId) {
            ackedIds.push(msg.queue_id);
            continue;
          }

          // Dedup: skip messages already processed
          if (msg.message_id && this._seenMessageIds.has(msg.message_id)) {
            ackedIds.push(msg.queue_id);
            continue;
          }

          // Room/A2A messages — skip (a non-DM conv delivered here would emit as a
          // no-roomId `message`, which the native worker tool-gate treats as an
          // owner DM: tool-enabled, bypassing arming). This endpoint is
          // `/dr/delivery` -> dr_delivery_service.pull_pending, which carries
          // `room_id` (populated from the conversation, mirroring the poll endpoint;
          // the DrDeliveryQueue is 1:1-only today so it is null in practice but
          // becomes a real server-field backstop for the cold-local-state window the
          // instant any room-backed conv ever DR-delivers). It carries NO
          // `a2a_channel_id` — A2A never rides the DR queue (MLS-only), so that
          // disjunct is a structurally-absent defensive check. `_isNonDmConversation`
          // (local rooms + a2aChannels) is the belt. Ack the queue_id so the server
          // does not redeliver the skipped message.
          if (msg.room_id || msg.a2a_channel_id || this._isNonDmConversation(msg.conversation_id, msg.conversation_group_id)) {
            ackedIds.push(msg.queue_id);
            continue;
          }

          if (!msg.header_blob || !msg.ciphertext) {
            ackedIds.push(msg.queue_id);
            continue;
          }

          const session = this._sessions.get(msg.conversation_id);
          if (!session) {
            console.warn(`[SecureChannel] DR delivery: no session for conv ${msg.conversation_id.slice(0, 8)}, skipping`);
            continue; // Don't ack — will retry when session exists
          }

          const encrypted = transportToEncryptedMessage({
            header_blob: msg.header_blob,
            ciphertext: msg.ciphertext,
          });

          const ratchetSnapshot = session.ratchet.serialize();
          let plaintext: string;
          try {
            plaintext = session.ratchet.decrypt(encrypted);
          } catch (decryptErr) {
            console.error(`[SecureChannel] DR delivery decrypt failed for conv ${msg.conversation_id.slice(0, 8)} — restoring ratchet:`, decryptErr);
            try { session.ratchet = DoubleRatchet.deserialize(ratchetSnapshot); } catch {}
            nackedIds.push(msg.queue_id);
            continue;
          }

          // Mark as seen
          if (msg.message_id) {
            this._seenMessageIds.add(msg.message_id);
            if (this._seenMessageIds.size > SecureChannel.SEEN_MSG_MAX) {
              const first = this._seenMessageIds.values().next().value;
              if (first) this._seenMessageIds.delete(first);
            }
          }

          this._sendAck(msg.message_id);

          // Activate session on first owner message
          if (!session.activated) {
            session.activated = true;
            console.log(`[SecureChannel] Session ${msg.conversation_id.slice(0, 8)}... activated via DR delivery`);
            if (this._persisted?.sessions[msg.conversation_id]) {
              this._persisted.sessions[msg.conversation_id].activated = true;
            }
            this._activateSiblings(msg.conversation_id);
            await this._persistState();
          }

          // Parse structured payload
          let messageText: string;
          let messageType: string;
          try {
            const parsed = JSON.parse(plaintext);
            messageType = parsed.type || "message";
            messageText = parsed.text || plaintext;
          } catch {
            messageType = "message";
            messageText = plaintext;
          }

          if (messageType === "session_init") {
            console.log(`[SecureChannel] session_init received via DR delivery for ${msg.conversation_id.slice(0, 8)}...`);
            await this._replayHistoryToSession(msg.conversation_id);
            await this._persistState();
            ackedIds.push(msg.queue_id);
            continue;
          }

          if (messageType === "message") {
            const topicId = msg.topic_id;
            this._appendHistory("owner", messageText, topicId);

            const metadata: MessageMetadata = {
              messageId: msg.message_id,
              conversationId: msg.conversation_id,
              timestamp: msg.created_at,
              topicId,
            };

            this.emit("message", messageText, metadata);
            Promise.resolve(this.config.onMessage?.(messageText, metadata)).catch((err: unknown) => {
              console.error("[SecureChannel] onMessage callback error:", err);
            });
          }

          this._persisted!.lastMessageTimestamp = msg.created_at;
          ackedIds.push(msg.queue_id);
        } catch (err) {
          console.warn(`[SecureChannel] DR delivery processing failed for ${msg.message_id?.slice(0, 8)}:`, err);
          nackedIds.push(msg.queue_id);
        }
      }

      await this._persistState();

      // Batch nack
      if (nackedIds.length > 0) {
        try {
          await fetch(`${this.config.apiUrl}/api/v1/dr/delivery/nack`, {
            method: "POST",
            headers: {
              Authorization: `Bearer ${this._deviceJwt}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ queue_ids: nackedIds }),
          });
        } catch { /* best effort */ }
      }

      // Batch ack
      if (ackedIds.length > 0) {
        try {
          await fetch(`${this.config.apiUrl}/api/v1/dr/delivery/ack`, {
            method: "POST",
            headers: {
              Authorization: `Bearer ${this._deviceJwt}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ queue_ids: ackedIds, device_id: this._deviceId }),
          });
          console.log(`[SecureChannel] DR delivery acked ${ackedIds.length}/${messages.length}`);
        } catch (ackErr) {
          console.warn("[SecureChannel] DR delivery ack failed:", ackErr);
        }
      }
    } catch (err) {
      console.warn("[SecureChannel] DR delivery pull error:", err);
    } finally {
      this._drDeliveryPulling = false;
    }
  }

  private async _handleMlsSyncResponse(data: any): Promise<void> {
    const action = data.action;
    const groupId = data.group_id;

    if (action === "rejoin") {
      console.log(`[SecureChannel] MLS sync: re-join needed for group ${groupId?.slice(0, 8)}`);
      // Delete old state, server will send fresh Welcome
      if (groupId) {
        await deleteMlsState(this.config.dataDir, groupId);
        for (const [roomId, room] of Object.entries(this._persisted?.rooms ?? {})) {
          if ((room as RoomState).mlsGroupId === groupId) {
            this._mlsGroups.delete(roomId);
            break;
          }
        }
      }
    } else if (action === "replay_complete") {
      console.log(`[SecureChannel] MLS sync complete for group ${groupId?.slice(0, 8)} (${data.count} messages replayed)`);
    }
  }

  /**
   * Handle an incoming MLS-encrypted A2A message.
   * The observer (owner device) decrypts as a regular MLS group member.
   */
  private async _handleA2AMessageMLS(data: any): Promise<void> {
    const groupId = data.group_id;
    // Accept either `channel_id` (legacy direct-event payload — e.g., when
    // dispatched from the WS handler at channel.ts:3375) or `a2a_channel_id`
    // (the field name used by _pullDeliveryQueue when it routes pulled
    // messages here at channel.ts:5127). Field-name divergence between the
    // two call sites silently dropped every inbound A2A application message
    // that came via the pull-delivery path — diagnosed live 2026-05-24
    // against a Hermes-to-OC A2A round-trip.
    const channelId = data.channel_id ?? data.a2a_channel_id;

    // Find A2A channel by channel ID or MLS group ID
    let a2aChannelId = channelId;
    if (!a2aChannelId) {
      for (const [cid, entry] of Object.entries(this._persisted?.a2aChannels ?? {})) {
        if (entry.mlsGroupId === groupId) {
          a2aChannelId = cid;
          break;
        }
      }
    }

    if (!a2aChannelId) {
      console.warn(`[SecureChannel] No A2A channel for MLS group ${groupId?.slice(0, 8)}`);
      return;
    }

    let mlsGroup = this._mlsGroups.get(`a2a:${a2aChannelId}`);

    // Try to restore MLS state from disk if not in memory (e.g. after restart)
    if (!mlsGroup?.isInitialized) {
      const entry = this._persisted?.a2aChannels?.[a2aChannelId];
      const mlsGroupId = entry?.mlsGroupId || groupId;
      if (mlsGroupId) {
        try {
          const savedState = await loadMlsState(this.config.dataDir, mlsGroupId);
          if (savedState) {
            const mgr = new MLSGroupManager();
            mgr.importState(JSON.parse(savedState) as SerializedMLSGroupState);
            this._mlsGroups.set(`a2a:${a2aChannelId}`, mgr);
            mlsGroup = mgr;
            console.log(`[SecureChannel] Restored A2A MLS state for ${a2aChannelId.slice(0, 8)} on message receive (epoch=${mgr.epoch})`);
          }
        } catch { /* ignore restore failure */ }
      }
    }

    if (!mlsGroup?.isInitialized) {
      console.warn(`[SecureChannel] MLS group not loaded for A2A ${a2aChannelId.slice(0, 8)}`);
      return;
    }

    try {
      const ciphertext = new Uint8Array(Buffer.from(data.payload, "hex"));
      const result = await mlsGroup.decrypt(ciphertext);

      // Persist updated state
      await saveMlsState(this.config.dataDir, groupId, JSON.stringify(mlsGroup.exportState()));

      if (result.isHandshake || !result.plaintext) {
        // Handshake message (commit/proposal) — no application data
        return;
      }

      const plaintext = new TextDecoder().decode(result.plaintext);

      const entry = this._persisted?.a2aChannels?.[a2aChannelId];
      const a2aMsg: A2AMessage = {
        text: plaintext,
        fromHubAddress: data.from_hub_address || data.hub_address || entry?.participants?.find(p => p.status === "active")?.hubAddress || "",
        channelId: a2aChannelId,
        conversationId: data.conversation_id || entry?.conversationId || "",
        parentSpanId: data.parent_span_id,
        timestamp: data.created_at || new Date().toISOString(),
      };

      console.log(`[SecureChannel] A2A message decrypted for ${a2aChannelId.slice(0, 8)} (epoch=${mlsGroup.epoch}, len=${plaintext.length})`);
      this.emit("a2a_message", a2aMsg);
      if (this.config.onA2AMessage) {
        this.config.onA2AMessage(a2aMsg);
      }
    } catch (err) {
      const failKey = `a2a:${a2aChannelId}`;
      const count = (this._mlsDecryptFailCounts.get(failKey) ?? 0) + 1;
      this._mlsDecryptFailCounts.set(failKey, count);
      console.error(`[SecureChannel] MLS A2A decrypt failed (${count}/${SecureChannel.MAX_MLS_DECRYPT_FAILS}) for ${a2aChannelId.slice(0, 8)}:`, err);
      if (count >= SecureChannel.MAX_MLS_DECRYPT_FAILS && groupId) {
        // #440: catch up a behind member via /sync replay before re-joining.
        if (!(await this._resyncOnDivergence(failKey, groupId))) {
          await this._triggerMlsEpochRecovery(failKey, groupId);
        }
      }
    }
  }

  /**
   * Found the MLS group for an A2A channel as the creator agent.
   * Calls /mls/a2a/{channelId}/init, creates the local MLS group, and sends
   * Welcome messages to all active/invited participants.
   * Extracted from the a2a_channel_approved WS handler so it can also be called
   * from the sync fallback in listA2AChannels.
   */
  private async _foundA2AGroup(
    channelId: string,
    convId: string,
    participants: Array<{ hubId: string; hubAddress: string; status: string; deviceId?: string | null }>,
  ): Promise<void> {
    if (!this._persisted || !this._deviceJwt) return;

    // Creator agent founds the MLS group
    const mlsInitRes = await fetch(
      `${this.config.apiUrl}/api/v1/mls/a2a/${channelId}/init`,
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${this._deviceJwt}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ member_device_ids: [this._persisted!.deviceId] }),
      },
    );

    if (!mlsInitRes.ok) {
      const detail = await mlsInitRes.text();
      throw new Error(`MLS A2A init failed (${mlsInitRes.status}): ${detail}`);
    }

    const mlsData = await mlsInitRes.json();
    const entry = this._persisted.a2aChannels?.[channelId];
    if (!entry) return;

    entry.mlsGroupId = mlsData.group_id;
    await this._persistState();
    console.log(
      `[SecureChannel] A2A ${channelId.slice(0, 8)} MLS group founded as creator (groupId=${mlsData.group_id.slice(0, 8)})`,
    );

    // Create MLS group and send Welcomes to active/invited participants
    try {
      const mgr = new MLSGroupManager();
      const identity = new TextEncoder().encode(this._persisted!.deviceId);
      const creatorKp = await mgr.generateKeyPackage(identity);
      const groupIdBytes = new TextEncoder().encode(mlsData.group_id);
      await mgr.createGroup(groupIdBytes, creatorKp);
      this._mlsGroups.set(`a2a:${channelId}`, mgr);
      await saveMlsState(this.config.dataDir, mlsData.group_id, JSON.stringify(mgr.exportState()));

      // Fetch KeyPackages for participants and send Welcomes
      for (const p of participants) {
        if (p.status !== "active" && p.status !== "invited") continue;
        try {
          const kpRes = await fetch(
            `${this.config.apiUrl}/api/v1/mls/key-packages?hub_id=${encodeURIComponent(p.hubId)}`,
            { headers: { Authorization: `Bearer ${this._deviceJwt}` } },
          );
          if (kpRes.ok) {
            const kpData = await kpRes.json();
            if (kpData.key_package) {
              const kpBytes = new Uint8Array(Buffer.from(kpData.key_package, "hex"));
              const memberKp = MLSGroupManager.deserializeKeyPackage(kpBytes);
              const { commit, welcome } = await mgr.addMembers([memberKp]);
              if (welcome && this._ws) {
                // Send commit
                this._ws.send(JSON.stringify({
                  event: "mls_commit",
                  data: {
                    group_id: mlsData.group_id,
                    epoch: Number(mgr.epoch),
                    payload: Buffer.from(commit).toString("hex"),
                  },
                }));
                // Send Welcome via WS.
                //
                // Field names MUST match the backend handler at
                // packages/backend/app/api/v1/ws.py:64-96 (mls_welcome):
                //   target_device_id  (NOT target_hub_id)
                //   payload           (NOT welcome)
                // Backend short-circuits when target_device_id is missing,
                // dropping the welcome silently — the root cause of the
                // 2026-05-24 A2A reliability discovery (cross-ref: action_items
                // "CRITICAL: OC plugin emits mls_welcome with target_hub_id+welcome").
                if (!p.deviceId) {
                  console.warn(
                    `[SecureChannel] Skipping MLS Welcome to ${p.hubAddress} for A2A ${channelId.slice(0, 8)}: no deviceId. ` +
                    `Backend a2a_channel_approved payload may be missing device_id for this participant.`,
                  );
                } else {
                  this._ws.send(JSON.stringify({
                    event: "mls_welcome",
                    data: {
                      group_id: mlsData.group_id,
                      payload: Buffer.from(welcome).toString("hex"),
                      target_device_id: p.deviceId,
                      a2a_channel_id: channelId,
                      // Kept for observability — backend ignores but it's
                      // useful in logs/wire captures to correlate to a hub.
                      target_hub_id: p.hubId,
                    },
                  }));
                  console.log(`[SecureChannel] Sent MLS Welcome to ${p.hubAddress} (device ${p.deviceId.slice(0, 8)}) for A2A ${channelId.slice(0, 8)}`);
                }
              }
              await saveMlsState(this.config.dataDir, mlsData.group_id, JSON.stringify(mgr.exportState()));
            }
          }
        } catch (kpErr) {
          console.warn(`[SecureChannel] Failed to Welcome participant ${p.hubAddress}:`, kpErr);
        }
      }
    } catch (mlsErr) {
      console.warn(`[SecureChannel] MLS group creation failed:`, mlsErr);
    }
  }

  /**
   * Paginated sync: fetch missed messages in pages of 200, up to 5 pages (1000 messages).
   * Tracks message IDs in _syncMessageIds to prevent duplicate processing from concurrent WS messages.
   */
  private async _syncMissedMessages(): Promise<void> {
    if (!this._persisted?.lastMessageTimestamp || !this._deviceJwt) return;

    this._syncMessageIds = new Set<string>();
    const MAX_PAGES = 5;
    const PAGE_SIZE = 200;
    let since = this._persisted.lastMessageTimestamp;
    let totalProcessed = 0;

    try {
      for (let page = 0; page < MAX_PAGES; page++) {
        const url = `${this.config.apiUrl}/api/v1/devices/${this._deviceId}/messages?since=${encodeURIComponent(since)}&limit=${PAGE_SIZE}`;
        const res = await fetch(url, {
          headers: { Authorization: `Bearer ${this._deviceJwt}` },
        });

        if (!res.ok) break;

        const messages: Array<{
          id: string;
          conversation_id: string;
          sender_device_id: string;
          header_blob: string;
          ciphertext: string;
          created_at: string;
          topic_id?: string;
          room_id?: string;
        }> = await res.json();

        if (messages.length === 0) break;

        for (const msg of messages) {
          if (msg.sender_device_id === this._deviceId) continue;

          // Dedup: skip if already processed (check both sync and WS buffers)
          if (this._syncMessageIds.has(msg.id)) continue;
          if (this._seenMessageIds.has(msg.id)) continue;
          this._syncMessageIds.add(msg.id);
          // Also register in the WS dedup buffer to prevent double-delivery
          // when sync poll and WS events overlap
          if (this._seenMessageIds.size >= SecureChannel.SEEN_MSG_MAX) {
            const first = this._seenMessageIds.values().next().value;
            if (first) this._seenMessageIds.delete(first);
          }
          this._seenMessageIds.add(msg.id);

          // Legacy DR room messages in sync — skip (rooms are MLS-only now). Honors
          // server `room_id` first, then local rooms.
          // TODO(unify): sync guard could adopt _isNonDmConversation for a2a parity —
          // inert today (A2A is MLS-only; this endpoint carries no a2a_channel_id).
          let roomId = msg.room_id;
          if (!roomId && this._persisted?.rooms) {
            for (const room of Object.values(this._persisted.rooms)) {
              if (room.conversationIds?.includes(msg.conversation_id)) {
                roomId = room.roomId;
                break;
              }
            }
          }
          if (roomId) {
            this._persisted!.lastMessageTimestamp = msg.created_at;
            since = msg.created_at;
            continue;
          }

          const session = this._sessions.get(msg.conversation_id);
          if (!session) {
            // No session during sync — skip silently. Resync only triggers
            // on real-time messages when the owner's browser is connected.
            console.warn(
              `[SecureChannel] No session for conversation ${msg.conversation_id} during sync, skipping`,
            );
            continue;
          }

          try {
            const encrypted = transportToEncryptedMessage({
              header_blob: msg.header_blob,
              ciphertext: msg.ciphertext,
            });
            const ratchetSnapshot = session.ratchet.serialize();
            let plaintext: string;
            try {
              plaintext = session.ratchet.decrypt(encrypted);
            } catch (decryptErr) {
              console.error(`[SecureChannel] Sync decrypt failed for ${msg.conversation_id.slice(0, 8)}... — restoring ratchet:`, decryptErr);
              try { session.ratchet = DoubleRatchet.deserialize(ratchetSnapshot); } catch {}
              // Advance past the failing message to prevent infinite retry loop.
              this._persisted!.lastMessageTimestamp = msg.created_at;
              since = msg.created_at;
              continue;
            }

            this._sendAck(msg.id);

            if (!session.activated) {
              session.activated = true;
              console.log(`[SecureChannel] Session ${msg.conversation_id.slice(0, 8)}... activated during sync`);
              if (this._persisted?.sessions[msg.conversation_id]) {
                this._persisted.sessions[msg.conversation_id].activated = true;
              }
              this._activateSiblings(msg.conversation_id);
            }

            let messageText: string;
            let messageType: string;
            try {
              const parsed = JSON.parse(plaintext);
              messageType = parsed.type || "message";
              messageText = parsed.text || plaintext;
            } catch {
              messageType = "message";
              messageText = plaintext;
            }

            if (messageType === "message") {
              const topicId = msg.topic_id;
              this._appendHistory("owner", messageText, topicId);

              const metadata: MessageMetadata = {
                messageId: msg.id,
                conversationId: msg.conversation_id,
                timestamp: msg.created_at,
                topicId,
              };

              this.emit("message", messageText, metadata);
              Promise.resolve(this.config.onMessage?.(messageText, metadata)).catch((err: unknown) => {
                console.error("[SecureChannel] onMessage callback error:", err);
              });
            }

            this._persisted!.lastMessageTimestamp = msg.created_at;
            since = msg.created_at;
            totalProcessed++;
          } catch (err) {
            console.warn(
              `[SecureChannel] Sync failed for msg ${msg.id.slice(0, 8)}... in conv ${msg.conversation_id.slice(0, 8)}...: ${String(err)}`,
            );
            // Advance past the failing message to prevent infinite retry loop.
            // The message is lost but the sync cursor moves forward.
            this._persisted!.lastMessageTimestamp = msg.created_at;
            since = msg.created_at;
            continue;
          }
        }

        await this._persistState();

        // If we got fewer than PAGE_SIZE, we've caught up
        if (messages.length < PAGE_SIZE) break;
      }

      if (totalProcessed > 0) {
        console.log(`[SecureChannel] Synced ${totalProcessed} missed messages`);
      }
    } catch {
      // Network error -- non-critical, will get messages via WS
    }

    this._syncMessageIds = null;
  }

  private _sendAck(messageId: string): void {
    this._pendingAcks.push(messageId);
    if (this._ackTimer) clearTimeout(this._ackTimer);
    this._ackTimer = setTimeout(() => this._flushAcks(), 500);
  }

  private _flushAcks(): void {
    if (this._pendingAcks.length === 0 || !this._ws) return;
    const batch = this._pendingAcks.splice(0, 50);
    this._ws.send(JSON.stringify({ event: "ack", data: { message_ids: batch } }));
  }

  private async _flushOutboundQueue(): Promise<void> {
    const queue = this._persisted?.outboundQueue;
    if (!queue || queue.length === 0 || !this._ws) return;

    console.log(`[SecureChannel] Flushing ${queue.length} queued outbound messages`);
    const messages = queue.splice(0); // Take all, clear queue

    for (const msg of messages) {
      try {
        this._ws.send(
          JSON.stringify({
            event: "message",
            data: {
              conversation_id: msg.convId,
              header_blob: msg.headerBlob,
              ciphertext: msg.ciphertext,
              message_group_id: msg.messageGroupId,
              topic_id: msg.topicId,
            },
          }),
        );
      } catch (err) {
        // Re-queue failed messages
        if (!this._persisted!.outboundQueue) {
          this._persisted!.outboundQueue = [];
        }
        this._persisted!.outboundQueue.push(msg);
        console.warn(`[SecureChannel] Failed to flush message, re-queued: ${err}`);
        break; // Stop flushing on first failure
      }
    }

    await this._persistState();
  }

  private _startPing(ws: WebSocket): void {
    this._stopPing(); // Clear any existing timers
    this._lastServerMessage = Date.now();
    this._pingTimer = setInterval(() => {
      if (ws.readyState !== WebSocket.OPEN) return;
      const silence = Date.now() - this._lastServerMessage;
      if (silence > SecureChannel.SILENCE_TIMEOUT_MS) {
        console.log(
          `[SecureChannel] No server data for ${Math.round(silence / 1000)}s — reconnecting stale WebSocket`,
        );
        ws.terminate(); // Forces close → _scheduleReconnect fires
      }
    }, SecureChannel.PING_INTERVAL_MS);
  }

  private _stopPing(): void {
    if (this._pingTimer) {
      clearInterval(this._pingTimer);
      this._pingTimer = null;
    }
  }

  private _startWakeDetector(): void {
    this._stopWakeDetector();
    this._lastWakeTick = Date.now();
    this._wakeDetectorTimer = setInterval(() => {
      const gap = Date.now() - this._lastWakeTick;
      this._lastWakeTick = Date.now();
      if (gap > 120_000 && this._ws) {
        console.log(
          `[SecureChannel] System wake detected (${Math.round(gap / 1000)}s gap) — forcing reconnect`,
        );
        this._ws.terminate();
      }
    }, 10_000);
  }

  private _stopWakeDetector(): void {
    if (this._wakeDetectorTimer) {
      clearInterval(this._wakeDetectorTimer);
      this._wakeDetectorTimer = null;
    }
  }

  private _startPendingPoll(): void {
    this._stopPendingPoll();
    this._pendingPollTimer = setInterval(() => {
      this._checkPendingMessages();
    }, PENDING_POLL_INTERVAL_MS);
  }

  private _stopPendingPoll(): void {
    if (this._pendingPollTimer) {
      clearInterval(this._pendingPollTimer);
      this._pendingPollTimer = null;
    }
  }

  private async _checkPendingMessages(): Promise<void> {
    // Don't poll while connecting (prevent hammering during reconnect)
    if (this._state === "connecting" || !this._deviceId || !this._deviceJwt) return;

    try {
      const url = `${this.config.apiUrl}/api/v1/devices/${this._deviceId}/pending`;
      const resp = await fetch(url, {
        headers: { Authorization: `Bearer ${this._deviceJwt}` },
        signal: AbortSignal.timeout(10_000),
      });

      if (!resp.ok) return; // Silent fail — WS silence detector is backup

      const data = (await resp.json()) as { pending: number };

      if (data.pending > 0 && this._state !== "ready") {
        console.log(
          `[SecureChannel] Poll detected ${data.pending} pending messages — forcing reconnect`,
        );
        if (this._ws) {
          this._ws.terminate();
        } else {
          this._scheduleReconnect();
        }
      } else if (data.pending > 0 && this._state === "ready") {
        // WS claims ready but messages are pending — possible half-open connection
        const silence = Date.now() - this._lastServerMessage;
        if (silence > 180_000) {
          console.log(
            `[SecureChannel] Poll: ${data.pending} pending + ${Math.round(silence / 1000)}s silence — forcing reconnect`,
          );
          if (this._ws) this._ws.terminate();
        }
      }
    } catch {
      // Network error on poll — expected when offline, ignore silently
    }
  }

  private _scheduleReconnect(): void {
    if (this._stopped) return;
    if (this._reconnectTimer) return; // Already scheduled

    // Detect rapid-disconnect cascade (e.g. two processes as same device).
    // If WS opened recently (< 10s) and we're disconnecting, count it.
    const sinceOpen = Date.now() - this._lastWsOpenTime;
    if (this._lastWsOpenTime > 0 && sinceOpen < 10_000) {
      this._rapidDisconnects++;
    } else {
      this._rapidDisconnects = 0;
    }

    if (this._rapidDisconnects >= 5) {
      console.error(
        `[SecureChannel] Detected rapid connect/disconnect loop (${this._rapidDisconnects} times in <10s each).`,
      );

      // Auth-failure branch: an `auth_failed` event was already emitted this
      // session (via proactive reissue or via _authedFetch's reactive 401
      // path). The disconnect cascade is caused by credential rejection, NOT
      // by a session collision — emit the correct cause and stop.
      if (this._authFailedThisSession) {
        console.log(
          "[SecureChannel] Auth-failed during repeated reconnects. Stopping; see prior auth_failed event.",
        );
        this._setState("error");
        this.emit("error", new Error("Reconnect loop detected — device credentials need attention (see auth_failed event)"));
        return;
      }

      // Session-replacement branch: the server-side WS handler closed our
      // socket because a newer session for the same device took over. Custom
      // close codes 4000 / 4001 are reserved for this signal. Emit a
      // dedicated event and stop reconnecting.
      const lastCode = this._lastCloseCode;
      if (lastCode === 4000 || lastCode === 4001) {
        console.log(
          `[SecureChannel] Server reports our session was replaced by a newer one (close code ${lastCode}). Stopping reconnect.`,
        );
        this._setState("error");
        this.emit("session_replaced", { code: lastCode });
        return;
      }

      // Fallback: genuinely indeterminate cascade. Preserve the original
      // operator-facing message so the existing diagnosis path remains.
      console.error(
        `[SecureChannel] This usually means another process is already connected as this device.`,
      );
      console.error(
        `[SecureChannel] Stopping reconnection. Check for duplicate gateway processes or stale CLI sessions.`,
      );
      this._setState("error");
      this.emit("error", new Error("Reconnect loop detected — another process may be connected as this device"));
      return;
    }

    const delay = Math.min(
      RECONNECT_BASE_MS * Math.pow(2, this._reconnectAttempt),
      RECONNECT_MAX_MS,
    );
    this._reconnectAttempt++;

    console.log(`[SecureChannel] Scheduling reconnect in ${delay}ms (attempt ${this._reconnectAttempt})`);

    this._reconnectTimer = setTimeout(() => {
      this._reconnectTimer = null; // Clear BEFORE calling _connect so future disconnects can schedule new reconnects
      if (!this._stopped) {
        this._connect();
      }
    }, delay);
  }

  private _setState(newState: ChannelState): void {
    if (this._state === newState) return;
    this._state = newState;
    this.emit("state", newState);
    this.config.onStateChange?.(newState);

    // Start/stop polling fallback based on connection state
    if (newState === "disconnected" && !this._pollFallbackTimer && this._deviceJwt) {
      this._startPollFallback();
    }
    if (newState === "ready" || newState === "error" || this._stopped) {
      this._stopPollFallback();
    }
  }

  private _startPollFallback(): void {
    this._stopPollFallback();
    console.log("[SecureChannel] Starting HTTP poll fallback (WS is down)");

    let interval = SecureChannel.POLL_FALLBACK_INTERVAL_MS;

    const poll = async () => {
      if (this._state === "ready" || this._stopped) {
        this._stopPollFallback();
        return;
      }

      try {
        const since = this._persisted?.lastMessageTimestamp;
        if (!since || !this._deviceJwt) return;

        const url = `${this.config.apiUrl}/api/v1/devices/${this._deviceId}/messages?since=${encodeURIComponent(since)}&limit=200`;
        const res = await fetch(url, {
          headers: { Authorization: `Bearer ${this._deviceJwt}` },
        });

        if (!res.ok) return;

        const messages: Array<{
          id: string;
          conversation_id: string;
          sender_device_id: string;
          header_blob: string;
          ciphertext: string;
          created_at: string;
          topic_id?: string;
          room_id?: string;
        }> = await res.json();

        let foundMessages = false;

        for (const msg of messages) {
          if (msg.sender_device_id === this._deviceId) continue;

          const session = this._sessions.get(msg.conversation_id);
          if (!session) continue;

          // Room/A2A messages — skip (a non-DM conv delivered here would decrypt
          // and emit as a no-roomId 1:1 `message`, which the native worker tool-gate
          // treats as an owner DM: tool-enabled, bypassing arming). Honor the
          // AUTHORITATIVE server `room_id` FIRST — the devices/messages responder
          // populates it for room rows (conv_room_map) — then fall back to LOCAL
          // room/A2A membership (the poll endpoint carries no a2a_channel_id, so
          // A2A is caught via local a2aChannels). Mirrors the sync path's
          // server-field-first guard. Advance the poll cursor (lastMessageTimestamp)
          // so the skipped message isn't re-fetched next poll.
          if (msg.room_id || this._isNonDmConversation(msg.conversation_id)) {
            this._persisted!.lastMessageTimestamp = msg.created_at;
            continue;
          }

          try {
            const encrypted = transportToEncryptedMessage({
              header_blob: msg.header_blob,
              ciphertext: msg.ciphertext,
            });
            const ratchetSnapshot = session.ratchet.serialize();
            let plaintext: string;
            try {
              plaintext = session.ratchet.decrypt(encrypted);
            } catch (decryptErr) {
              console.error(`[SecureChannel] Room sync decrypt failed for ${msg.conversation_id.slice(0, 8)}... — restoring ratchet:`, decryptErr);
              try { session.ratchet = DoubleRatchet.deserialize(ratchetSnapshot); } catch {}
              continue;
            }

            if (!session.activated) {
              session.activated = true;
            }

            let messageText: string;
            let messageType: string;
            try {
              const parsed = JSON.parse(plaintext);
              messageType = parsed.type || "message";
              messageText = parsed.text || plaintext;
            } catch {
              messageType = "message";
              messageText = plaintext;
            }

            if (messageType === "message") {
              const topicId = msg.topic_id;
              this._appendHistory("owner", messageText, topicId);

              const metadata: MessageMetadata = {
                messageId: msg.id,
                conversationId: msg.conversation_id,
                timestamp: msg.created_at,
                topicId,
              };

              this.emit("message", messageText, metadata);
              Promise.resolve(this.config.onMessage?.(messageText, metadata)).catch((err: unknown) => {
                console.error("[SecureChannel] onMessage callback error:", err);
              });
              foundMessages = true;
            }

            this._persisted!.lastMessageTimestamp = msg.created_at;
          } catch (err) {
            this.emit("error", err);
            break;
          }
        }

        if (messages.length > 0) {
          await this._persistState();
        }

        // Adaptive rate: faster when active, slower when idle
        const newInterval = foundMessages
          ? SecureChannel.POLL_FALLBACK_INTERVAL_MS
          : SecureChannel.POLL_FALLBACK_IDLE_MS;

        if (newInterval !== interval) {
          interval = newInterval;
          // Restart timer with new interval
          if (this._pollFallbackTimer) {
            clearInterval(this._pollFallbackTimer);
            this._pollFallbackTimer = setInterval(poll, interval);
          }
        }
      } catch {
        // Network error — try again next tick
      }
    };

    // Run first poll after a short delay, then on interval
    setTimeout(poll, 1_000);
    this._pollFallbackTimer = setInterval(poll, interval);
  }

  private _stopPollFallback(): void {
    if (this._pollFallbackTimer) {
      clearInterval(this._pollFallbackTimer);
      this._pollFallbackTimer = null;
      console.log("[SecureChannel] Stopped HTTP poll fallback");
    }
  }

  private _handleError(err: Error): void {
    this._setState("error");
    this.emit("error", err);
  }

  // Phase 2 — rolling-window timestamps of device_revoked self-heal reconnects,
  // used as a war cap so a takeover war or a server status/WS inconsistency can't
  // thrash forever.
  private _revokeRecoveries: number[] = [];
  private static readonly REVOKE_RECOVERY_MAX = 3;
  private static readonly REVOKE_RECOVERY_WINDOW_MS = 120_000;

  /**
   * Handle a WS `device_revoked` event.
   *
   * The signal is ambiguous: the server sends it both for a genuine owner revoke
   * AND for a transient/racy session-takeover of a device that is still ACTIVE
   * (the error text even reads "or another session has taken over" — loopita's
   * case, where the device stayed ACTIVE on the server). So we do NOT terminate
   * blindly. We ask the PUBLIC /devices/{id}/status endpoint (no JWT needed, so
   * it answers even when our device JWT is being rejected):
   *   - ACTIVE          -> reconnect in place (transient); stay online.
   *   - non-ACTIVE      -> terminal (genuinely revoked; operator must re-enroll).
   *   - unreachable/429  -> reconnect (inconclusive; a network blip shouldn't
   *                         permanently kill a healthy agent — the WS is down too,
   *                         so normal backoff handles it, and the war cap bounds
   *                         thrash if the device really is dead).
   *
   * A rolling-window cap (REVOKE_RECOVERY_MAX self-heals per WINDOW_MS) gives up
   * (terminal) on a repeating loop. Never clears credentials in any branch —
   * re-enrollment, which SHOULD wipe creds, goes through setup --force /
   * _forceReEnroll in start().
   */
  private async _handleDeviceRevoked(): Promise<void> {
    const now = Date.now();
    this._revokeRecoveries = this._revokeRecoveries.filter(
      (t) => now - t < SecureChannel.REVOKE_RECOVERY_WINDOW_MS,
    );
    if (this._revokeRecoveries.length >= SecureChannel.REVOKE_RECOVERY_MAX) {
      console.warn(
        "[SecureChannel] device_revoked self-healed too many times in a short window — giving up (terminal)",
      );
      this._handleError(new Error("Device was revoked"));
      return;
    }

    const deviceId = this._deviceId ?? this._persisted?.deviceId;
    if (!deviceId) {
      this._handleError(new Error("Device was revoked"));
      return;
    }

    let reconnect: boolean;
    try {
      const status = await pollDeviceStatus(this.config.apiUrl, deviceId);
      // Rate-limited is inconclusive -> lean to uptime. Only an explicit
      // non-ACTIVE status is treated as a confirmed revoke.
      reconnect = status.rateLimited === true || status.status === "ACTIVE";
      if (!reconnect) {
        console.warn(
          `[SecureChannel] device_revoked confirmed by server (status=${status.status}) — terminal`,
        );
      }
    } catch (err) {
      // Status endpoint unreachable — inconclusive; bias to uptime.
      console.warn(
        "[SecureChannel] device_revoked status check failed; reconnecting (inconclusive):",
        err,
      );
      reconnect = true;
    }

    if (reconnect) {
      this._revokeRecoveries.push(now);
      console.log(
        "[SecureChannel] device_revoked but device not confirmed dead — reconnecting (self-heal)",
      );
      this._scheduleReconnect();
      return;
    }
    this._handleError(new Error("Device was revoked"));
  }

  /**
   * Persist all ratchet session states to disk.
   * Syncs live ratchet states back into the persisted sessions map.
   */
  private async _persistState(): Promise<void> {
    if (!this._persisted) return;

    const hasOwnerSessions = this._sessions.size > 0;
    const hasA2AChannels = !!this._persisted.a2aChannels && Object.keys(this._persisted.a2aChannels).length > 0;

    if (!hasOwnerSessions && !hasA2AChannels) return;

    // Persist sticky room context for proactive send routing across restarts
    this._persisted.lastInboundRoomId = this._lastInboundRoomId;

    // Sync dedup sets to persisted state (capped to max size)
    this._persisted.seenMessageIds = [...this._seenMessageIds].slice(-SecureChannel.SEEN_MSG_MAX);
    this._persisted.seenA2AMessageIds = [...this._a2aSeenMessageIds].slice(-SecureChannel.A2A_SEEN_MAX);

    // Sync all live owner-agent ratchet states back to persisted (1:1 only — rooms use MLS)
    for (const [convId, session] of this._sessions) {
      this._persisted.sessions[convId] = {
        ownerDeviceId: session.ownerDeviceId,
        ratchetState: session.ratchet.serialize(),
        activated: session.activated,
      };
    }

    await saveState(this.config.dataDir, this._persisted);
    await backupState(this.config.dataDir);
    this._scheduleServerBackup();
  }

  /**
   * Debounced server backup upload (60s after last state change).
   * Only runs when backupCode is configured.
   */
  private _scheduleServerBackup(): void {
    if (!this.config.backupCode || !this._persisted || !this._deviceJwt) return;

    if (this._serverBackupTimer) clearTimeout(this._serverBackupTimer);
    this._serverBackupTimer = setTimeout(async () => {
      if (this._serverBackupRunning || !this._persisted || !this._deviceJwt) return;
      this._serverBackupRunning = true;
      try {
        await uploadBackupToServer(
          this._persisted,
          this.config.backupCode!,
          this.config.apiUrl,
          this._deviceJwt,
        );
      } catch (err) {
        console.warn("[SecureChannel] Server backup upload failed:", err);
      } finally {
        this._serverBackupRunning = false;
      }
    }, 60_000);
  }
}
